import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate, formatDateTime, formatAmount, formatFileSize, validatePlate, normalizePlate } from "@/lib/api";
import PaymentMethodSelect from "@/components/PaymentMethodSelect";
import { brokerSplitPcts } from "@/lib/brokerSplit";
import { useFirstTimeHint } from "@/lib/useFirstTimeHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { StatusBadge } from "@/components/StatusBadge";
import { ExpiryTimeline } from "@/components/ExpiryTimeline";
import { PolicyDetailDialog } from "@/components/PolicyDetailDialog";
import { VehicleAutocomplete, COMMON_BRANDS, COMMON_MODELS_BY_BRAND } from "@/components/VehicleAutocomplete";
import { PlateInput } from "@/components/PlateInput";
import { ClientCombobox } from "@/components/ClientCombobox";
import { resolveGuarantee, buildGuaranteeCode } from "@/lib/guaranteeMatch";
import { DropZone } from "@/components/DropZone";
import { askMultiFileAction } from "@/components/MultiFileUploadDialog";
import { PolicyReadyNoticeDialog } from "@/components/PolicyReadyNoticeDialog";
// PolicySuspensionPanel deprecato: la UI è ora inline in Policies.jsx (blocco "policy-suspension-block").
import { PolicyVehicleDetails } from "@/components/PolicyVehicleDetails";
import { PolicyGuaranteesEditor } from "@/components/PolicyGuaranteesEditor";
import { VehiclesEditor, buildVehiclesPayload } from "@/components/VehiclesEditor";
import { BoatEnginesEditor, buildBoatEnginesPayload } from "@/components/BoatEnginesEditor";
import { useAttachmentPreview } from "@/hooks/useAttachmentPreview";
import { POLICY_TYPES as TYPES, POLICY_STATUSES as STATUS, FRAZIONAMENTI, POLICY_EMPTY as empty, addMonthsISO as addMonths, isMotoBrand, BOAT_ENGINE_INSTALL_OPTIONS, BOAT_ENGINE_FUEL_OPTIONS, BOAT_HULL_MATERIAL_OPTIONS } from "@/lib/policyConstants";
import { Plus, Trash2, Pencil, Printer, Car, Bike, Download, Paperclip, UserCircle, Eye, Sparkles, Home, Heart, Stethoscope, Activity, Plane, Shield, RefreshCw, Mail, ExternalLink, FileText, ChevronDown, ChevronUp, History, PauseCircle, PlayCircle, AlertTriangle, MessageSquareText, X, CreditCard, MoreVertical } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { AttachmentItem } from "@/components/AttachmentItem";
import DocumentTitleSelect from "@/components/DocumentTitleSelect";
import StaffEmailCombobox from "@/components/StaffEmailCombobox";
import SignaturePreviewDialog, { isPreviewSkipped } from "@/components/SignaturePreviewDialog";
import { AttachmentsHoverPreview } from "@/components/AttachmentsHoverPreview";
import { ClaimsDialog } from "@/components/ClaimsDialog";
import { SendMessageDialog } from "@/components/SendMessageDialog";
import { PolicyTypeBadge } from "@/components/PolicyTypeBadge";
import { NotesHoverIcon } from "@/components/NotesHoverIcon";
import { MoneyInput } from "@/components/MoneyInput";
import { ScanButton } from "@/components/ScanButton";
import { CameraButton } from "@/components/CameraButton";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { DatePicker } from "@/components/DatePicker";
import ApplyPdfTemplateButton from "@/components/ApplyPdfTemplateButton";
import { EmailPreviewDialog, shouldSkipEmailPreview } from "@/components/EmailPreviewDialog";
import { ExpiryNoticeDialog } from "@/components/ExpiryNoticeDialog";

// 🆕 16/07/2026 — Helper universale per copia negli appunti con toast di conferma
import { copyToClipboard } from "@/lib/clipboard";
import { downloadWithProgress, runExtractionWithProgress } from "@/lib/downloadWithProgress";

const buildPolicyWhatsAppLink = (client, policy) => {
  const rawPhone = client?.mobile || client?.phone;
  if (!rawPhone) return null;
  let normalized = String(rawPhone).replace(/[^\d+]/g, "");
  if (!normalized.startsWith("+")) {
    if (normalized.startsWith("00")) normalized = "+" + normalized.slice(2);
    else if (normalized.startsWith("3")) normalized = "+39" + normalized;
    else normalized = "+" + normalized;
  }
  const wa = normalized.replace("+", "");
  const polNum = policy?.policy_number ? ` n° ${policy.policy_number}` : "";
  const compagnia = policy?.company ? ` (${policy.company})` : "";
  const msg = encodeURIComponent(
    `Ciao ${client?.name || ""}, ti scrivo a proposito della tua polizza${polNum}${compagnia}. ` +
    `Fammi sapere quando hai un minuto.`,
  );
  return `whatsapp://send?phone=${wa}&text=${msg}`;
};

export default function Policies() {
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  useFirstTimeHint("crm_dblclick_hint_v1", "💡 Suggerimento: doppio click sul nome del cliente per aprire la scheda in modifica.");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  // 🆕 Feb 2026 — anteprima inline PDF/immagini
  const { openPreview: openAttachmentPreview, PreviewDialog: AttachmentPreviewDialog } = useAttachmentPreview();
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [partnerCompanyLinks, setPartnerCompanyLinks] = useState([]);
  // 🆕 Feb 2026 — Dialog "Registra incasso" per singola rata
  const [installmentPayDialog, setInstallmentPayDialog] = useState(null); // { date, amount, paid_date, method } | null
  // 🆕 16/07/2026 — Gestione Rinnovi/Sostituzioni/Offerte da polizza
  const [chainAction, setChainAction] = useState(null); // "renewal" | "replacement" | "quote_offer" | null
  const [chainActionBusy, setChainActionBusy] = useState(false);
  const [policyChain, setPolicyChain] = useState([]); // Array polizze catena (dalla più recente alla più vecchia)
  const [chainCurrentIdx, setChainCurrentIdx] = useState(0);
  // 🆕 Feb 2026 — Selezione causale sostituzione (mostrata prima del dialog di conferma)
  const [replacementReasons, setReplacementReasons] = useState([]);
  const [replacementReasonPickerOpen, setReplacementReasonPickerOpen] = useState(false);
  const [replacementReasonLoading, setReplacementReasonLoading] = useState(false);
  const [selectedReplacementReason, setSelectedReplacementReason] = useState(null); // { code, description }
  const [users, setUsers] = useState([]);  // staff agency users (per split provvigione collab)
  // 🆕 Feb 2026 — Conteggio ricevute emesse per polizza (badge nella riga della lista)
  const [receiptsByPolicy, setReceiptsByPolicy] = useState({});
  // 🆕 Feb 2026 — Catalogo Prodotti: dropdown Prodotto dopo la Compagnia
  const [products, setProducts] = useState([]);
  // 🆕 Feb 2026 — Config visibilità margini prodotto (Costo Acquisto/Vendita)
  const [marginConfig, setMarginConfig] = useState({ can_see: false });
  const canSeeMargins = !!marginConfig.can_see;
  const [categories, setCategories] = useState([]);
  const [policyTypes, setPolicyTypes] = useState([]);
  // 🆕 10/07/2026 — Opzioni Tipo di guida (esclusiva/esperta/libera) da lookup-list
  const [drivingTypeOpts, setDrivingTypeOpts] = useState([]);
  useEffect(() => {
    api.get("/lookup-lists/driving_type")
      .then(({ data }) => setDrivingTypeOpts((data || []).filter((x) => x.is_active !== false)))
      .catch(() => setDrivingTypeOpts([]));
  }, []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [revisioneFilter, setRevisioneFilter] = useState(false); // mostra solo polizze auto con revisione da gestire
  const [noCoveragesFilter, setNoCoveragesFilter] = useState(false); // mostra solo polizze senza garanzie
  const [toCompleteFilter, setToCompleteFilter] = useState(false); // 🆕 polizze in_emissione da offerta pagata
  const [multiVehFilter, setMultiVehFilter] = useState("all"); // 🆕 Feb 2026 — all | multi | flotta | storica
  // 🆕 Filtri "anomalie" attivabili dalla Dashboard (Admin Health Snapshot)
  const [noCompanyFilter, setNoCompanyFilter] = useState(false); // polizze senza compagnia (attive)
  const [noAttachmentsFilter, setNoAttachmentsFilter] = useState(false); // polizze senza allegati (attive)
  const [expiringWithinDays, setExpiringWithinDays] = useState(null); // 7/15/30 → polizze in scadenza entro N giorni
  // 🆕 Auto-focus sul campo "Numero polizza" quando si apre dal bottone "Emetti polizza"
  const [focusPolicyNumber, setFocusPolicyNumber] = useState(false);
  const [readyNoticePolicy, setReadyNoticePolicy] = useState(null); // 🆕 stato dialog "Polizza pronta"
  // 🆕 10/07/2026 — Prompt: rate con importo ma senza data effettivo. { count, firstDate }
  const [missingPaidDatesPrompt, setMissingPaidDatesPrompt] = useState(null);
  // 🆕 Anteprima email prima dell'invio (unificata via EmailPreviewDialog)
  const [emailPreviewCtx, setEmailPreviewCtx] = useState(null);
  // 🆕 Card "Dati cliente" in modifica polizza è collassabile (sandwich), persistita in localStorage
  // 🆕 Feb 2026 — Dialog "Collega area privata": permette di collegare le polizze
  // dell'intestatario di questa polizza all'area privata di un altro cliente esistente.
  // Utile per gestione famiglia (marito vede polizze moglie/figli) anche a posteriori.
  const [portalLinkDialog, setPortalLinkDialog] = useState(null); // { sourceClientId, sourceClientLabel, search }
  const [portalLinkExisting, setPortalLinkExisting] = useState([]); // [{owner_id, owner_label}]
  const [clientCardCollapsed, setClientCardCollapsed] = useState(() => {
    try { return localStorage.getItem("policies_client_card_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("policies_client_card_collapsed", clientCardCollapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [clientCardCollapsed]);
  // 🆕 Altre card collassabili nel form polizza (Importi, Scadenze rate, Provvigioni)
  const useCollapsedPref = (key, def = false) => {
    const [v, setV] = useState(() => {
      try { return localStorage.getItem(key) === "1" ? true : (localStorage.getItem(key) === "0" ? false : def); } catch { return def; }
    });
    useEffect(() => { try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* ignore */ } }, [v, key]);
    return [v, setV];
  };
  const [amountsCollapsed, setAmountsCollapsed] = useCollapsedPref("policies_amounts_collapsed", false);
  const [installmentsCollapsed, setInstallmentsCollapsed] = useCollapsedPref("policies_installments_collapsed", false);
  const [commissionsCollapsed, setCommissionsCollapsed] = useCollapsedPref("policies_commissions_collapsed", false);
  const [summaryCollapsed, setSummaryCollapsed] = useCollapsedPref("policies_summary_collapsed", false);
  const [paidCollapsed, setPaidCollapsed] = useCollapsedPref("policy_paid_collapsed", false);
  const [vehicleCollapsed, setVehicleCollapsed] = useCollapsedPref("policies_vehicle_collapsed", false);
  const [sendingExpiryNotice, setSendingExpiryNotice] = useState(false);
  // 🆕 28/07/2026 — Dialog "Avvisa" con scelta canale Email/WhatsApp/Entrambi
  const [noticeDialog, setNoticeDialog] = useState(null); // { policy, client }
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [brokerFilter, setBrokerFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [mine, setMine] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [attachOpen, setAttachOpen] = useState(null); // policy object
  // 🆕 Feb 2026 — Stato per aprire il dialog "Genera documento da modello" dal DropdownMenu
  const [pdfTplPolicyId, setPdfTplPolicyId] = useState(null);
  // 🆕 16/07/2026 — Stato locale flag allegati (visibile / notifica) per salvataggio esplicito
  const [pendingAttFlags, setPendingAttFlags] = useState({}); // { att_id: { client_visible, notify_email_pending } }
  const [savingFlags, setSavingFlags] = useState(false);
  const [notifyBatchOpen, setNotifyBatchOpen] = useState(false);
  const [notifyCustomMsg, setNotifyCustomMsg] = useState("");
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(null); // 🆕 policy for claims dialog
  const [msgTarget, setMsgTarget] = useState(null); // 🆕 policy for message dialog
  const [detailOpen, setDetailOpen] = useState(null); // policy object for "Vedi"
  // 🆕 16/07/2026 — Totali provvigione/fee emessi dal PolicyGuaranteesEditor (usati per le Scadenze rate)
  const [guaranteeTotals, setGuaranteeTotals] = useState(null);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewForm, setRenewForm] = useState({
    new_start_date: "",
    new_end_date: "",
    same_company: true,
    new_company_id: "",
    new_policy_number: "",
    new_premium: 0,
    new_frazionamento: "annuale",
    notes: "",
  });
  const [renewing, setRenewing] = useState(false);
  const [renewReturnTo, setRenewReturnTo] = useState(null);
  const startRenew = (p) => { setRenewTarget(p); setRenewForm({
    new_start_date: p.end_date || new Date().toISOString().slice(0, 10),
    new_end_date: "",
    same_company: true,
    new_company_id: "",
    new_policy_number: "",
    new_premium: p.premium || 0,
    new_frazionamento: p.frazionamento || "annuale",
    notes: "",
  }); };
  const closeRenew = () => {
    setRenewTarget(null);
    if (renewReturnTo) { const rt = renewReturnTo; setRenewReturnTo(null); navigate(rt); }
  };

  // Auto-ricalcolo della scadenza in base a data inizio + frazionamento
  useEffect(() => {
    if (!renewTarget || !renewForm.new_start_date || !renewForm.new_frazionamento) return;
    const fraz = FRAZIONAMENTI.find((f) => f.v === renewForm.new_frazionamento);
    if (!fraz) return;
    const computed = addMonths(renewForm.new_start_date, fraz.months);
    if (computed && computed !== renewForm.new_end_date) {
      setRenewForm((f) => ({ ...f, new_end_date: computed }));
    }
  }, [renewForm.new_start_date, renewForm.new_frazionamento, renewForm.new_end_date, renewTarget]);

  const confirmRenew = async () => {
    if (!renewTarget || !renewForm.new_start_date) {
      toast.error("Inserisci la nuova data di inizio");
      return;
    }
    if (!renewForm.same_company && (!renewForm.new_company_id || !renewForm.new_policy_number)) {
      toast.error("Compagnia e numero polizza obbligatori se cambia compagnia");
      return;
    }
    setRenewing(true);
    try {
      const body = {
        new_start_date: renewForm.new_start_date,
        new_end_date: renewForm.new_end_date || null,
        same_company: renewForm.same_company,
        new_company_id: renewForm.same_company ? null : renewForm.new_company_id,
        new_policy_number: renewForm.same_company ? null : renewForm.new_policy_number,
        new_premium: Number(renewForm.new_premium) || 0,
        new_frazionamento: renewForm.new_frazionamento,
        notes: renewForm.notes || null,
      };
      const r = await api.post(`/policies/${renewTarget.id}/renew`, body);
      // 🆕 Feb 2026 — Fix: il clone backend copia le vecchie % imposte/SSN/netto sulle coverages.
      // Azzeriamole subito così l'utente, al successivo import PDF (extractFromPolicyPDF),
      // le vedrà ricalcolate dai dati del nuovo PDF invece di leggere quelle del vecchio contratto.
      try {
        const newPol = r?.data?.new_policy;
        if (newPol && newPol.id && Array.isArray(newPol.coverages) && newPol.coverages.length) {
          const resetCoverages = newPol.coverages.map((c) => ({
            ...c,
            imposte_percent: null,
            ssn_percent: null,
            net_premium: null,
          }));
          await api.put(`/policies/${newPol.id}`, { ...newPol, coverages: resetCoverages });
        }
      } catch (_err) {
        // Non blocchiamo il flusso: il fallback di calcolo in extractFromPolicyPDF gestirà comunque il caso
      }
      toast.success(r.data?.message || "Polizza rinnovata");
      load();
      closeRenew();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRenewing(false); }
  };

  // 🆕 Sospensione / Riattivazione polizza
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendForm, setSuspendForm] = useState({ suspension_start_date: "", reason: "" });
  const [suspending, setSuspending] = useState(false);
  const [suspendResult, setSuspendResult] = useState(null); // { whatsapp_url, whatsapp_text }
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [reactivateForm, setReactivateForm] = useState({ reactivation_date: "" });
  const [reactivating, setReactivating] = useState(false);
  const [reactivateResult, setReactivateResult] = useState(null);
  // 🆕 28/07/2026 — Toggle storico sospensioni inline (sotto lo Stato)
  const [showSuspensionHistory, setShowSuspensionHistory] = useState(false);

  const openSuspend = (p) => {
    setSuspendTarget(p);
    setSuspendForm({ suspension_start_date: new Date().toISOString().slice(0, 10), reason: "" });
    setSuspendResult(null);
  };
  const confirmSuspend = async () => {
    if (!suspendTarget || !suspendForm.suspension_start_date) {
      toast.error("Inserisci la data di sospensione");
      return;
    }
    setSuspending(true);
    try {
      const { data } = await api.post(`/policies/${suspendTarget.id}/suspend`, {
        suspension_start_date: suspendForm.suspension_start_date,
        reason: suspendForm.reason || null,
      });
      toast.success("Polizza sospesa");
      setSuspendResult(data);
      // 🆕 Feb 2026 — Aggiorna il dialog Modifica se aperto sulla stessa polizza
      // così status, badge e toolbar (Sospendi → Riattiva) si sincronizzano subito.
      try {
        const { data: fresh } = await api.get(`/policies/${suspendTarget.id}`);
        if (fresh && fresh.id) {
          setEditing((prev) => (prev && prev.id === suspendTarget.id ? { ...prev, ...fresh } : prev));
          setForm((prev) => (prev && prev.id === suspendTarget.id ? { ...prev, ...fresh } : prev));
        }
      } catch (_) { /* non-blocking */ }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSuspending(false); }
  };

  const openReactivate = (p) => {
    setReactivateTarget(p);
    // 🆕 28/07/2026 — Se il form ha `suspension_end_date` valorizzato (nuova UI inline),
    // usa quello come default per la riattivazione, altrimenti oggi.
    const prefill = (p && p._prefill_reactivation_date) || new Date().toISOString().slice(0, 10);
    setReactivateForm({ reactivation_date: prefill });
  };
  const confirmReactivate = async () => {
    if (!reactivateTarget || !reactivateForm.reactivation_date) {
      toast.error("Inserisci la data di riattivazione");
      return;
    }
    setReactivating(true);
    try {
      const { data } = await api.post(`/policies/${reactivateTarget.id}/reactivate`, {
        reactivation_date: reactivateForm.reactivation_date,
      });
      // 🆕 Feb 2026 — La riattivazione ora crea una NUOVA polizza (duplicazione)
      // e archivia l'originale come "sostituita_riattivazione".
      toast.success(
        data.new_policy_number
          ? `Nuova polizza ${data.new_policy_number} creata (attiva) · scadenza ${formatDate(data.new_end_date)} · ${data.days_suspended}gg fermo`
          : `Polizza riattivata — nuova scadenza: ${formatDate(data.new_end_date)} (${data.days_suspended}gg di fermo)`
      );
      setReactivateResult(data);
      // 🆕 Feb 2026 — La duplicazione avviene SUBITO lato backend (endpoint /reactivate).
      // Chiudiamo il dialog Modifica aperto sulla vecchia polizza (ora archiviata come
      // "sostituita_riattivazione", quindi non più modificabile né visibile in lista):
      // così l'utente vede immediatamente il clone attivo nella lista senza dover premere Salva.
      // Nessuna copia in editing/form → il useEffect di auto-compute end_date non parte
      // e la scadenza calcolata dal backend viene preservata.
      if (editing && reactivateTarget && editing.id === reactivateTarget.id) {
        setEditing(null);
        setForm(empty);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReactivating(false); }
  };

  // 🆕 Proroga sospensione (regola dei 10 giorni gestita lato backend)
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendForm, setExtendForm] = useState({ additional_months: 3, reason: "" });
  const [extending, setExtending] = useState(false);
  const [extendResult, setExtendResult] = useState(null);
  const openExtend = (p) => {
    setExtendTarget(p);
    setExtendForm({ additional_months: 3, reason: "" });
    setExtendResult(null);
  };
  const confirmExtend = async () => {
    if (!extendTarget) return;
    setExtending(true);
    try {
      const { data } = await api.post(`/policies/${extendTarget.id}/extend-suspension`, {
        additional_months: Number(extendForm.additional_months) || 0,
        reason: extendForm.reason || null,
      });
      toast.success(`Proroga registrata — nuovo termine: ${formatDate(data.reactivation_deadline)}`);
      setExtendResult(data);
      // 🆕 Feb 2026 — Sync dialog Modifica se aperto sulla stessa polizza
      try {
        const { data: fresh } = await api.get(`/policies/${extendTarget.id}`);
        if (fresh && fresh.id) {
          setEditing((prev) => (prev && prev.id === extendTarget.id ? { ...prev, ...fresh } : prev));
          setForm((prev) => (prev && prev.id === extendTarget.id ? { ...prev, ...fresh } : prev));
        }
      } catch (_) { /* non-blocking */ }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExtending(false); }
  };
  const [editAttachments, setEditAttachments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachDesc, setAttachDesc] = useState("");
  // Selezione & invio allegati via email
  const [selectedForEmail, setSelectedForEmail] = useState({});
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: "", subject: "", body: "" });
  const [sendBusy, setSendBusy] = useState(false);
  const [sendLogs, setSendLogs] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, label} per dialog conferma
  const [deleting, setDeleting] = useState(false);
  const [attDeleteTarget, setAttDeleteTarget] = useState(null);
  const [attDeleting, setAttDeleting] = useState(false);
  const { user: currentUser } = useAuth();
  // Bulk delete (admin-only, OTP via email)
  const isAdmin = currentUser?.role === "admin";
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkStage, setBulkStage] = useState(null); // null | "confirm" | "otp"
  const [bulkOtp, setBulkOtp] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkOtpInfo, setBulkOtpInfo] = useState(null); // {email, policy_count}

  const load = useCallback(async () => {
    try {
      const params = {};
      if (mine) params.mine = true;
      if (showArchive) params.archived = true;
      const [p, c, co, b, cat, pt, u, pcl, rc, prd, mcfg] = await Promise.all([
        api.get("/policies", { params }),
        api.get("/clients"), api.get("/companies"), api.get("/brokers"),
        api.get("/policy-categories"), api.get("/policy-types"), api.get("/users").catch(() => ({ data: [] })),
        api.get("/partner-company-links").catch(() => ({ data: [] })),
        api.get("/receipts/counts-by-policy").catch(() => ({ data: { counts: {} } })),
        api.get("/products", { params: { active_only: true } }).catch(() => ({ data: [] })),
        api.get("/product-margins-config").catch(() => ({ data: { can_see: false } })),
      ]);
      setItems(p.data); setClients(c.data); setCompanies(co.data); setBrokers(b.data); setCategories(cat.data); setPolicyTypes(pt.data); setUsers(u.data || []);
      setPartnerCompanyLinks(pcl.data || []);
      setReceiptsByPolicy(rc.data?.counts || {});
      setProducts(prd.data || []);
      setMarginConfig(mcfg.data || { can_see: false });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [mine, showArchive]);
  useEffect(() => { load(); }, [load]);

  // 🆕 Helper: determina se il tipo polizza selezionato ha i campi veicolo abilitati
  // in base alla configurazione dinamica di `policy_types` (Impostazioni → Tipologie polizza).
  // Fallback: "auto" e "moto" hardcoded per retro-compatibilità se la config non è caricata.
  const currentPolicyTypeConfig = useMemo(
    () => (policyTypes || []).find((t) => t.code === form.type) || null,
    [policyTypes, form.type],
  );
  const typeHasVehicleFields = useMemo(() => {
    if (currentPolicyTypeConfig && typeof currentPolicyTypeConfig.vehicle_fields_enabled === "boolean") {
      return currentPolicyTypeConfig.vehicle_fields_enabled;
    }
    // Fallback finché policy_types non è caricato
    return form.type === "auto" || form.type === "moto";
  }, [currentPolicyTypeConfig, form.type]);

  // Auto-apri polizza da query param ?focus=<id> (es. dal doppio click in Estratto Conto / Wizard "salva e apri")
  // Se è presente anche ?return_to=<url> torna a quella pagina alla chiusura del dialog.
  const [searchParams, setSearchParams] = useSearchParams();
  const [flashId, setFlashId] = useState(null);
  const [policyReturnTo, setPolicyReturnTo] = useState(null);
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || !items) return;
    let cancelled = false;
    const clearFocusParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      next.delete("return_to");
      next.delete("action");
      next.delete("from_brokers");
      setSearchParams(next, { replace: true });
    };
    const openFound = (p) => {
      const rt = searchParams.get("return_to");
      if (rt) setPolicyReturnTo(rt);
      const fromBrokers = searchParams.get("from_brokers") === "1";
      if (searchParams.get("action") === "renew") {
        if (rt) { setPolicyReturnTo(null); setRenewReturnTo(rt); }
        startRenew(p);
        setFlashId(focusId);
        clearFocusParams();
        return undefined;
      }
      openEdit(p);
      setFlashId(focusId);
      if (fromBrokers) {
        toast.success("Compagnie aggiornate — dropdown ricaricato");
      }
      // 🆕 Se ?action=extract è presente (provenienza: banner offerta pagata),
      //    apri direttamente il file picker per allegare la copia polizza ed estrarre i dati.
      const action = searchParams.get("action");
      if (action === "extract") {
        setTimeout(() => {
          const inp = document.querySelector('[data-testid="import-policy-pdf"]');
          if (inp) inp.click();
        }, 600);
      }
      const t1 = setTimeout(() => {
        const el = document.getElementById(`policy-row-${focusId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      const t2 = setTimeout(() => setFlashId(null), 2500);
      clearFocusParams();
      return () => { clearTimeout(t1); clearTimeout(t2); };
    };
    const p = items.find((x) => x.id === focusId);
    if (p) {
      return openFound(p);
    }
    // 🆕 28/07/2026 — Fallback: se la lista già caricata non contiene la polizza
    // (es. filtri o vista storica), interroga GET /api/policies/:id per aprirla comunque.
    if (items.length === 0) return; // ancora in caricamento, riprova al prossimo tick
    (async () => {
      try {
        const { data } = await api.get(`/policies/${focusId}`);
        if (!cancelled && data && data.id) openFound(data);
        else if (!cancelled) {
          toast.error("Polizza non trovata");
          clearFocusParams();
        }
      } catch (_err) {
        if (!cancelled) {
          toast.error("Polizza non trovata");
          clearFocusParams();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [items, searchParams, setSearchParams]);

  // 🆕 Click-to-filter dalla Dashboard: legge ?filter=<status> o ?status=<status> al mount e imposta il filtro corrispondente.
  // Valori supportati: TUTTI quelli di POLICY_STATUSES (allineati al backend PolicyStatus)
  //   + all, expiring, expiring_7/15/30 (alias UI).
  // ?anomaly=no_company|no_attachments attiva i filtri "anomalie" specifici.
  // I param vengono rimossi dall'URL dopo l'applicazione per evitare che sovrascrivano le successive interazioni utente.
  useEffect(() => {
    const rawFilter = searchParams.get("filter") || searchParams.get("status");
    const rawAnomaly = searchParams.get("anomaly");
    let changed = false;
    if (rawFilter) {
      const map = { expiring: "in_scadenza", expiring_30: "in_scadenza", expiring_15: "in_scadenza", expiring_7: "in_scadenza" };
      const desired = map[rawFilter] || rawFilter;
      // 🆕 Fix Feb 2026 — allineato a POLICY_STATUSES (include rinnovata, in_emissione, sostituita_riattivazione)
      const validFilters = ["all", ...STATUS];
      if (validFilters.includes(desired) && desired !== filter) {
        setFilter(desired);
        // 🆕 28/07/2026 — Anche il deep-link ?filter=disdetta|sostituita|rinnovata richiede showArchive=true
        if (["disdetta", "sostituita", "sostituita_riattivazione", "rinnovata"].includes(desired)) {
          setShowArchive(true);
        }
        changed = true;
      }
      if (rawFilter === "expiring_7") { setExpiringWithinDays(7); changed = true; }
      else if (rawFilter === "expiring_15") { setExpiringWithinDays(15); changed = true; }
      else if (rawFilter === "expiring_30" || rawFilter === "expiring") { setExpiringWithinDays(30); changed = true; }
    }
    if (rawAnomaly === "no_company") { setNoCompanyFilter(true); setFilter("all"); changed = true; }
    else if (rawAnomaly === "no_attachments") { setNoAttachmentsFilter(true); setFilter("all"); changed = true; }
    if (!changed) return;
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    next.delete("status");
    next.delete("anomaly");
    setSearchParams(next, { replace: true });
  }, []);

  // Chiude il dialog Modifica polizza ripulendo lo stato e, se aperto con ?return_to=,
  // naviga alla pagina di provenienza (es. Estratto Conto).
  const closePolicyDialog = useCallback(() => {
    setOpen(false);
    setForm(empty);
    setEditing(null);
    setPendingFiles([]);
    if (policyReturnTo) {
      const target = policyReturnTo;
      setPolicyReturnTo(null);
      setTimeout(() => navigate(target), 50);
    }
  }, [navigate, policyReturnTo]);

  // 🆕 Compagnie filtrate per il broker selezionato (via partner_company_links attivi).
  // Fallback a tutte le compagnie se nessun broker selezionato o link inattivi/assenti.
  const getCompaniesForBroker = (brokerId) => {
    if (!brokerId) return companies;
    const links = (partnerCompanyLinks || []).filter(
      (l) => l.partner_id === brokerId && l.active !== false
    );
    if (links.length === 0) return companies;
    const ids = new Set(links.map((l) => l.linked_company_id));
    return (companies || []).filter((c) => ids.has(c.id));
  };
  const getLinkedCompaniesCount = (brokerId) => {
    if (!brokerId) return null;
    const count = (partnerCompanyLinks || []).filter(
      (l) => l.partner_id === brokerId && l.active !== false
    ).length;
    return count > 0 ? count : null;
  };

  // Brand/model suggestions (predictive)
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [modelSuggestions, setModelSuggestions] = useState([]);
  // File in attesa di essere allegati alla polizza (drag&drop nel dialog)
  const [pendingFiles, setPendingFiles] = useState([]);
  // 🆕 Feb 2026 — Dialog unificato di import allegati (merge PDF + rinomina + categoria)
  // Attivato da drag&drop, pulsante sfoglia, scanner e fotocamera. Sostituisce l'upload diretto.
  const POLICY_ATT_CATEGORIES = useMemo(() => [
    "Polizza firmata",
    "Attestato di rischio",
    "Scheda tecnica",
    "Documento identità",
    "Preventivo controfirmato",
    "Fattura / Quietanza",
    "Libretto / Carta di circolazione",
    "Comunicazione compagnia",
    "Altro",
  ], []);
  const [importDialog, setImportDialog] = useState(null); // { files, merge, mergedName, newNames[], category, customCategory, busy }
  // Overlay full-screen drag&drop quando il dialog polizza è aperto
  const [dragOverlayActive, setDragOverlayActive] = useState(false);
  useEffect(() => {
    // Attivo solo quando l'edit dialog è aperto e il dialog allegati NON è aperto
    // (quest'ultimo ha già il suo DropZone interno)
    if (!open || attachOpen) return;
    let counter = 0;
    const isFileDrag = (e) => {
      const types = e.dataTransfer?.types;
      return types && (Array.from(types).includes("Files") || Array.from(types).includes("application/x-moz-file"));
    };
    const onDragEnter = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counter += 1;
      if (counter === 1) setDragOverlayActive(true);
    };
    const onDragOver = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    const onDragLeave = (e) => {
      if (!isFileDrag(e)) return;
      counter = Math.max(0, counter - 1);
      if (counter === 0) setDragOverlayActive(false);
    };
    const onDrop = async (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counter = 0;
      setDragOverlayActive(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;
      // 🆕 Feb 2026 — Unificato: anche il drag&drop apre il dialog "merge + rinomina + categoria"
      openImportDialog(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
      setDragOverlayActive(false);
    };
  }, [open, attachOpen, editing?.id]);
  useEffect(() => {
    api.get("/utils/vehicle-brands")
      .then(({ data }) => setBrandSuggestions([...COMMON_BRANDS, ...(data.brands || [])]))
      .catch(() => setBrandSuggestions(COMMON_BRANDS));
  }, []);
  useEffect(() => {
    const b = (form.vehicle_brand || "").trim();
    const localModels = b ? (COMMON_MODELS_BY_BRAND[b.toLowerCase()] || []) : [];
    if (!b) { setModelSuggestions(localModels); return; }
    const t = setTimeout(() => {
      api.get("/utils/vehicle-models", { params: { brand: b } })
        .then(({ data }) => setModelSuggestions([...localModels, ...(data.models || [])]))
        .catch(() => setModelSuggestions(localModels));
    }, 250);
    return () => clearTimeout(t);
  }, [form.vehicle_brand]);

  const loadAttachments = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/policies/${pid}/attachments`);
      setAttachments(data);
      // 🆕 Inizializza flag pending con valori attuali (client_visible + notify_email_pending)
      const nextFlags = {};
      (data || []).forEach((a) => {
        nextFlags[a.id] = {
          client_visible: !!a.client_visible,
          notify_email_pending: !!a.notify_email_pending,
        };
      });
      setPendingAttFlags(nextFlags);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  // 🆕 Helper: c'è almeno un flag modificato rispetto ai valori originali?
  const flagsDirty = useMemo(() => {
    return (attachments || []).some((a) => {
      const p = pendingAttFlags[a.id] || {};
      return (
        (p.client_visible ?? !!a.client_visible) !== !!a.client_visible ||
        (p.notify_email_pending ?? !!a.notify_email_pending) !== !!a.notify_email_pending
      );
    });
  }, [attachments, pendingAttFlags]);

  const pendingNotifyIds = useMemo(() => {
    return (attachments || [])
      .filter((a) => (pendingAttFlags[a.id]?.notify_email_pending ?? !!a.notify_email_pending))
      .map((a) => a.id);
  }, [attachments, pendingAttFlags]);

  const saveAttFlags = useCallback(async () => {
    if (!flagsDirty) return;
    setSavingFlags(true);
    try {
      const items = (attachments || []).map((a) => ({
        id: a.id,
        client_visible: !!(pendingAttFlags[a.id]?.client_visible),
        notify_email_pending: !!(pendingAttFlags[a.id]?.notify_email_pending),
      }));
      await api.patch("/attachments/batch-flags", { items });
      toast.success("Flag allegati salvati.");
      if (attachOpen?.id) await loadAttachments(attachOpen.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingFlags(false);
    }
  }, [attachments, pendingAttFlags, flagsDirty, attachOpen, loadAttachments]);

  const sendBatchNotification = useCallback(async () => {
    if (!attachOpen || pendingNotifyIds.length === 0) return;
    setNotifyBusy(true);
    try {
      // Se ci sono flag non salvati, salvali prima
      if (flagsDirty) {
        const items = (attachments || []).map((a) => ({
          id: a.id,
          client_visible: !!(pendingAttFlags[a.id]?.client_visible),
          notify_email_pending: !!(pendingAttFlags[a.id]?.notify_email_pending),
        }));
        await api.patch("/attachments/batch-flags", { items });
      }
      const res = await api.post(`/policies/${attachOpen.id}/attachments/notify-batch`, {
        attachment_ids: pendingNotifyIds,
        custom_message: (notifyCustomMsg || "").trim() || null,
      });
      toast.success(`Notifica inviata a ${res.data.recipient} (${res.data.notified_count} file).`);
      setNotifyBatchOpen(false);
      setNotifyCustomMsg("");
      await loadAttachments(attachOpen.id);
      loadSendLogs(attachOpen.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNotifyBusy(false);
    }
  }, [attachOpen, pendingNotifyIds, notifyCustomMsg, attachments, pendingAttFlags, flagsDirty, loadAttachments]);

  const loadEditAttachments = useCallback(async (pid) => {
    if (!pid) { setEditAttachments([]); return; }
    try {
      const { data } = await api.get(`/policies/${pid}/attachments`);
      setEditAttachments(data || []);
    } catch { setEditAttachments([]); }
  }, []);

  // 🆕 Handler unificato: se la polizza è ESISTENTE → carica subito, altrimenti coda in pendingFiles
  const handleFilesAdd = useCallback((files) => {
    const arr = Array.isArray(files) ? files : Array.from(files || []);
    if (arr.length === 0) return;
    // 🆕 Feb 2026 — Nuovo flusso unificato: NON caricare direttamente, apri il dialog "import"
    // (merge PDF opzionale + rinomina + categoria) → upload avviene solo in `confirmImport`.
    openImportDialog(arr);
  }, []);

  // 🆕 Feb 2026 — Apre il dialog di import allegati con preset intelligenti.
  const openImportDialog = useCallback((files) => {
    const arr = Array.from(files || []).filter(Boolean);
    if (arr.length === 0) return;
    const names = arr.map((f) => (f.name || "").replace(/\.[^.]+$/, ""));
    setImportDialog({
      files: arr,
      merge: false,
      mergedName: `allegato_${new Date().toISOString().slice(0, 10)}`,
      newNames: names,
      category: "",
      customCategory: "",
      busy: false,
    });
  }, []);

  // 🆕 Feb 2026 — Conferma import: (opzionale) merge PDF → upload con categoria.
  const confirmImport = useCallback(async () => {
    if (!importDialog) return;
    const { files, merge, mergedName, newNames, category, customCategory } = importDialog;
    const finalCategory = (category === "Altro" ? (customCategory || "").trim() : category).trim();
    setImportDialog((s) => ({ ...s, busy: true }));

    const applyExt = (newBase, origName, forcedExt) => {
      const safe = (newBase || "").trim() || (origName || "file").replace(/\.[^.]+$/, "");
      if (forcedExt) {
        return /\.[a-zA-Z0-9]{1,6}$/.test(safe) ? safe : `${safe}.${forcedExt}`;
      }
      const origExt = (origName || "").split(".").pop() || "";
      const hasExt = /\.[a-zA-Z0-9]{1,6}$/.test(safe);
      return hasExt ? safe : (origExt ? `${safe}.${origExt}` : safe);
    };

    let uploadQueue = [];
    try {
      if (merge && files.length >= 2) {
        // Server-side merge → 1 PDF
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f, f.name));
        const res = await api.post("/files/process-multi?mode=merge", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        const mName = applyExt(mergedName, "merged.pdf", "pdf");
        const mergedFile = new File([res.data], mName, { type: "application/pdf" });
        uploadQueue = [mergedFile];
      } else {
        uploadQueue = files.map((f, i) => {
          const finalName = applyExt(newNames[i], f.name, null);
          return new File([f], finalName, { type: f.type });
        });
      }

      if (editing?.id) {
        // Upload immediato con categoria
        let ok = 0;
        for (const f of uploadQueue) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            const qs = new URLSearchParams({ description: "", category: finalCategory });
            await api.post(`/policies/${editing.id}/attachments?${qs.toString()}`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            ok++;
          } catch (err) {
            toast.error(`Upload "${f.name}" fallito: ${formatApiError(err)}`);
          }
        }
        if (ok > 0) {
          toast.success(`${ok} allegat${ok === 1 ? "o" : "i"} caricat${ok === 1 ? "o" : "i"}${finalCategory ? ` · ${finalCategory}` : ""} ✓`);
          loadEditAttachments(editing.id);
          if (attachOpen?.id === editing.id) loadAttachments(editing.id);
        }
      } else {
        // Nuova polizza: accumula in pendingFiles con metadati per upload al salvataggio
        const tagged = uploadQueue.map((f) => {
          try { f._category = finalCategory; } catch { /* File immutabile su alcuni browser — fallback: nome contiene chip visibile */ }
          return f;
        });
        setPendingFiles((prev) => [...prev, ...tagged]);
        toast.success(`${tagged.length} file in coda${finalCategory ? ` · ${finalCategory}` : ""} · saranno caricati al salvataggio`);
      }
      setImportDialog(null);
    } catch (e) {
      toast.error(formatApiError(e) || "Errore import allegati");
      setImportDialog((s) => (s ? { ...s, busy: false } : s));
    }
  }, [importDialog, editing?.id, attachOpen?.id, loadEditAttachments, loadAttachments]);

  const loadSendLogs = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/policies/${pid}/attachment-send-logs`);
      setSendLogs(data);
    } catch { setSendLogs([]); }
  }, []);

  const openSendDialog = () => {
    if (!attachOpen) return;
    const client = clients.find((c) => c.id === attachOpen.client_id);
    const ids = Object.entries(selectedForEmail).filter(([_, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.warning("Seleziona almeno un allegato"); return; }
    setSendForm({
      to_email: client?.email || "",
      subject: `Documenti polizza ${attachOpen.policy_number || ""}`.trim(),
      body: `Gentile ${client?.surname || ""} ${client?.name || ""},\n\nin allegato i documenti relativi alla sua polizza ${attachOpen.policy_number || ""}.\n\nCordiali saluti.`,
    });
    setSendDialogOpen(true);
  };

  const confirmSendAttachments = async () => {
    if (!attachOpen || sendBusy) return;
    const ids = Object.entries(selectedForEmail).filter(([_, v]) => v).map(([k]) => k);
    if (ids.length === 0) return;
    if (!sendForm.to_email || !sendForm.to_email.includes("@")) {
      toast.error("Email destinatario non valida"); return;
    }
    setSendBusy(true);
    try {
      const { data } = await api.post(`/policies/${attachOpen.id}/send-attachments`, {
        attachment_ids: ids,
        to_email: sendForm.to_email,
        subject: sendForm.subject,
        body: sendForm.body,
      });
      toast.success(`Email inviata a ${data.recipient} (${data.sent} allegat${data.sent === 1 ? "o" : "i"})`);
      setSendDialogOpen(false);
      setSelectedForEmail({});
      loadSendLogs(attachOpen.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendBusy(false);
    }
  };

  const onUploadFile = async (f) => {
    if (!f || !attachOpen) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const desc = encodeURIComponent(attachDesc || "");
      await api.post(`/policies/${attachOpen.id}/attachments?description=${desc}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Allegato caricato");
      setAttachDesc("");
      loadAttachments(attachOpen.id);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const onUpload = async (e) => {
    const fl = Array.from(e.target.files || []);
    if (fl.length > 0) {
      const processed = await askMultiFileAction(fl);
      for (const f of processed) await onUploadFile(f);
    }
    e.target.value = "";
  };

  const onUploadDrop = async (files) => {
    const processed = await askMultiFileAction(files);
    for (const f of processed) await onUploadFile(f);
  };

  const previewPolicyAttachment = useCallback((att) => {
    openAttachmentPreview(`/attachments/${att.id}/download`, {
      filename: att.original_filename || att.filename,
      content_type: att.content_type,
    });
  }, [openAttachmentPreview]);

  const downloadAttachment = async (att) => {
    // 🆕 Feb 2026 — Download con progress bar (helper globale)
    await downloadWithProgress({
      label: att.original_filename || att.filename || "allegato",
      signedUrlPath: `/attachments/${att.id}/signed-url`,
      blobPath: `/attachments/${att.id}/download`,
      filename: att.original_filename || att.filename,
    }).catch(() => { /* toast già mostrato dall'helper */ });
  };

  const removeAttachment = (att) => {
    setAttDeleteTarget({ id: att.id, label: att.original_filename || att.id });
  };

  // 🆕 Feb 2026 — Invia allegato per Firma OTP con dialog titolo documento
  const [signAttDialog, setSignAttDialog] = useState(null); // { att, title, notify_email }
  const [signAttBusy, setSignAttBusy] = useState(false);
  // 🆕 Feb 2026 — Preview pre-invio firma OTP allegato
  const [attPreviewDialog, setAttPreviewDialog] = useState(null); // { payload, data }
  const [attPreviewBusy, setAttPreviewBusy] = useState(false);

  const requestSignatureForAttachment = (att) => {
    if (!editing?.client_id) {
      toast.error("Nessun cliente associato alla polizza");
      return;
    }
    setSignAttDialog({ att, title: "", notify_email: "", client_id: editing.client_id, require_drawn: false });
  };

  const submitAttSignature = async () => {
    if (!signAttDialog) return;
    const title = (signAttDialog.title || "").trim();
    if (!title) {
      toast.error("Seleziona o inserisci il titolo del documento");
      return;
    }
    const targetClientId = signAttDialog.client_id || editing?.client_id;
    if (!targetClientId) {
      toast.error("Seleziona un cliente destinatario");
      return;
    }
    // Costruisco il payload per l'invio (usato subito se skip preview, altrimenti passato al preview)
    const payload = {
      attachment_source: "policy",
      attachment_id: signAttDialog.att.id,
      client_id: targetClientId,
      document_title: title,
      notify_email: signAttDialog.notify_email || undefined,
      require_drawn_signature: !!signAttDialog.require_drawn,
    };
    if (isPreviewSkipped()) {
      return doAttSignatureSend(payload, title);
    }
    // Recupero email/mobile del cliente dai dati già caricati
    const c = clients.find((x) => x.id === targetClientId) || {};
    const isCompany = c.subject_type === "company";
    const emailAddr = (isCompany ? (c.legale_rappresentante_email || c.email) : c.email) || "";
    const mobileAddr = (isCompany ? (c.legale_rappresentante_mobile || c.mobile || c.phone) : (c.mobile || c.phone)) || "";
    const cleanSurname = (c.surname || "").trim() === "-" ? "" : (c.surname || "");
    const recipientName = isCompany
      ? (c.ragione_sociale || c.company_name || c.name || "").trim()
      : `${cleanSurname} ${c.name || ""}`.replace(/\s+/g, " ").trim();
    // URL PDF anteprima allegato — richiediamo un signed url e apriamo in nuova tab
    let pdfBlobUrl = null;
    try {
      const resp = await api.get(`/attachments/${signAttDialog.att.id}/signed-url`);
      if (resp?.data?.url) pdfBlobUrl = resp.data.url;
    } catch { /* preview opzionale */ }
    setAttPreviewDialog({
      payload,
      title,
      data: {
        title,
        recipient_name: recipientName,
        recipient_email: emailAddr,
        recipient_mobile: mobileAddr,
        pdf_url: pdfBlobUrl,
        doc_type_label: "Allegato polizza",
        require_drawn_signature: payload.require_drawn_signature,
      },
    });
  };

  const doAttSignatureSend = async (payload, title) => {
    setSignAttBusy(true);
    try {
      await api.post("/attachments/request-signature", payload);
      toast.success(`Invito firma "${title}" inviato via email al cliente`);
      setSignAttDialog(null);
      setAttPreviewDialog(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSignAttBusy(false);
    }
  };

  const confirmAttPreview = async () => {
    if (!attPreviewDialog) return;
    setAttPreviewBusy(true);
    try {
      await doAttSignatureSend(attPreviewDialog.payload, attPreviewDialog.title);
    } finally {
      setAttPreviewBusy(false);
    }
  };

  const confirmRemoveAttachment = async () => {
    if (!attDeleteTarget) return;
    setAttDeleting(true);
    try {
      await api.delete(`/attachments/${attDeleteTarget.id}`);
      toast.success("Allegato eliminato ✓");
      setAttDeleteTarget(null);
      // 🆕 Refresh sia il pannello "Invia allegati" che la lista inline "Modifica polizza"
      if (attachOpen?.id) loadAttachments(attachOpen.id);
      if (editing?.id) loadEditAttachments(editing.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAttDeleting(false);
    }
  };

  const exportCSV = async () => {
    try {
      const resp = await api.get("/policies/export.csv", { params: mine ? { mine: true } : {}, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `polizze_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((p) => {
    if (filter !== "all") {
      // 🆕 09/07/2026 — Il filtro "in_scadenza" ora considera anche le polizze
      // ATTIVE con end_date entro 30 giorni (non solo quelle con status=in_scadenza
      // salvato in DB, che dipende dal cron giornaliero). Così l'elenco resta
      // coerente con la card Dashboard "In scadenza 30gg".
      if (filter === "in_scadenza") {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        // 🆕 22/07/2026 — Considera la scadenza effettiva (prossima rata non pagata) se presente
        const scadenzaIso = p.effective_end_date || p.end_date;
        const end = scadenzaIso ? new Date(scadenzaIso) : null;
        if (end) end.setHours(0, 0, 0, 0);
        const days = end ? Math.round((end - today) / (1000 * 60 * 60 * 24)) : null;
        const isExpiringWithin30 = days != null && days >= 0 && days <= 30;
        const okStatus = ["attiva", "in_scadenza"].includes(p.status);
        if (!(okStatus && isExpiringWithin30)) return false;
      } else if (p.status !== filter) {
        return false;
      }
    }
    if (categoryFilter !== "all" && p.category_id !== categoryFilter) return false;
    if (brokerFilter !== "all") {
      if (brokerFilter === "__none__") {
        if (p.broker_id) return false;
      } else if (p.broker_id !== brokerFilter) {
        return false;
      }
    }
    if (companyFilter !== "all" && p.company_id !== companyFilter) return false;
    // Filtro revisioni da gestire: solo polizze auto con next_review_date mancante,
    // scaduta o in scadenza entro 30 gg.
    if (revisioneFilter) {
      if (p.type !== "auto") return false;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const rev = p.next_review_date ? new Date(p.next_review_date) : null;
      if (rev) rev.setHours(0, 0, 0, 0);
      const days = rev ? Math.round((rev - today) / (1000 * 60 * 60 * 24)) : null;
      const needsAttention = !rev || days < 0 || days <= 30;
      if (!needsAttention) return false;
    }
    if (noCoveragesFilter) {
      const cnt = Array.isArray(p.coverages) ? p.coverages.length : 0;
      if (cnt > 0) return false;
    }
    if (toCompleteFilter) {
      // Polizza creata automaticamente da offerta pagata, ancora in fase di emissione
      if (!(p.status === "in_emissione" && p.source_offer_id)) return false;
    }
    // 🆕 Feb 2026 — Filtro multi-veicolo (all | multi | flotta | storica)
    if (multiVehFilter !== "all") {
      const n = Array.isArray(p.vehicles) ? p.vehicles.length : 0;
      if (multiVehFilter === "multi" && n < 2) return false;
      if (multiVehFilter === "flotta" && p.multi_vehicle_kind !== "flotta") return false;
      if (multiVehFilter === "storica" && p.multi_vehicle_kind !== "storica_cumulativa") return false;
    }
    // 🆕 Anomalie da Dashboard — allineate al backend /api/policies (esclude disdetta/rinnovata; il flag hidden_from_list è già escluso lato backend)
    if (noCompanyFilter) {
      if (["disdetta", "rinnovata"].includes(p.status)) return false;
      if (p.company_id || (p.company && String(p.company).trim())) return false;
    }
    if (noAttachmentsFilter) {
      if (["disdetta", "rinnovata"].includes(p.status)) return false;
      if ((p.attachments_count ?? 0) > 0) return false;
    }
    if (expiringWithinDays != null) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const end = p.end_date ? new Date(p.end_date) : null;
      if (!end) return false;
      end.setHours(0, 0, 0, 0);
      const days = Math.round((end - today) / (1000 * 60 * 60 * 24));
      if (days < 0 || days > expiringWithinDays) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      const haystack = [
        p.policy_number, p.client_name, p.company, p.vehicle_plate,
        p.vehicle_brand, p.vehicle_model, p.type, p.client_fiscal_code,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  // Conteggio polizze auto con revisione da gestire (mancante/scaduta/entro 30gg)
  const revisioneCount = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return items.filter((p) => {
      if (p.type !== "auto") return false;
      const rev = p.next_review_date ? new Date(p.next_review_date) : null;
      if (rev) rev.setHours(0, 0, 0, 0);
      const days = rev ? Math.round((rev - today) / (1000 * 60 * 60 * 24)) : null;
      return !rev || days < 0 || days <= 30;
    }).length;
  }, [items]);

  // Conteggio polizze senza garanzie (audit rapido del portafoglio)
  const noCoveragesCount = useMemo(() => {
    return items.filter((p) => !Array.isArray(p.coverages) || p.coverages.length === 0).length;
  }, [items]);

  // 🆕 Conteggio polizze "da completare": create automaticamente da offerta pagata, ancora in_emissione
  const toCompleteCount = useMemo(() => {
    return items.filter((p) => p.status === "in_emissione" && p.source_offer_id).length;
  }, [items]);

  // ---------- Sorting (click sugli header colonna) ----------
  const [sortKey, setSortKey] = useState("end_date");
  const [sortDir, setSortDir] = useState("asc");
  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sorted = useMemo(() => {
    const accessor = (p) => {
      switch (sortKey) {
        case "client_name": return (p.client_name || "").toLowerCase();
        case "vehicle_plate": return (p.vehicle_plate || "").toUpperCase();
        case "type_vehicle":
          return `${p.type || ""} ${p.vehicle_brand || ""} ${p.vehicle_model || ""}`.toLowerCase();
        case "client_fiscal_code": return (p.client_fiscal_code || "").toUpperCase();
        case "company": return (p.company || "").toLowerCase();
        case "broker":
          return (brokers.find((b) => b.id === p.broker_id)?.name || "").toLowerCase();
        case "end_date":
          return p.end_date ? new Date(p.end_date).getTime() : Number.POSITIVE_INFINITY;
        case "created_at":
          return p.created_at ? new Date(p.created_at).getTime() : 0;
        case "status": return (p.status || "").toLowerCase();
        default: return "";
      }
    };
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir, brokers]);

  const selectedClient = useMemo(() => clients.find((c) => c.id === form.client_id), [clients, form.client_id]);

  // 🆕 Feb 2026 — Set di client_id che sono condivisi in almeno un'altra area privata
  // (usato per mostrare il badge 🔗 accanto al nome cliente nella lista polizze)
  const sharedClientIds = useMemo(() => {
    const s = new Set();
    (clients || []).forEach((c) => {
      (c.linked_clients_visible_in_my_portal || []).forEach((linkedId) => s.add(linkedId));
    });
    return s;
  }, [clients]);

  // 🆕 Feb 2026 — Apertura dialog "Collega area privata" (retroattivo, da dentro la polizza)
  const openPortalLinkDialog = async () => {
    if (!selectedClient?.id) return;
    setPortalLinkDialog({
      sourceClientId: selectedClient.id,
      sourceClientLabel: `${selectedClient.surname || ""} ${selectedClient.name || ""}`.trim(),
      search: "",
    });
    try {
      const { data } = await api.get(`/clients/${selectedClient.id}/portal-link-owners`);
      setPortalLinkExisting(data?.owners || []);
    } catch { setPortalLinkExisting([]); }
  };

  const addPortalLinkFromPolicy = async (ownerClient) => {
    if (!portalLinkDialog?.sourceClientId || !ownerClient?.id) return;
    if (ownerClient.id === portalLinkDialog.sourceClientId) {
      toast.error("Non puoi collegare un cliente a sé stesso");
      return;
    }
    try {
      await api.post(`/clients/${ownerClient.id}/portal-links`, { client_id: portalLinkDialog.sourceClientId });
      setPortalLinkExisting([...portalLinkExisting, ownerClient]);
      setPortalLinkDialog({ ...portalLinkDialog, search: "" });
      toast.success(`Polizze di ${portalLinkDialog.sourceClientLabel} ora visibili anche nell'area privata di ${ownerClient.surname} ${ownerClient.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removePortalLinkFromPolicy = async (ownerId) => {
    if (!portalLinkDialog?.sourceClientId) return;
    try {
      await api.delete(`/clients/${ownerId}/portal-links/${portalLinkDialog.sourceClientId}`);
      setPortalLinkExisting(portalLinkExisting.filter((o) => o.id !== ownerId));
      toast.success("Collegamento rimosso");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Auto-fill commission % from selected company
  const onSelectCompany = (cid) => {
    const co = companies.find((c) => c.id === cid);
    // 🆕 Popola Provvigione % dal default della compagnia (con override per ramo se configurato).
    // Se poi c'è anche un broker collegato, il useEffect `/commissions/compute` sovrascriverà con
    // il tasso specifico del link Partner↔Compagnia.
    let nextPct = form.commission_percent;
    if (co) {
      const branchRates = Array.isArray(co.default_branch_rates) ? co.default_branch_rates : [];
      const ramoRate = branchRates.find((r) => r && r.ramo === form.type);
      const pctFromCompany = ramoRate?.commission_percent ?? co.default_commission_percent;
      if (pctFromCompany != null && !Number.isNaN(Number(pctFromCompany))) {
        nextPct = String(pctFromCompany);
      }
    } else {
      // Nessuna compagnia → azzera
      nextPct = "";
    }
    setForm({
      ...form,
      company_id: cid,
      company: co?.name || form.company,
      commission_percent: nextPct,
      commission_amount: "", // rimosso: verrà ricalcolato da computedCommission
    });
  };

  const computedCommission = useMemo(() => {
    const pct = parseFloat(form.commission_percent);
    // Base = imponibile (netto) se presente, altrimenti fallback al premio lordo
    const base = parseFloat(form.taxable_base);
    const baseUsed = !isNaN(base) && base > 0 ? base : parseFloat(form.premium);
    if (!isNaN(pct) && !isNaN(baseUsed)) return (baseUsed * pct) / 100;
    return 0;
  }, [form.commission_percent, form.taxable_base, form.premium]);

  // 🆕 16/07/2026 — Auto-sync commission_amount ← Imponibile × Provvigione %
  // Fix: dopo modifica delle garanzie (che aggiornano taxable_base) il commission_amount
  // restava stale. Ora si sincronizza automaticamente ad ogni variazione dei componenti,
  // ma solo se la commission_percent è > 0 (per non azzerare override manuali su polizze
  // dove la % non è nota).
  useEffect(() => {
    const pct = parseFloat(form.commission_percent);
    if (!Number.isFinite(pct) || pct <= 0) return;
    const rounded = (Math.round((computedCommission) * 100) / 100).toFixed(2);
    setForm((f) => {
      const current = String(f.commission_amount ?? "");
      if (current === rounded) return f;
      return { ...f, commission_amount: rounded };
    });
  }, [computedCommission, form.commission_percent]);

  // 🆕 Se la Compagnia viene svuotata, azzera anche Provvigione % e importo derivato
  useEffect(() => {
    if (!form.company_id) {
      if (form.commission_percent !== "" || form.commission_amount !== "") {
        setForm((f) => ({ ...f, commission_percent: "", commission_amount: "" }));
      }
    }
  }, [form.company_id, form.commission_percent, form.commission_amount]);

  const computeAutoEndDate = (f) => {
    if (!f.start_date) return null;
    let months;
    if (f.contract_duration_years && Number(f.contract_duration_years) > 0) months = Math.round(Number(f.contract_duration_years) * 12);
    else if (f.frazionamento) months = FRAZIONAMENTI.find((x) => x.v === f.frazionamento)?.months || 12;
    else return null;
    return addMonths(f.start_date, months);
  };
  const [endRecalc, setEndRecalc] = useState(null);
  useEffect(() => { setEndRecalc(null); }, [editing?.id]);
  const recalcEndDate = () => {
    const ed = computeAutoEndDate(form);
    if (!ed) { toast.error("Inserisci decorrenza e durata (o frazionamento) per ricalcolare"); return; }
    if (!form.end_date) { setForm((f) => ({ ...f, end_date: ed })); toast.success(`Scadenza calcolata: ${formatDate(ed)}`); return; }
    if (form.end_date === ed) { toast.info("La scadenza è già corretta rispetto a decorrenza e durata"); return; }
    setEndRecalc(ed);
  };

  // Auto-compute end_date (decorrenza + durata) SOLO se la scadenza è ancora vuota:
  // una scadenza già presente (manuale o salvata) non viene mai sovrascritta.
  useEffect(() => {
    if (!form.start_date || form.end_date) return;
    if (editing?.link_type === "replacement") return;
    // 🆕 Feb 2026 — Le polizze RIATTIVATE (clone) hanno end_date calcolata dal backend
    // preservando la durata residua della polizza originale (non start+1anno).
    // Non ricalcolare, altrimenti sovrascriveremmo la scadenza corretta.
    if (editing?.reactivated_from_policy_id) return;
    let months;
    if (form.contract_duration_years && Number(form.contract_duration_years) > 0) {
      months = Math.round(Number(form.contract_duration_years) * 12);
    } else if (form.frazionamento) {
      months = FRAZIONAMENTI.find((f) => f.v === form.frazionamento)?.months || 12;
    } else {
      return;
    }
    const ed = addMonths(form.start_date, months);
    setForm((f) => (f.end_date ? f : { ...f, end_date: ed }));
  }, [form.start_date, form.frazionamento, form.contract_duration_years, editing?.link_type, editing?.reactivated_from_policy_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // === Fase 2: Auto-calcolo provvigione + extra fees da partner_company_links ===
  const [linkInfo, setLinkInfo] = useState(null); // { found, base, commission_percent, commission_amount, extra_fees_breakdown, extra_fees_total }
  const [taxInfo, setTaxInfo] = useState(null); // { imposte_percent, imposte_amount, ssn_percent, ssn_amount, gross }
  // 🆕 Traccia se l'utente ha modificato manualmente Extra Fees:
  //     se true, l'auto-fetch dal link partner↔compagnia NON sovrascrive il valore digitato.
  //     Viene resettato ad ogni openEdit (nuova polizza aperta).
  const extraFeesManualRef = useRef(false);

  // Auto-calcolo bidirezionale Imponibile ↔ Totale Lordo + Imposte + SSN
  // ⚠️ Trigger SOLO su edit esplicito da parte dell'utente (via _edit_source): all'apertura
  //     della modifica polizza NON deve ricalcolare, per non sovrascrivere valori manuali già salvati.
  useEffect(() => {
    const ramo = form.type;
    const src = form._edit_source;
    const baseVal = parseFloat(form.taxable_base);
    const gross = parseFloat(form.premium);
    if (!ramo) { setTaxInfo(null); return; }
    // 🆕 Se l'utente non ha ancora modificato nulla in questa sessione di edit, non ricalcolare.
    if (!src) return;
    let cancelled = false;
    const tid = setTimeout(async () => {
      try {
        let payload = null;
        if (src === "premium" && gross > 0) {
          // Inverse: dato il lordo ricava imponibile via backend (gestisce anche il mapping moto→auto, ecc.)
          payload = { ramo, gross };
        } else if (src === "taxable_base" && baseVal > 0) {
          // Forward: dato l'imponibile calcola imposte/SSN/lordo
          payload = { ramo, taxable_base: baseVal };
        }
        if (!payload) return;
        const { data } = await api.post("/tax-rates/compute", payload);
        if (cancelled) return;
        setTaxInfo(data);
        // Se l'aliquota non è configurata, il backend restituisce null su tutti i campi:
        // NON sovrascriviamo i valori manuali eventualmente già presenti.
        setForm((f) => ({
          ...f,
          taxable_base: data.taxable_base != null ? String(data.taxable_base) : f.taxable_base,
          imposte_amount: data.imposte_amount != null ? String(data.imposte_amount) : f.imposte_amount,
          ssn_amount: data.ssn_amount != null ? String(data.ssn_amount) : f.ssn_amount,
          premium: data.gross != null ? data.gross : f.premium,
          _edit_source: null,
        }));
      } catch { /* silent */ }
    }, 350);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [form.type, form.taxable_base, form.premium, form._edit_source]);
  useEffect(() => {
    const broker = form.broker_id ? brokers.find((b) => b.id === form.broker_id) : null;
    const cid = form.company_id;
    const ramo = form.type;
    const base = parseFloat(form.taxable_base) || parseFloat(form.premium) || 0;
    if (!broker || !cid || !ramo || base <= 0) { setLinkInfo(null); return; }
    let cancelled = false;
    const tid = setTimeout(async () => {
      try {
        const partner_type = broker.type === "agenzia" ? "agenzia" : "broker";
        const { data } = await api.post("/commissions/compute", {
          partner_type,
          partner_id: broker.id,
          company_id: cid,
          ramo,
          premium: parseFloat(form.premium) || 0,
          taxable_base: parseFloat(form.taxable_base) || null,
        });
        if (cancelled) return;
        if (data && data.found) {
          setLinkInfo(data);
          setForm((f) => ({
            ...f,
            commission_percent: data.commission_percent != null ? String(data.commission_percent) : f.commission_percent,
            commission_amount: data.commission_amount != null ? String(data.commission_amount) : f.commission_amount,
            // 🆕 Sovrascrivi extra_fees_total dal link SOLO se l'utente non l'ha modificato manualmente
            extra_fees_total: extraFeesManualRef.current
              ? f.extra_fees_total
              : (data.extra_fees_total ?? 0),
            extra_fees_breakdown: data.extra_fees_breakdown || [],
          }));
        } else {
          setLinkInfo(null);
        }
      } catch { /* silent: compute non-critico */ }
    }, 350);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [form.broker_id, form.company_id, form.type, form.premium, form.taxable_base, brokers]);

  const submit = async ({ skipMissingPaidDatesCheck = false } = {}) => {
    try {
      // 🆕 Validazione formato targa
      if ((form.vehicle_plate || "").trim() && !form.vehicle_plate_override) {
        const _pv = validatePlate(form.vehicle_plate);
        if (!_pv.ok) {
          toast.error(`Formato targa non valido: ${_pv.message}. Spunta "Formato speciale" per forzare il salvataggio.`);
          return;
        }
      }
      // 🆕 10/07/2026 — Validazione rate con importo ma senza "Data pagam. effettivo"
      // (utile a evitare polizze salvate con incassi silenziosi non tracciati per data)
      // NB: le rate possono avere importo esplicito (installment_amounts[d]) OPPURE ereditato
      // dal fallback `defaultPerInstallment` = premium / N_rate. Il controllo copre entrambi.
      // 🆕 Feb 2026 — ESCLUSIONE percorso di RIATTIVAZIONE: le polizze clone hanno rate
      // ereditate dalla polizza originale (già pagate) più nuove rate future non ancora
      // incassate → la validazione qui bloccherebbe erroneamente il salvataggio.
      const _isReactivatedPolicy = !!(editing?.reactivated_from_policy_id || form?.reactivated_from_policy_id);
      if (!skipMissingPaidDatesCheck && !_isReactivatedPolicy) {
        const paidDates = form.installment_paid_dates || {};
        const amounts = form.installment_amounts || {};
        // Ricostruisci le date rata come nella UI
        const FRAZ_MONTHS = { annuale: 12, semestrale: 6, quadrimestrale: 4, trimestrale: 3, mensile: 1 };
        const monthsPerInst = FRAZ_MONTHS[form.frazionamento] || 12;
        const computeInstallmentDates = () => {
          if (!form.start_date) return [];
          const start = new Date(form.start_date);
          let end;
          if (form.end_date) end = new Date(form.end_date);
          else { end = new Date(start); end.setUTCFullYear(end.getUTCFullYear() + 1); }
          if (isNaN(start) || isNaN(end) || end <= start) return [];
          const out = []; const cur = new Date(start);
          for (let i = 0; i < 120; i++) {
            if (cur >= end) break;
            out.push(cur.toISOString().slice(0, 10));
            cur.setUTCMonth(cur.getUTCMonth() + monthsPerInst);
          }
          return out;
        };
        const instDates = computeInstallmentDates();
        const totalPremium = Number(form.premium) || 0;
        // 🆕 15/07/2026 (aggiornato) — Include Σ Vendita + Σ Fee Broker (ripart.) + Σ Spese Gest. Broker + Σ Extra Fees.
        const _excludeSell = (() => { try { return localStorage.getItem("policy_guarantees_exclude_sell_from_total") === "1"; } catch { return false; } })();
        const _covs = form.coverages || [];
        const _sumSellCost = _excludeSell ? 0 : _covs.reduce((s, c) => s + (parseFloat(c.sell_cost) || 0), 0);
        const _sumFeeBrokerSplit = _covs.reduce((s, c) => s + (parseFloat(c.broker_fee_to_split) || 0), 0);
        const _sumBrokerMgmt = _covs.reduce((s, c) => s + (parseFloat(c.broker_management_cost) || 0), 0);
        const _sumExtraFees = _covs.reduce((s, c) => s + (parseFloat(c.extra_fees) || 0), 0);
        const _totalGross = totalPremium + _sumSellCost + _sumFeeBrokerSplit + _sumBrokerMgmt + _sumExtraFees;
        const defaultPerInst = instDates.length > 0 ? Math.round((_totalGross / instDates.length) * 100) / 100 : 0;
        const missing = instDates.filter((d) => {
          const raw = amounts[d];
          const nCustom = raw != null && raw !== "" ? (typeof raw === "number" ? raw : parseFloat(raw)) : NaN;
          const effectiveAmt = Number.isFinite(nCustom) ? nCustom : defaultPerInst;
          return effectiveAmt > 0 && !paidDates[d];
        });
        if (missing.length > 0) {
          setMissingPaidDatesPrompt({ count: missing.length, firstDate: missing[0] });
          return;
        }
      }
      const toNullableNum = (v) => {
        if (v === "" || v == null) return null;
        if (typeof v === "number") return Number.isFinite(v) ? v : null;
        // 🆕 Supporta formato italiano "1.234,56" e formato dot "1234.56"
        const s = String(v).trim();
        if (!s) return null;
        // Se contiene la virgola come decimale, rimuovi i punti (migliaia) e sostituisci virgola con punto
        let normalized;
        if (s.includes(",")) {
          normalized = s.replace(/\./g, "").replace(",", ".");
        } else {
          normalized = s;
        }
        const n = Number(normalized);
        return Number.isFinite(n) ? n : null;
      };
      // Sanitizza le coperture: premium/sum_insured vuoti diventano null (Pydantic non accetta "")
      const cleanCoverages = (form.coverages || []).map((c) => ({
        ...c,
        // 🆕 09/07/2026 — Assicura che `code` sia sempre una stringa (mai null/undefined)
        code: c.code == null ? "" : String(c.code),
        name: c.name == null ? null : String(c.name),
        premium: toNullableNum(c.premium),
        sum_insured: toNullableNum(c.sum_insured),
        deductible: c.deductible === "" ? null : c.deductible,
        // 🆕 Feb 2026 — Prodotto collegato + snapshot costi (visibili solo a ruoli autorizzati)
        product_id: c.product_id || null,
        buy_cost: canSeeMargins ? toNullableNum(c.buy_cost) : null,
        sell_cost: canSeeMargins ? toNullableNum(c.sell_cost) : null,
      }));
      // ⚠️ Validazione garanzie: la polizza deve avere almeno 1 copertura
      if (cleanCoverages.length === 0) {
        toast.warning("⚠️ Nessuna garanzia inserita in polizza", { duration: 4000 });
        const ok = window.confirm(
          "Attenzione: questa polizza non ha alcuna garanzia/copertura inserita.\n\n" +
          "Si consiglia di aggiungere almeno una garanzia (RCA, Furto, Incendio, ecc.) " +
          "per documentare correttamente le coperture attive.\n\n" +
          "Vuoi salvare comunque?"
        );
        if (!ok) return;
      }
      const payload = {
        ...form,
        premium: parseFloat(form.premium) || 0,
        taxable_base: toNullableNum(form.taxable_base),
        imposte_amount: toNullableNum(form.imposte_amount),
        ssn_amount: toNullableNum(form.ssn_amount),
        commission_percent: toNullableNum(form.commission_percent),
        commission_amount: toNullableNum(form.commission_amount),
        commission_cost: toNullableNum(form.commission_cost),
        // 🆕 Feb 2026 — Costo polizza + Costo fisso broker/agenzia
        policy_cost: toNullableNum(form.policy_cost),
        broker_fixed_fee: toNullableNum(form.broker_fixed_fee),
        broker_management_fee: toNullableNum(form.broker_management_fee),
        product_id: form.product_id || null,
        product_buy_cost: canSeeMargins ? toNullableNum(form.product_buy_cost) : null,
        product_sell_cost: canSeeMargins ? toNullableNum(form.product_sell_cost) : null,
        extra_fees_total: toNullableNum(form.extra_fees_total),
        extra_fees_breakdown: form.extra_fees_breakdown || null,
        include_extra_in_statement: form.include_extra_in_statement === true,
        include_broker_fixed_fee_in_statement: form.include_broker_fixed_fee_in_statement !== false,
        exclude_extra_fees_from_statement: form.exclude_extra_fees_from_statement === true,
        exclude_broker_fixed_from_statement: form.exclude_broker_fixed_from_statement === true,
        vehicle_fiscal_power: toNullableNum(form.vehicle_fiscal_power),
        vehicle_kw: toNullableNum(form.vehicle_kw),
        vehicle_displacement: toNullableNum(form.vehicle_displacement),
        signing_amount: toNullableNum(form.signing_amount),
        bollo_amount: toNullableNum(form.bollo_amount),
        coverages: cleanCoverages,
        // Pagamento polizza
        is_paid: !!form.is_paid,
        paid_amount: toNullableNum(form.paid_amount),
        paid_commission: toNullableNum(form.paid_commission),
        paid_date: form.paid_date || null,
        paid_method: form.paid_method || null,
      };
      // 🆕 Feb 2026 — Payload multi-veicolo (primo veicolo dai top-level + extras)
      const _vehicles_payload = buildVehiclesPayload(form);
      if (_vehicles_payload !== undefined) payload.vehicles = _vehicles_payload;
      // Classificazione polizza multi-veicolo (valida solo con 2+ veicoli, altrimenti azzerata)
      payload.multi_vehicle_kind = (_vehicles_payload && _vehicles_payload.length >= 2 && ["storica_cumulativa", "flotta"].includes(form.multi_vehicle_kind))
        ? form.multi_vehicle_kind
        : null;
      // 🆕 30/07/2026 — Payload multi-motore natante
      const _boat_engines_payload = buildBoatEnginesPayload(form);
      if (_boat_engines_payload !== undefined) payload.boat_engines = _boat_engines_payload;
      // Rimuovi campo UI-only prima dell'invio
      delete payload.boat_engines_extra;
      let savedPolicyId = editing?.id;
      if (editing) await api.put(`/policies/${editing.id}`, payload);
      else {
        const { data } = await api.post("/policies", payload);
        savedPolicyId = data?.id;
      }
      // Upload allegati pendenti (drag&drop nel dialog)
      if (savedPolicyId && pendingFiles.length > 0) {
        // 🆕 Feb 2026 — Upload sequenziale per preservare la categoria tag (_category) per ogni file
        let ok = 0;
        for (const f of pendingFiles) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            const cat = f._category || "";
            const qs = new URLSearchParams({ description: "", category: cat });
            await api.post(`/policies/${savedPolicyId}/attachments?${qs.toString()}`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            ok++;
          } catch (uplErr) {
            toast.error(`Upload "${f.name}" fallito: ${formatApiError(uplErr)}`);
          }
        }
        if (ok > 0) {
          toast.success(`${ok} allegat${ok === 1 ? "o caricato" : "i caricati"}`);
        }
      }
      if (editing) {
        toast.success("Polizza aggiornata");
        // 🆕 Se la polizza è passata da "in_emissione" a uno stato finale (es. "attiva"),
        //    apri il dialog anteprima per inviare al cliente l'email "Polizza pronta".
        const wasInEmissione = editing.status === "in_emissione";
        const isNowFinalized = form.status && form.status !== "in_emissione";
        if (wasInEmissione && isNowFinalized && savedPolicyId) {
          setReadyNoticePolicy({
            id: savedPolicyId,
            policy_number: form.policy_number,
            client_name: (clients.find((c) => c.id === form.client_id) || {}).name
              ? `${(clients.find((c) => c.id === form.client_id) || {}).name} ${(clients.find((c) => c.id === form.client_id) || {}).surname || ""}`.trim()
              : (editing.client_name || ""),
            client_email: (clients.find((c) => c.id === form.client_id) || {}).email || editing.client_email,
          });
        }
      } else {
        // Polizza appena creata → proponi follow-up in agenda
        toast.success("Polizza creata", {
          action: {
            label: "📅 Follow-up 7gg",
            onClick: async () => {
              try {
                await api.post("/agenda/quick-followup", {
                  client_id: form.client_id, policy_id: savedPolicyId,
                  days: 7, reminder_minutes_before: 60,
                });
                toast.success("Follow-up creato in agenda");
              } catch { toast.error("Errore creazione follow-up"); }
            },
          },
          duration: 8000,
        });
      }
      setPendingFiles([]);
      const shouldReturn = !!policyReturnTo;
      if (shouldReturn) {
        closePolicyDialog();
      } else {
        setOpen(false); setForm(empty); setEditing(null);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = (p) => {
    setDeleteTarget({ id: p.id, label: `${p.policy_number || "(senza numero)"} · ${p.client_name || ""}`, otp: "", otp_sent: false, requesting: false, sent_to: null });
  };

  // 🆕 Emetti ricevuta dalla polizza: pre-compila draft dal backend e reindirizza alla pagina ricevute.
  // Il backend allega automaticamente il PDF ricevuta agli allegati della polizza al salvataggio.
  // Al termine del save, l'utente viene riportato sul dialog Modifica polizza (?focus=<id>).
  const emettiRicevutaPolizza = async (p) => {
    try {
      const { data } = await api.post(`/policies/${p.id}/receipt-draft`);
      // Salva il draft in sessionStorage + marker per return-to-policy
      sessionStorage.setItem("receipt_draft", JSON.stringify({
        ...data,
        _return_to_policy_id: p.id, // 🆕 marker per ritorno automatico dopo save
      }));
      navigate("/agency/receipts?draft=1");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const requestDeleteOtp = async () => {
    if (!deleteTarget) return;
    setDeleteTarget((prev) => ({ ...prev, requesting: true }));
    try {
      const { data } = await api.post(`/policies/${deleteTarget.id}/delete/request-otp`);
      setDeleteTarget((prev) => ({ ...prev, otp_sent: true, sent_to: data.sent_to || null, requesting: false }));
      toast.success(`OTP inviato a ${data.sent_to || "email"}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleteTarget((prev) => ({ ...prev, requesting: false }));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (!deleteTarget.otp || deleteTarget.otp.length !== 6) {
      toast.error("Inserisci il codice OTP a 6 cifre");
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/policies/${deleteTarget.id}`, {
        headers: { "X-Otp-Code": deleteTarget.otp.trim() },
      });
      toast.success("Polizza eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    // 🆕 16/07/2026 — Reset carosello quando cambia polizza
    setChainCurrentIdx(0);
    // Se la polizza fa parte di una catena (renewed_from_policy_id, renewed_to_policy_id,
    // reactivated_from_policy_id, next_policy_id o reactivated_to_policy_id), carica la chain.
    // 🆕 Feb 2026 — Anche le riattivazioni fanno parte della catena (originale sostituita_riattivazione + clone attivo).
    if (
      p?.id && (
        p.renewed_from_policy_id ||
        p.renewed_to_policy_id ||
        p.reactivated_from_policy_id ||
        p.next_policy_id ||
        p.reactivated_to_policy_id
      )
    ) {
      api.get(`/policies/${p.id}/chain`).then((r) => {
        const chain = r.data?.chain || [];
        setPolicyChain(chain);
        // Cerca la posizione della polizza corrente nella chain (0 = più recente)
        const idx = chain.findIndex((x) => x.id === p.id);
        setChainCurrentIdx(idx >= 0 ? idx : 0);
      }).catch(() => setPolicyChain([]));
    } else {
      setPolicyChain([]);
    }
    // 🆕 Se la polizza già salvata ha un extra_fees_total esplicito (non null),
    //     considera il campo come "modificato manualmente" così l'auto-fetch dal link
    //     NON sovrascrive il valore appena caricato dal DB.
    extraFeesManualRef.current = p?.extra_fees_total != null && p.extra_fees_total !== "";
    // 🆕 Se la polizza esistente ha una targa con formato non standard, abilita automaticamente
    // il flag "Formato speciale" così l'utente può salvare modifiche senza dover ri-spuntare la checkbox.
    const _plate = (p.vehicle_plate || "").trim();
    const _needsOverride = _plate && !validatePlate(_plate).ok && !p.vehicle_plate_override;
    setForm({
      ...empty,
      ...p,
      // 🆕 Default orari copertura = 24:00 se non impostati
      start_time: p.start_time || "24:00",
      end_time: p.end_time || "24:00",
      commission_percent: p.commission_percent ?? "",
      commission_amount: p.commission_amount ?? "",
      commission_cost: p.commission_cost ?? "",
      policy_cost: p.policy_cost ?? "",
      broker_fixed_fee: p.broker_fixed_fee ?? "",
      broker_management_fee: p.broker_management_fee ?? "",
      product_id: p.product_id || "",
      product_buy_cost: p.product_buy_cost ?? "",
      product_sell_cost: p.product_sell_cost ?? "",
      vehicle_plate_override: _needsOverride ? true : (p.vehicle_plate_override || false),
      // 🆕 Feb 2026 — Multi-veicolo: estrai i veicoli AGGIUNTIVI (dal 2° in poi) da vehicles[]
      vehicles_extra: Array.isArray(p.vehicles) && p.vehicles.length > 1 ? p.vehicles.slice(1) : [],
      multi_vehicle_kind: p.multi_vehicle_kind || null,
      // 🆕 30/07/2026 — Multi-motore natante: estrai i motori aggiuntivi (dal 2° in poi)
      boat_engines_extra: Array.isArray(p.boat_engines) && p.boat_engines.length > 1 ? p.boat_engines.slice(1) : [],
    });
    setOpen(true);
    if (p?.id) loadEditAttachments(p.id);
  };

  const [polDragOver, setPolDragOver] = useState(false);
  const [polExtractBusy, setPolExtractBusy] = useState(false);

  // Estrazione AI completa: imposta TUTTI i campi disponibili in base al tipo di polizza estratto.
  // - Per polizze veicolo: targa, marca, modello, anno, prima immatricolazione
  // - Per qualsiasi tipo: numero, compagnia, premio, date, frazionamento, garanzie/coperture
  const applyPolicyExtraction = async (data, fileName) => {
    const ex = data?.extracted || {};
    const numKeys = ["premium", "commission_percent", "commission_amount"];
    const toNum = (v) => {
      if (v == null) return v;
      if (typeof v === "number") return v;
      const n = Number(String(v).replace(/[^\d.,-]/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", "."));
      return isNaN(n) ? null : n;
    };
    // 🆕 Feb 2026 — Replica logica PolicyWizard.jsx (righe 380-450):
    // auto-match compagnia + fetch garanzie master → ereditate imposte_percent/ssn_percent/company_guarantee_id.
    const normName = (s) => (s || "").toString()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    let matchedCompany = null;
    if (ex.company && Array.isArray(companies) && companies.length) {
      matchedCompany = companies.find((c) => (c.name || "").toLowerCase().trim() === String(ex.company).toLowerCase().trim())
        || companies.find((c) => normName(c.name) === normName(ex.company));
    }
    let masterGuarantees = [];
    if (matchedCompany?.id) {
      try {
        const { data: mg } = await api.get(`/companies/${matchedCompany.id}/guarantees`);
        masterGuarantees = Array.isArray(mg) ? mg : [];
      } catch { /* la master può non essere configurata: procediamo senza */ }
    }
    const findMaster = (extractedName) => resolveGuarantee(extractedName, masterGuarantees);
    // Fallback aggregato polizza (se PDF fornisce solo totali)
    const guarantees = Array.isArray(ex.guarantees) ? ex.guarantees.filter((g) => g && (g.name || g.code)) : [];
    const _polNet = toNum(ex.net_premium) || toNum(ex.taxable_base);
    const _polTaxes = toNum(ex.taxes_amount) || toNum(ex.imposte_amount);
    const _polSsn = toNum(ex.ssn_amount);
    const _polImpostePct = _polTaxes != null && _polNet != null && _polNet > 0
      ? Math.round((_polTaxes / _polNet) * 100 * 100) / 100 : null;
    const _polSsnPct = _polSsn != null && _polNet != null && _polNet > 0
      ? Math.round((_polSsn / _polNet) * 100 * 100) / 100 : null;
    // Mappa garanzia estratta → coverage: priorità PDF → master → aggregato → formula inversa
    const extractedCoverages = guarantees.map((g) => {
      const master = findMaster(g.name || g.code);
      const premium = toNum(g.premium);
      const taxesAmount = toNum(g.taxes_amount);
      const ssnAmount = toNum(g.ssn_amount);
      // Priorità imposte/ssn %: (1) PDF diretto → (2) master compagnia → (3) calcolo da importi € → (4) aggregato polizza
      let impostePct = toNum(g.imposte_percent);
      let ssnPct = toNum(g.ssn_percent);
      if (impostePct == null && master?.imposte_percent != null) impostePct = Number(master.imposte_percent);
      if (ssnPct == null && master?.ssn_percent != null) ssnPct = Number(master.ssn_percent);
      let netPremium = toNum(g.net_premium);
      if (netPremium == null && premium != null && (taxesAmount != null || ssnAmount != null)) {
        netPremium = Math.round((premium - (taxesAmount || 0) - (ssnAmount || 0)) * 100) / 100;
      }
      if (impostePct == null && taxesAmount != null && netPremium != null && netPremium > 0) {
        impostePct = Math.round((taxesAmount / netPremium) * 100 * 100) / 100;
      }
      if (ssnPct == null && ssnAmount != null && netPremium != null && netPremium > 0) {
        ssnPct = Math.round((ssnAmount / netPremium) * 100 * 100) / 100;
      }
      if (impostePct == null && _polImpostePct != null) impostePct = _polImpostePct;
      if (ssnPct == null && _polSsnPct != null) ssnPct = _polSsnPct;
      if (netPremium == null && premium != null && premium > 0 && (impostePct != null || ssnPct != null)) {
        const totPct = (impostePct || 0) + (ssnPct || 0);
        netPremium = totPct > 0 ? Math.round((premium / (1 + totPct / 100)) * 100) / 100 : premium;
      }
      return {
        code: buildGuaranteeCode(g.code || (master?.name || g.name || "")),
        name: master?.name || g.name || g.code || "Garanzia",
        extracted_name: master ? (g.name || g.code || "") : null,  // 🆕 Feb 2026 — etichetta originale se rinominata
        _unmatched: !master,                                        // 🆕 flag per UI
        active: true,
        premium,
        sum_insured: toNum(g.sum_insured),
        deductible: g.deductible == null ? null : String(g.deductible),
        company_guarantee_id: master?.id || null,
        imposte_percent: impostePct,
        ssn_percent: ssnPct,
        net_premium: netPremium,
        taxes_amount: taxesAmount,
        ssn_amount: ssnAmount,
        _matchedName: normName(g.name || g.code),
      };
    });
    setForm((prev) => {
      const next = { ...prev };
      // Campi comuni
      next.policy_number = ex.policy_number || prev.policy_number;
      next.company = ex.company || prev.company;
      next.start_date = ex.start_date || prev.start_date;
      next.end_date = ex.end_date || prev.end_date;
      next.frazionamento = ex.frazionamento || prev.frazionamento;
      next.type = ex.type || prev.type;
      // Campi numerici (premio lordo + breakdown imposte)
      numKeys.forEach((k) => { if (ex[k] != null) next[k] = ex[k]; });
      // 🆕 Premio netto / imposte / SSN — popolati dall'AI quando rilevati nel PDF
      const _grossExtracted = toNum(ex.premium);
      const _netExtracted = toNum(ex.net_premium);
      const _taxesExtracted = toNum(ex.taxes_amount);
      const _ssnExtracted = toNum(ex.ssn_amount);
      if (_netExtracted != null) next.taxable_base = _netExtracted;
      if (_taxesExtracted != null) next.imposte_amount = _taxesExtracted;
      if (_ssnExtracted != null) next.ssn_amount = _ssnExtracted;
      // Auto-derivazione: se manca uno dei 4 valori e ho gli altri 3, lo calcolo per coerenza
      const g = _grossExtracted != null ? _grossExtracted : toNum(prev.premium);
      const n = _netExtracted != null ? _netExtracted : toNum(prev.taxable_base);
      const t = _taxesExtracted != null ? _taxesExtracted : toNum(prev.imposte_amount);
      const s = _ssnExtracted != null ? _ssnExtracted : toNum(prev.ssn_amount);
      // gross = net + taxes + ssn (relazione fondamentale)
      if (g == null && n != null && t != null) next.premium = Math.round((n + t + (s || 0)) * 100) / 100;
      if (n == null && g != null && t != null) next.taxable_base = Math.round((g - t - (s || 0)) * 100) / 100;
      if (t == null && g != null && n != null) next.imposte_amount = Math.round((g - n - (s || 0)) * 100) / 100;
      // Veicolo (auto/moto/autocarro/agricolo/nautica)
      const vehicleType = (next.type || "").toLowerCase();
      const isVehicle = /(auto|moto|veicolo|autocarro|truck|agricolo|nautic|natant)/.test(vehicleType);
      if (isVehicle || ex.vehicle_plate) {
        next.vehicle_plate = (ex.vehicle_plate || prev.vehicle_plate || "").toUpperCase().replace(/\s+/g, "");
        next.vehicle_brand = ex.vehicle_brand || prev.vehicle_brand;
        next.vehicle_model = ex.vehicle_model || prev.vehicle_model;
        if (ex.vehicle_year) next.vehicle_year = ex.vehicle_year;
        if (ex.vehicle_first_registration) next.vehicle_first_registration = ex.vehicle_first_registration;
        if (ex.vehicle_purchase_date) next.vehicle_purchase_date = ex.vehicle_purchase_date;
        if (ex.vehicle_fuel) next.vehicle_fuel = ex.vehicle_fuel;
        if (ex.vehicle_body_type) next.vehicle_body_type = ex.vehicle_body_type;
        if (ex.chassis_number) next.chassis_number = ex.chassis_number;
      }
      // Casa / abitazione
      if (/(casa|abitazion|home)/.test(vehicleType)) {
        if (ex.property_address) next.property_address = ex.property_address;
        if (ex.property_type) next.property_type = ex.property_type;
        if (ex.property_sqm) next.property_sqm = ex.property_sqm;
      }
      // Coperture/garanzie: 🆕 Feb 2026 — Merge intelligente invece di sovrascrittura totale.
      // Per ogni copertura estratta, cerchiamo un match per nome (normalizzato) nelle coperture
      // esistenti (ereditate dal rinnovo/precedente polizza). Se l'estrazione NON fornisce
      // imposte_percent / ssn_percent / company_guarantee_id, preserviamo i valori esistenti e
      // ricalcoliamo net_premium. Se una copertura esistente non ha match nell'estrazione,
      // resta comunque nella polizza.
      if (extractedCoverages.length) {
        const prevCoverages = Array.isArray(prev.coverages) ? prev.coverages : [];
        const prevByNorm = new Map();
        prevCoverages.forEach((c) => {
          const key = normName(c.name || c.code);
          if (key && !prevByNorm.has(key)) prevByNorm.set(key, c);
        });
        const usedPrevKeys = new Set();
        const mergedFromExtracted = extractedCoverages.map((ec) => {
          const prevMatch = prevByNorm.get(ec._matchedName);
          if (prevMatch) usedPrevKeys.add(ec._matchedName);
          // Preservazione: se l'estrazione ha null, prendi dal prev
          const impostePct = ec.imposte_percent != null ? ec.imposte_percent
            : (prevMatch?.imposte_percent != null ? Number(prevMatch.imposte_percent) : null);
          const ssnPct = ec.ssn_percent != null ? ec.ssn_percent
            : (prevMatch?.ssn_percent != null ? Number(prevMatch.ssn_percent) : null);
          const companyGuaranteeId = ec.company_guarantee_id || prevMatch?.company_guarantee_id || null;
          // Ricalcolo del netto con le % effettive (potenzialmente ereditate)
          const premium = ec.premium != null ? ec.premium : (prevMatch?.premium != null ? Number(prevMatch.premium) : null);
          let netPremium = ec.net_premium;
          if ((netPremium == null || (impostePct !== ec.imposte_percent || ssnPct !== ec.ssn_percent))
              && premium != null && premium > 0 && (impostePct != null || ssnPct != null)) {
            const totPct = (impostePct || 0) + (ssnPct || 0);
            netPremium = totPct > 0 ? Math.round((premium / (1 + totPct / 100)) * 100) / 100 : premium;
          }
          if (netPremium == null && prevMatch?.net_premium != null) netPremium = Number(prevMatch.net_premium);
          const out = {
            ...ec,
            imposte_percent: impostePct,
            ssn_percent: ssnPct,
            company_guarantee_id: companyGuaranteeId,
            net_premium: netPremium,
            premium,
          };
          delete out._matchedName;
          return out;
        });
        // Aggiungiamo le coperture esistenti che non hanno match nell'estrazione (potrebbero essere garanzie
        // opzionali già scelte dall'utente che l'AI non è riuscita a rilevare)
        const unmatched = prevCoverages.filter((c) => {
          const key = normName(c.name || c.code);
          return key && !usedPrevKeys.has(key);
        });
        next.coverages = [...mergedFromExtracted, ...unmatched];
      }
      return next;
    });
    // Auto-match compagnia se presente nelle anagrafiche
    if (ex.company && Array.isArray(companies) && companies.length) {
      const found = companies.find((c) => (c.name || "").toLowerCase().trim() === String(ex.company).toLowerCase().trim());
      if (found) setForm((prev) => ({ ...prev, company_id: found.id }));
    }
    const filledCount = Object.keys(ex).filter((k) => ex[k] != null && ex[k] !== "").length;
    toast.success(
      `Estratti ${filledCount} campi da ${fileName}` +
      (extractedCoverages.length ? ` · ${extractedCoverages.length} garanzie` : "") +
      ((ex.vehicle_plate ? ` · targa ${ex.vehicle_plate}` : ""))
    );
    // Validazione intestatario vs cliente selezionato
    const extractedCF = ((ex.client_fiscal_code || ex.fiscal_code || ex.codice_fiscale) || "").toString().trim().toUpperCase();
    const clientId = form.client_id;
    if (extractedCF && clientId) {
      const cli = clients.find((c) => c.id === clientId);
      const clientCF = (cli?.fiscal_code || "").trim().toUpperCase();
      if (clientCF && clientCF !== extractedCF) {
        toast.warning(
          `⚠️ Intestatario polizza diverso dal cliente selezionato. PDF: ${extractedCF} · Cliente: ${clientCF}. Verifica prima di salvare.`,
          { duration: 12000 }
        );
      }
    }
    // 🆕 Se il cliente NON è ancora selezionato ma abbiamo i dati intestatario:
    //    prova a trovare un cliente esistente per CF, altrimenti CREA UN NUOVO CLIENTE con i dati estratti.
    if (!clientId && (extractedCF || (ex.client_surname && ex.client_name))) {
      try {
        // Step 1: cerca cliente esistente per CF (o nome+cognome+data)
        let matched = null;
        try {
          const params = {
            fiscal_code: extractedCF || undefined,
            surname: ex.client_surname || undefined,
            name: ex.client_name || undefined,
            birth_date: ex.client_birth_date || undefined,
          };
          const r = await api.get("/clients/find-duplicate", { params });
          if (r.data?.client?.id) matched = r.data.client;
        } catch (errFind) {
          if (errFind?.response?.status !== 404) throw errFind;
        }
        if (matched) {
          setForm((p) => ({ ...p, client_id: matched.id }));
          toast.success(`🔗 Cliente esistente trovato: ${matched.surname} ${matched.name}. Collegato alla nuova polizza.`);
        } else if (ex.client_surname && ex.client_name) {
          // Step 2: crea nuovo cliente con i dati estratti
          const clientPayload = {
            surname: (ex.client_surname || "").toUpperCase().trim(),
            name: (ex.client_name || "").toUpperCase().trim(),
            fiscal_code: extractedCF || null,
            birth_date: ex.client_birth_date || null,
            birth_place: ex.client_birth_place || null,
            gender: ex.client_gender === "F" ? "F" : (ex.client_gender === "M" ? "M" : null),
            address: ex.client_address || null,
            house_number: ex.client_house_number || null,
            city: ex.client_city || null,
            province: (ex.client_province || "").toUpperCase().slice(0, 2) || null,
            cap: ex.client_cap || null,
            email: ex.client_email || null,
            mobile: ex.client_mobile || null,
            client_kind: "client",
            source: "extracted_from_policy_pdf",
          };
          // Rimuovi chiavi null per non far esplodere validazioni
          Object.keys(clientPayload).forEach((k) => clientPayload[k] == null && delete clientPayload[k]);
          const cr = await api.post("/clients", clientPayload);
          const newCli = cr?.data;
          if (newCli?.id) {
            setForm((p) => ({ ...p, client_id: newCli.id }));
            // Aggiungi al riferimento locale così ClientCombobox lo mostra subito
            setClients((prev) => Array.isArray(prev) && prev.some((c) => c.id === newCli.id) ? prev : [newCli, ...(prev || [])]);
            toast.success(`✅ Cliente creato automaticamente dai dati polizza: ${newCli.surname} ${newCli.name}${newCli.fiscal_code ? ` (${newCli.fiscal_code})` : ""}. Verrà collegato al salvataggio.`, { duration: 8000 });
          }
        }
      } catch (errClient) {
        toast.warning(`Cliente non collegato automaticamente: ${formatApiError(errClient)}. Selezionalo manualmente dal menù Cliente.`);
      }
    }
  };

  const extractPolicyFile = async (file) => {
    if (!file) return;
    setPolExtractBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // 🆕 Feb 2026 — Barra di progresso durante l'estrazione AI (progress simulato 0→90%, 100% al termine)
      const { data } = await runExtractionWithProgress({
        label: `Estrazione polizza · ${file.name}`,
        run: () => api.post("/utils/extract-pdf?kind=polizza", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
        }),
      });
      await applyPolicyExtraction(data, file.name);
      // 🆕 Allega il file alla polizza dopo l'estrazione (così il PDF analizzato resta visibile in archivio)
      if (editing?.id) {
        try {
          const fd2 = new FormData();
          fd2.append("file", file);
          await api.post(`/policies/${editing.id}/attachments?description=${encodeURIComponent("Estratto AI")}`, fd2, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          loadEditAttachments(editing.id);
          if (attachOpen?.id === editing.id) loadAttachments(editing.id);
          toast.success(`📎 "${file.name}" allegato alla polizza`);
        } catch (errAttach) {
          toast.warning(`Dati estratti ma allegato fallito: ${formatApiError(errAttach)}`);
        }
      } else {
        // Nuova polizza: accumula nella coda così verrà caricato al salvataggio
        setPendingFiles((arr) => [...arr, file]);
        toast.success(`📎 "${file.name}" in coda · sarà allegato al salvataggio`);
      }
    } catch (err) {
      // Il toast d'errore è già stato mostrato da runExtractionWithProgress; log a scopo debug
      console.error("[extractPolicyFile]", err);
    }
    finally { setPolExtractBusy(false); }
  };

  const importPolicyPDF = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await extractPolicyFile(f);
    e.target.value = "";
  };

  // ---------- Bulk delete (admin-only) ----------
  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAllVisible = (rows, checked) => setSelectedIds((s) => {
    const n = new Set(s);
    rows.forEach((p) => { if (checked) n.add(p.id); else n.delete(p.id); });
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const requestBulkOtp = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/policies/bulk-delete/request-otp", {
        policy_ids: Array.from(selectedIds),
      });
      setBulkOtpInfo(data);
      setBulkStage("otp");
      setBulkOtp("");
      if (data.ok) toast.success(`Codice OTP inviato a ${data.email}`);
      else toast.error("Invio OTP fallito (controlla SMTP)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };
  const confirmBulkDelete = async () => {
    const code = (bulkOtp || "").trim();
    if (!/^\d{4,10}$/.test(code)) { toast.error("OTP non valido"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/policies/bulk-delete/confirm", {
        policy_ids: Array.from(selectedIds),
        otp: code,
      });
      toast.success(`Eliminate ${data.deleted_count} polizze`);
      clearSelection();
      setBulkStage(null);
      setBulkOtp("");
      setBulkOtpInfo(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="policies-page">
      {/* Overlay full-screen drag&drop: appare quando l'utente trascina un file mentre il dialog polizza è aperto */}
      {dragOverlayActive && (
        <div
          data-testid="policy-drag-overlay"
          className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center bg-blue-600/30 backdrop-blur-sm border-4 border-dashed border-blue-500 animate-fade-in"
        >
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 text-center max-w-md mx-4">
            <div className="text-5xl mb-2">📎</div>
            <p className="text-lg font-semibold text-slate-900">
              {editing?.id ? "Rilascia qui per allegare alla polizza" : "Rilascia qui · saranno allegati al salvataggio"}
            </p>
            <p className="text-xs text-slate-500 mt-1.5">
              PDF, immagini o documenti · max 10 MB ciascuno
            </p>
          </div>
        </div>
      )}
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Portafoglio</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><Shield className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 bg-amber-100 rounded-lg p-1.5" />Polizze</h1>
          <p className="text-slate-500 mt-1">{items.length} polizze totali</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" data-testid="print-policies" onClick={() => window.print()} className="text-xs sm:text-sm"><Printer className="h-4 w-4 mr-1.5" /> Stampa</Button>
          {canWrite("policies") && (
          <Button
            data-testid="policy-wizard-btn"
            variant="outline"
            onClick={() => navigate("/policies/wizard")}
            className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs sm:text-sm"
          >
            <Sparkles className="h-4 w-4 mr-1.5" /> Wizard<span className="hidden sm:inline"> guidato</span>
          </Button>
          )}
          {canWrite("policies") && (
          <Dialog open={open} onOpenChange={(o) => {
            if (!o) { closePolicyDialog(); }
            else {
              // 🆕 Nuova polizza (senza editing): resetta il flag manuale di Extra fees
              if (!editing) extraFeesManualRef.current = false;
              setOpen(true);
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-policy-btn" className="bg-[#0F172A] hover:bg-[#1E293B] text-xs sm:text-sm">
                <Plus className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Nuova </span>polizza
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-[1360px] max-h-[95vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Modifica polizza" : "Nuova polizza"}</DialogTitle></DialogHeader>
              {/* 🆕 16/07/2026 — Azioni: Rinnovo / Sostituzione / Crea Offerta + Carosello catena */}
              {editing && (
                <div className="mb-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="policy-chain-actions">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-lg">⚠️</span>
                    <p className="text-xs text-amber-900 leading-snug">
                      <strong>Attenzione:</strong> ognuna di queste azioni crea un <strong>nuovo record</strong> o documento collegato alla polizza corrente. La polizza attuale verrà archiviata e resterà accessibile solo dallo storico (carosello sotto).
                    </p>
                  </div>
                  {/* 🆕 Feb 2026 — Badge causale sostituzione: visibile se questa polizza è nata da una sostituzione */}
                  {editing?.link_type === "replacement" && editing?.replacement_reason_code && (
                    <div className="mb-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 flex items-start gap-2" data-testid="policy-replacement-reason-badge">
                      <span className="text-blue-700 text-base leading-none mt-0.5">🔄</span>
                      <div className="flex-1 text-xs text-blue-900 leading-snug">
                        <div>
                          <strong>Causale sostituzione:</strong>{" "}
                          <code className="bg-white px-1.5 py-0.5 rounded border border-blue-200 font-mono">{editing.replacement_reason_code}</code>
                          {editing.replacement_reason_label && editing.replacement_reason_label !== editing.replacement_reason_code && (
                            <span className="ml-1">— {editing.replacement_reason_label}</span>
                          )}
                        </div>
                        {editing.replacement_preserved_full_data && (
                          <div className="mt-1 text-[11px] text-amber-800">
                            📄 <strong>Copia integrale</strong>: la polizza è stata clonata mantenendo date, importi, garanzie. Solo targa e n° polizza da compilare.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="policy-btn-renewal"
                      disabled={chainActionBusy}
                      onClick={() => setChainAction("renewal")}
                      className="bg-emerald-50 border-emerald-400 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-500 font-semibold"
                    >
                      🔁 Rinnovo
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="policy-btn-replacement"
                      disabled={chainActionBusy}
                      onClick={async () => {
                        // 🆕 Feb 2026 — Prima di aprire il dialog di conferma, chiedi la causale di sostituzione.
                        // La causale è SEMPRE obbligatoria: se la lista è vuota, l'utente deve prima
                        // configurare almeno una causale dal menù Gestione Agenzia.
                        setSelectedReplacementReason(null);
                        setReplacementReasonLoading(true);
                        setReplacementReasonPickerOpen(true);
                        try {
                          const { data } = await api.get("/replacement-reasons");
                          const active = (Array.isArray(data) ? data : []).filter((r) => r.active !== false);
                          setReplacementReasons(active);
                        } catch {
                          setReplacementReasons([]);
                        } finally {
                          setReplacementReasonLoading(false);
                        }
                      }}
                      className="bg-blue-50 border-blue-400 text-blue-800 hover:bg-blue-100 hover:border-blue-500 font-semibold"
                    >
                      🔄 Sostituzione Polizza
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="policy-btn-create-offer"
                      disabled={chainActionBusy}
                      onClick={() => setChainAction("quote_offer")}
                      className="bg-purple-50 border-purple-400 text-purple-800 hover:bg-purple-100 hover:border-purple-500 font-semibold"
                    >
                      📄 Crea Offerta da Polizza
                    </Button>
                    {/* 🆕 28/07/2026 — Riattivazione polizza sospesa (visibile solo se status = sospesa) */}
                    {editing?.status === "sospesa" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="policy-btn-reactivate"
                        disabled={reactivating}
                        onClick={() => openReactivate({ ...editing, _prefill_reactivation_date: form.suspension_end_date || undefined })}
                        className="bg-teal-50 border-teal-400 text-teal-800 hover:bg-teal-100 hover:border-teal-500 font-semibold"
                      >
                        ▶️ Riattiva Polizza
                      </Button>
                    )}
                    {/* 🆕 Feb 2026 — Sospendi polizza (visibile solo se status = attiva) */}
                    {editing?.status === "attiva" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="policy-btn-suspend"
                        onClick={() => openSuspend(editing)}
                        className="bg-orange-50 border-orange-400 text-orange-800 hover:bg-orange-100 hover:border-orange-500 font-semibold"
                      >
                        ⏸️ Sospendi Polizza
                      </Button>
                    )}
                    {/* 🆕 16/07/2026 — Annulla Rinnovo/Sostituzione: visibile solo se la polizza corrente è parte di una catena (child) */}
                    {editing?.link_type && editing?.renewed_from_policy_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="policy-btn-undo-chain"
                        disabled={chainActionBusy}
                        onClick={() => setChainAction("undo")}
                        className="bg-rose-50 border-rose-400 text-rose-800 hover:bg-rose-100 hover:border-rose-500 font-semibold ml-auto"
                      >
                        🗑️ Annulla {editing.link_type === "renewal" ? "Rinnovo" : editing.link_type === "replacement" ? "Sostituzione" : "Offerta"}
                      </Button>
                    )}
                    {chainActionBusy && <span className="text-xs text-amber-700 font-medium">⏳ Elaborazione in corso…</span>}
                  </div>
                  {/* Carosello catena Rinnovo/Sostituzione */}
                  {policyChain.length > 1 && (
                    <div className="mt-3 pt-3 border-t border-amber-300" data-testid="policy-chain-carousel">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[11px] font-semibold text-amber-900 uppercase tracking-wider">🎠 Storico polizza · {policyChain.length} versioni (più recente a sinistra)</p>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" data-testid="policy-chain-prev" disabled={chainCurrentIdx <= 0}
                            onClick={() => {
                              const nextIdx = Math.max(0, chainCurrentIdx - 1);
                              setChainCurrentIdx(nextIdx);
                              openEdit(policyChain[nextIdx]);
                            }} className="h-6 w-6 p-0">◀</Button>
                          <span className="text-xs font-mono tabular-nums text-amber-900 min-w-[3ch] text-center" data-testid="policy-chain-pos">{chainCurrentIdx + 1}/{policyChain.length}</span>
                          <Button size="sm" variant="ghost" data-testid="policy-chain-next" disabled={chainCurrentIdx >= policyChain.length - 1}
                            onClick={() => {
                              const nextIdx = Math.min(policyChain.length - 1, chainCurrentIdx + 1);
                              setChainCurrentIdx(nextIdx);
                              openEdit(policyChain[nextIdx]);
                            }} className="h-6 w-6 p-0">▶</Button>
                        </div>
                      </div>
                      <div className="flex gap-1.5 overflow-x-auto pb-1">
                        {policyChain.map((cp, i) => {
                          // 🆕 Feb 2026 — Riattivazione: le polizze clone hanno reactivated_from_policy_id
                          // valorizzato ma link_type=null, quindi rientrano nel default. Etichetta dedicata.
                          const label = cp.reactivated_from_policy_id
                            ? "▶️ Riattivata"
                            : cp.link_type === "renewal"
                              ? "🔁 Rinnovo"
                              : cp.link_type === "replacement"
                                ? "🔄 Sostituzione"
                                : cp.link_type === "from_quote_offer"
                                  ? "📄 Da Offerta"
                                  : "🌱 Originale";
                          const isCurrent = i === chainCurrentIdx;
                          const isMostRecent = i === 0;
                          return (
                            <button
                              key={cp.id}
                              type="button"
                              data-testid={`policy-chain-item-${i}`}
                              onClick={() => { setChainCurrentIdx(i); openEdit(cp); }}
                              className={`shrink-0 text-left px-2 py-1 rounded border transition-colors min-w-[140px] ${isCurrent ? "bg-amber-200 border-amber-600 shadow-inner" : "bg-white border-amber-300 hover:bg-amber-100"}`}
                              title={`${label}${isMostRecent ? " (attuale)" : ""} · ${cp.policy_number || "s.n."}`}
                            >
                              <div className="text-[9px] font-bold uppercase tracking-wide text-amber-800">{label}{isMostRecent && " ★"}</div>
                              <div className="text-[10px] font-mono text-slate-800 truncate">{cp.policy_number || "—"}</div>
                              <div className="text-[9px] text-slate-500">{cp.end_date ? new Date(cp.end_date).toLocaleDateString("it-IT") : "—"} · <span className="uppercase">{cp.status}</span></div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* 🆕 16/07/2026 — Log audit operazioni catena */}
                  {Array.isArray(editing?.chain_audit_log) && editing.chain_audit_log.length > 0 && (
                    <details className="mt-3 pt-3 border-t border-amber-300 group" data-testid="policy-chain-audit-log">
                      <summary className="cursor-pointer text-[11px] font-semibold text-amber-900 uppercase tracking-wider hover:text-amber-950 select-none list-none flex items-center gap-1.5">
                        <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
                        📋 Log operazioni · {editing.chain_audit_log.length} eventi
                      </summary>
                      <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                        {[...editing.chain_audit_log].reverse().map((ev, i) => {
                          const actionLabel = {
                            renewed: "🔁 Rinnovo",
                            replaced: "🔄 Sostituzione",
                            offer_created: "📄 Offerta creata",
                            offer_converted: "✅ Offerta convertita in polizza",
                            undone_renewal: "🗑️ Annullato Rinnovo",
                            undone_replacement: "🗑️ Annullata Sostituzione",
                            undone_from_quote_offer: "🗑️ Annullata Offerta",
                          }[ev.action] || ev.action;
                          const when = ev.at ? new Date(ev.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                          return (
                            <div key={i} className="text-[10px] bg-white/70 border border-amber-200 rounded px-2 py-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2" data-testid={`policy-chain-audit-item-${i}`}>
                              <span className="font-bold text-amber-900 shrink-0">{actionLabel}</span>
                              <span className="text-slate-600 shrink-0">👤 {ev.user_name || "-"}</span>
                              <span className="text-slate-500 font-mono shrink-0">{when}</span>
                              {ev.notes && <span className="text-slate-700 italic truncate">— {ev.notes}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </div>
              )}
              <div
                  data-testid="policy-extract-dropzone"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setPolDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setPolDragOver(false); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    setPolDragOver(false);
                    extractPolicyFile(e.dataTransfer.files?.[0]);
                  }}
                  className={`mb-3 rounded-lg border-2 border-dashed p-4 transition ${polDragOver ? "border-blue-500 bg-blue-100" : polExtractBusy ? "border-amber-400 bg-amber-50" : "border-blue-300 bg-blue-50/60"}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{polExtractBusy ? "⏳" : "📥"}</span>
                      <div>
                        <div className="text-sm font-semibold text-blue-900">
                          {polExtractBusy
                            ? "Estrazione AI in corso…"
                            : editing
                              ? "Trascina la copia della polizza per completare i dati mancanti"
                              : "Trascina qui la polizza per estrazione automatica"}
                        </div>
                        <div className="text-[11px] text-slate-600">
                          PDF / JPG / PNG / HEIC · L&apos;AI riconosce <strong>tipo polizza</strong>, garanzie, premio, dati veicolo/casa. {editing ? <strong>Solo i campi vuoti verranno popolati</strong> : "Crea i campi specifici e le garanzie attive."}
                        </div>
                      </div>
                    </div>
                    <label className="cursor-pointer">
                      <input data-testid="import-policy-pdf" type="file" accept="application/pdf,image/*,.heic,.heif" className="hidden" onChange={importPolicyPDF} disabled={polExtractBusy} />
                      <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-blue-300 bg-white hover:bg-blue-100 text-blue-700 font-medium">📄 {editing ? "Allega e completa" : "Oppure scegli file"}</span>
                    </label>
                  </div>
                </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Cliente <span className="text-red-500">*</span></Label>
                  <ClientCombobox
                    clients={clients}
                    value={form.client_id}
                    onChange={(v) => setForm({ ...form, client_id: v })}
                    onClientCreated={(c) => {
                      // 🆕 Inserisci nuovo cliente in lista + selezionalo subito
                      setClients((prev) => (prev.some((x) => x.id === c.id) ? prev : [...prev, c]));
                      setForm((f) => ({ ...f, client_id: c.id }));
                    }}
                    placeholder="Dato obbligatorio · seleziona cliente"
                    testid="policy-client"
                  />
                  {/* Pannello dati cliente completo (read-only) */}
                  {(() => {
                    const c = clients.find((x) => x.id === form.client_id);
                    if (!c) return null;
                    const fullAddress = [c.address, c.house_number, c.cap, c.city, c.province].filter(Boolean).join(" ");
                    const fmtDate = (d) => d ? new Date(d).toLocaleDateString("it-IT") : "—";
                    const renderField = (label, value, mono = false, href = null) => value ? (
                      <div key={label} className="text-xs text-slate-700 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
                        {href ? (
                          <a href={href} onClick={(e) => e.stopPropagation()} className={`text-blue-600 hover:underline truncate block ${mono ? "font-mono" : ""}`} title={value}>{value}</a>
                        ) : (
                          <div className={`truncate ${mono ? "font-mono font-semibold" : ""}`} title={value}>{value}</div>
                        )}
                      </div>
                    ) : null;
                    return (
                      <div
                        data-testid="policy-client-info"
                        className="mt-2 rounded-lg border border-blue-200 bg-blue-50/30 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() => setClientCardCollapsed((v) => !v)}
                          data-testid="policy-client-card-toggle"
                          className="w-full bg-blue-100/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-800 flex items-center justify-between hover:bg-blue-100 transition-colors"
                          title={clientCardCollapsed ? "Espandi dati cliente" : "Comprimi dati cliente"}
                        >
                          <span className="flex items-center gap-1.5">
                            <span className={`transition-transform inline-block ${clientCardCollapsed ? "" : "rotate-90"}`}>▶</span>
                            {c.subject_type === "company" ? "🏢 Dati cliente (Azienda)" : "👤 Dati cliente"}
                            {clientCardCollapsed && (
                              <span className="ml-2 text-[10px] text-blue-700 font-normal normal-case tracking-normal" data-testid="policy-client-card-collapsed-summary">
                                {c.subject_type === "company"
                                  ? (c.ragione_sociale || "").toUpperCase()
                                  : `${c.surname || ""} ${c.name || ""}`.trim()}
                                {c.subject_type === "company" && c.partita_iva && <span className="ml-1 font-mono">· P.IVA {c.partita_iva}</span>}
                                {c.subject_type !== "company" && c.fiscal_code && <span className="ml-1 font-mono">· {c.fiscal_code.toUpperCase()}</span>}
                              </span>
                            )}
                          </span>
                          <a
                            href={editing?.id ? `/clients?focus=${c.id}&return_to=${encodeURIComponent(`/policies?focus=${editing.id}`)}` : `/clients?focus=${c.id}`}
                            onClick={(e) => {
                              if (editing?.id) {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpen(false);
                                navigate(`/clients?focus=${c.id}&return_to=${encodeURIComponent(`/policies?focus=${editing.id}`)}`);
                              } else {
                                e.stopPropagation();
                              }
                            }}
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-blue-700 hover:underline"
                            data-testid="open-client-card"
                          >
                            Apri scheda →
                          </a>
                        </button>
                        {clientCardCollapsed ? null : (
                          <>
                        {/* Copertina sintetica: avatar placeholder + nome + badge ID */}
                        <div className="flex items-center gap-3 px-3 pt-3">
                          <div
                            className={`h-12 w-12 rounded-full text-white font-bold text-lg flex items-center justify-center shadow-sm shrink-0 ${c.subject_type === "company" ? "bg-gradient-to-br from-indigo-500 to-indigo-700" : "bg-gradient-to-br from-blue-500 to-blue-700"}`}
                            aria-hidden="true"
                          >
                            {c.subject_type === "company"
                              ? "🏢"
                              : (((c.surname?.[0] || "") + (c.name?.[0] || "")).toUpperCase() || "?")}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 truncate text-base">
                              {c.subject_type === "company"
                                ? (c.ragione_sociale || "Azienda")
                                : (`${c.surname || ""} ${c.name || ""}`.trim() || "Cliente")}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                              <span className="text-[10px] font-mono bg-slate-100 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5">
                                ID {c.id?.slice(0, 8) || "—"}
                              </span>
                              {c.subject_type === "company" && (
                                <span className="text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5">
                                  🏢 Azienda
                                </span>
                              )}
                              {c.marketing_consent?.accepted && (
                                <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                                  ✓ Marketing
                                </span>
                              )}
                              {c.subject_type !== "company" && c.driving_license_human_verified_at && (
                                <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                                  ✓ Patente verificata
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="p-3 space-y-3">
                          {/* 🆕 Feb 2026 — Dati Azienda (se subject_type=company) */}
                          {c.subject_type === "company" ? (
                            <>
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-1">🏢 Dati Azienda</div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
                                  {renderField("Ragione Sociale", c.ragione_sociale)}
                                  {renderField("P.IVA", c.partita_iva, true)}
                                  {renderField("CF Societario", c.codice_fiscale_societario?.toUpperCase(), true)}
                                  {renderField("Codice SDI", c.sdi_code, true)}
                                  {renderField("PEC", c.pec_email || c.pec, false, (c.pec_email || c.pec) ? `mailto:${c.pec_email || c.pec}` : null)}
                                </div>
                              </div>
                              {(c.legale_rappresentante_name || c.legale_rappresentante_surname || c.legale_rappresentante_fiscal_code) && (
                                <div>
                                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">👔 Legale Rappresentante</div>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
                                    {renderField("Cognome Nome", `${c.legale_rappresentante_surname || ""} ${c.legale_rappresentante_name || ""}`.trim())}
                                    {renderField("Codice Fiscale", c.legale_rappresentante_fiscal_code?.toUpperCase(), true)}
                                    {renderField("Carica", c.legale_rappresentante_role)}
                                    {renderField("Sesso", c.legale_rappresentante_gender === "F" ? "Femmina" : c.legale_rappresentante_gender === "M" ? "Maschio" : "")}
                                    {renderField("Data nascita", fmtDate(c.legale_rappresentante_birth_date))}
                                    {renderField("Luogo nascita", [c.legale_rappresentante_birth_place, c.legale_rappresentante_birth_province ? `(${c.legale_rappresentante_birth_province})` : ""].filter(Boolean).join(" "))}
                                    {renderField("Nazionalità", c.legale_rappresentante_nationality)}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Anagrafica</div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
                                {renderField("Cognome Nome", `${c.surname || ""} ${c.name || ""}`.trim())}
                                {renderField("Sesso", c.gender === "F" ? "Femmina" : c.gender === "M" ? "Maschio" : "")}
                                {renderField("Codice fiscale", c.fiscal_code?.toUpperCase(), true)}
                                {renderField("Stato civile", c.civil_status)}
                                {renderField("Data nascita", fmtDate(c.birth_date))}
                                {renderField("Luogo nascita", c.birth_place)}
                                {renderField("Professione", c.occupation)}
                                {renderField("Datore lavoro", c.employer)}
                              </div>
                            </div>
                          )}
                          {/* Contatti */}
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Contatti</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
                              {renderField("Email", c.email, false, c.email ? `mailto:${c.email}` : null)}
                              {renderField("PEC", c.pec, false, c.pec ? `mailto:${c.pec}` : null)}
                              {renderField("Cellulare", c.mobile, true, c.mobile ? `tel:${c.mobile}` : null)}
                              {renderField("Telefono", c.phone, true, c.phone ? `tel:${c.phone}` : null)}
                            </div>
                          </div>
                          {/* Indirizzo */}
                          {fullAddress && (
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{c.subject_type === "company" ? "Sede Legale" : "Residenza"}</div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
                                {renderField("Indirizzo", [c.address, c.house_number].filter(Boolean).join(" "))}
                                {renderField("Città", [c.cap, c.city, c.province ? `(${c.province})` : ""].filter(Boolean).join(" "))}
                              </div>
                            </div>
                          )}
                          {/* Documenti & Scadenze — solo Privati */}
                          {c.subject_type !== "company" && (c.driving_license_issued_at || c.driving_license_renewed_at || c.driving_license_expires_at || c.id_card_expires_at || c.health_card_expires_at) && (
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Documenti</div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
                                {renderField("🪪 Primo ril. patente", fmtDate(c.driving_license_issued_at))}
                                {renderField("🔄 Rinnovo patente", fmtDate(c.driving_license_renewed_at))}
                                {renderField("🚗 Scad. patente", fmtDate(c.driving_license_expires_at))}
                                {renderField("🆔 Scad. CI", fmtDate(c.id_card_expires_at))}
                                {renderField("💳 Scad. T.S.", fmtDate(c.health_card_expires_at))}
                              </div>
                            </div>
                          )}
                          {/* Bancari */}
                          {c.iban && (
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Bancari</div>
                              <div className="text-xs text-slate-700 min-w-0">
                                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">IBAN</div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); copyToClipboard(String(c.iban || "").toUpperCase().replace(/\s/g, ""), "IBAN"); }}
                                  className="font-mono font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded px-1 -mx-1 truncate max-w-full text-left cursor-pointer inline-flex items-center gap-1"
                                  title="Clicca per copiare l'IBAN negli appunti"
                                  data-testid="policy-client-iban-copy"
                                >
                                  {c.iban}
                                  <span className="text-slate-400 text-[10px]">📋</span>
                                </button>
                              </div>
                            </div>
                          )}
                          {/* Note */}
                          {c.notes && (
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Note</div>
                              <div className="text-xs text-slate-700 italic whitespace-pre-wrap">{c.notes}</div>
                            </div>
                          )}
                        </div>
                        {/* 🆕 Feb 2026 — Collega area privata (retroattivo) */}
                        <div className="border-t border-slate-200 mt-3 pt-3 px-3 pb-3">
                          <button
                            type="button"
                            data-testid="policy-link-portal-btn"
                            onClick={openPortalLinkDialog}
                            className="w-full text-xs font-medium px-3 py-2 rounded-md border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100 transition-colors flex items-center justify-center gap-2"
                            title="Rendi visibili le polizze di questo cliente anche nell'area privata di un altro cliente esistente (es. marito che gestisce le polizze di moglie/figli)"
                          >
                            🔗 Collega area privata a un altro cliente
                          </button>
                        </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {(typeHasVehicleFields || form.show_vehicle_data) && (
                  <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                    <button
                      type="button"
                      data-testid="policy-vehicle-toggle"
                      onClick={() => setVehicleCollapsed((c) => !c)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors mb-2"
                      aria-expanded={!vehicleCollapsed}
                      title={vehicleCollapsed ? "Espandi Veicolo assicurato" : "Comprimi Veicolo assicurato"}
                    >
                      <span className="flex items-center gap-2 flex-wrap">
                        <Car className="h-4 w-4 text-blue-700 shrink-0" />
                        <span className="text-sm font-semibold text-blue-900">Veicolo assicurato</span>
                        {vehicleCollapsed && (() => {
                          // 🆕 Feb 2026 — Elenca TUTTI i veicoli della polizza (marca modello + targa)
                          const list = [];
                          if (form.vehicle_plate) list.push({ plate: form.vehicle_plate, brand: form.vehicle_brand, model: form.vehicle_model, type: form.type });
                          if (Array.isArray(form.vehicles_extra)) {
                            for (const v of form.vehicles_extra) if (v && (v.plate || "").trim()) list.push(v);
                          }
                          if (list.length === 0) return null;
                          const kindBadge = list.length >= 2 && form.multi_vehicle_kind ? (
                            form.multi_vehicle_kind === "flotta"
                              ? { emoji: "🚛", label: "Flotta", cls: "bg-purple-100 text-purple-800 border-purple-300" }
                              : { emoji: "🕰️", label: "Storica cumulativa", cls: "bg-amber-100 text-amber-800 border-amber-300" }
                          ) : null;
                          return (
                            <span className="flex flex-wrap items-center gap-1.5" data-testid="policy-vehicles-summary">
                              {kindBadge && (
                                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md border ${kindBadge.cls}`} data-testid="policy-multi-kind-badge">
                                  {kindBadge.emoji} {kindBadge.label}
                                </span>
                              )}
                              {list.map((v, i) => (
                                <span key={`${v.plate || i}`} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md bg-white border border-blue-300 text-blue-900 shadow-sm">
                                  {(v.brand || v.model) && (
                                    <span className="font-semibold">{[v.brand, v.model].filter(Boolean).join(" ")}</span>
                                  )}
                                  {v.plate && (
                                    <span className="font-mono font-bold text-blue-800 px-1.5 py-0.5 rounded bg-blue-100">{v.plate}</span>
                                  )}
                                  {v.type && (
                                    <span className="text-[10px] font-semibold px-1 py-0 rounded bg-sky-200 text-sky-900 uppercase">{v.type}</span>
                                  )}
                                </span>
                              ))}
                              {list.length > 1 && (
                                <span className="text-[10px] font-semibold text-blue-600 px-1.5 py-0.5 rounded-full bg-blue-100 border border-blue-200">
                                  {list.length} veicoli
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-blue-700 transition-transform ${vehicleCollapsed ? "" : "rotate-180"}`} />
                    </button>
                    {!typeHasVehicleFields && form.show_vehicle_data && (
                      <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">Sezione attivata manualmente</span>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, show_vehicle_data: false })}
                          className="text-slate-500 hover:text-rose-600 hover:underline"
                          data-testid="policy-hide-vehicle"
                        >
                          ✕ Nascondi
                        </button>
                      </div>
                    )}
                    {vehicleCollapsed ? null : (
                    <>
                    {selectedClient?.vehicles?.length > 0 && (
                      <div className="mt-2">
                        <Label className="text-xs text-slate-500">Veicoli del cliente</Label>
                        <Select value={form.vehicle_plate} onValueChange={(v) => {
                          const veh = selectedClient.vehicles.find((x) => x.plate === v);
                          setForm({ ...form, vehicle_plate: v, vehicle_brand: veh?.brand || "", vehicle_model: veh?.model || "" });
                        }}>
                          <SelectTrigger data-testid="policy-vehicle-select"><SelectValue placeholder="Seleziona o inserisci manualmente" /></SelectTrigger>
                          <SelectContent>
                            {selectedClient.vehicles.map((v) => (
                              <SelectItem key={v.plate} value={v.plate}>{v.plate} — {v.brand} {v.model}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                      <div className="flex flex-col">
                        <PlateInput
                          testid="policy-plate"
                          value={form.vehicle_plate}
                          onChange={(v) => setForm({ ...form, vehicle_plate: (v || "").replace(/\s+/g, "") })}
                          overrideFlag={!!form.vehicle_plate_override}
                          onOverrideChange={(v) => setForm({ ...form, vehicle_plate_override: v })}
                        />
                      </div>
                      <VehicleAutocomplete
                        testid="policy-brand"
                        placeholder="Marca"
                        value={form.vehicle_brand}
                        onChange={(v) => {
                          // Auto-classifica polizza come "moto" se il brand è di una moto e il tipo attuale è "auto"
                          const patch = { vehicle_brand: v };
                          if (isMotoBrand(v) && form.type === "auto") {
                            patch.type = "moto";
                            toast.info(`🏍️ Brand ${v} riconosciuto come moto: tipo polizza auto-impostato a "Moto"`, { duration: 4500 });
                          }
                          setForm({ ...form, ...patch });
                        }}
                        suggestions={brandSuggestions}
                      />
                      <VehicleAutocomplete
                        testid="policy-model"
                        placeholder="Modello"
                        value={form.vehicle_model}
                        onChange={(v) => setForm({ ...form, vehicle_model: v })}
                        suggestions={modelSuggestions}
                      />
                    </div>
                    {/* Sezione Revisione veicolo (azzurro tenue) */}
                    <fieldset className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
                      <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-indigo-700 bg-white rounded">
                        🔧 Revisione veicolo
                      </legend>
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                      <div>
                        <Label className="text-xs text-slate-600 flex items-center gap-1">
                          ✅ Data revisione effettuata
                        </Label>
                        <DatePicker data-testid="policy-last-review-date" value={form.last_review_date || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            // Auto-aggiorna prossima scadenza a +2 anni, forzando il giorno = 30 dello stesso mese
                            // (o ultimo giorno del mese se febbraio). Il bollo della revisione scade legalmente a fine mese.
                            let nextRev = form.next_review_date;
                            if (v) {
                              const d = new Date(v);
                              d.setFullYear(d.getFullYear() + 2);
                              const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                              d.setDate(Math.min(30, lastDay));
                              nextRev = d.toISOString().slice(0, 10);
                            }
                            setForm({ ...form, last_review_date: v, next_review_date: nextRev });
                          }}
                          className="font-mono" />
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          Compilala dopo la revisione → la prossima scadenza si aggiorna a +2 anni (giorno 30 del mese).
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600 flex items-center gap-1">
                          🔧 Prossima scadenza revisione
                          {form.next_review_date_client_updated_at && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200" title={`Aggiornata dal cliente il ${formatDate(form.next_review_date_client_updated_at)}`}>
                              ✓ dal cliente
                            </span>
                          )}
                        </Label>
                        <DatePicker data-testid="policy-next-review-date" value={form.next_review_date || ""}
                          onChange={(e) => setForm({ ...form, next_review_date: e.target.value })}
                          className="font-mono" />
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          Promemoria automatico 30 e 7 gg prima. Auto-calc: veicolo nuovo = +4 anni dall&apos;immatricolazione, dopo prima revisione = +2 anni.
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid="policy-check-revisione"
                        disabled={!form.vehicle_plate || !validatePlate(form.vehicle_plate).ok}
                        onClick={() => {
                          const plate = form.vehicle_plate;
                          try { Promise.resolve(navigator.clipboard?.writeText(plate)).catch(() => {}); } catch { /* noop */ }
                          toast.info(`Targa ${plate} copiata. Incollala nel campo "Targa" del portale.`, { duration: 6000 });
                          window.open("https://www.ilportaledellautomobilista.it/interrogazionistoricorevisioni/spa/", "_blank", "noopener,noreferrer");
                        }}
                        className="h-10 inline-flex items-center justify-center gap-1.5 px-3 rounded-md border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title={form.vehicle_plate ? "Apri il Portale dell'Automobilista e copia la targa" : "Inserisci prima la targa"}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Verifica revisione
                      </button>
                      </div>
                    </fieldset>
                    {/* Sezione Scadenza polizza & Avviso cliente (ambra tenue) */}
                    <fieldset className="mt-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                      <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700 bg-white rounded">
                        ⏰ Scadenza polizza & avviso cliente
                      </legend>
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
                        <div>
                          <Label className="text-xs text-slate-600 flex items-center gap-1">📅 Data scadenza polizza</Label>
                          <DatePicker data-testid="policy-end-date-2" value={form.end_date || ""}
                            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                            className="font-mono" />
                          {form.end_date && (() => {
                            const t = new Date(); t.setHours(0,0,0,0);
                            const e = new Date(form.end_date); e.setHours(0,0,0,0);
                            const days = Math.ceil((e - t) / 86400000);
                            const cls = days < 0 ? "text-red-700 bg-red-50 border-red-200" : days <= 30 ? "text-amber-800 bg-amber-100 border-amber-300" : "text-emerald-700 bg-emerald-50 border-emerald-200";
                            const lbl = days < 0 ? `⚠️ Scaduta da ${Math.abs(days)} gg` : days === 0 ? "🔴 Scade oggi" : days <= 30 ? `⏰ Mancano ${days} gg` : `✅ Mancano ${days} gg`;
                            return <span className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${cls}`}>{lbl}</span>;
                          })()}
                        </div>
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer select-none mb-1.5" title="Includi questo cliente nei promemoria automatici (30 e 7 gg prima)">
                          <input
                            type="checkbox"
                            data-testid="policy-notify-expiry"
                            checked={form.notify_expiry_client !== false}
                            onChange={(e) => setForm({ ...form, notify_expiry_client: e.target.checked })}
                            className="h-3.5 w-3.5 accent-amber-600"
                          />
                          Promemoria automatici
                        </label>
                        <button
                          type="button"
                          data-testid="policy-send-expiry-notice"
                          disabled={!editing?.id || !form.end_date || sendingExpiryNotice}
                          onClick={async () => {
                            if (!editing?.id) return;
                            // 🆕 28/07/2026 — Apri il dialog condiviso ExpiryNoticeDialog con scelta canale
                            setSendingExpiryNotice(true);
                            let client = null;
                            if (editing.client_id) {
                              try {
                                const { data } = await api.get(`/clients/${editing.client_id}`);
                                // 🆕 Feb 2026 — L'endpoint restituisce { client, policies }
                                client = data?.client || data;
                              } catch { /* ignora */ }
                            }
                            setNoticeDialog({ policy: editing, client });
                            setSendingExpiryNotice(false);
                          }}
                          className="h-10 inline-flex items-center justify-center gap-1.5 px-3 rounded-md border border-amber-400 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!editing?.id ? "Salva prima la polizza" : !form.end_date ? "Imposta una data di scadenza" : "Scegli canale (Email / WhatsApp / Entrambi)"}
                        >
                          <Mail className="h-3.5 w-3.5" />
                          {sendingExpiryNotice ? "Apro…" : "Invia avviso al cliente"}
                        </button>
                      </div>
                    </fieldset>
                    {/* Sezione Bollo auto (arancione tenue) */}
                    <fieldset className="mt-3 rounded-lg border border-orange-200 bg-orange-50/50 p-3">
                      <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-orange-700 bg-white rounded">
                        🚗 Bollo auto
                      </legend>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-slate-600 flex items-center gap-1">
                          🚗 Scadenza bollo auto
                          {form.bollo_due_date_client_updated_at && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200" title={`Aggiornata dal cliente il ${formatDate(form.bollo_due_date_client_updated_at)}`}>
                              ✓ dal cliente
                            </span>
                          )}
                        </Label>
                        <DatePicker data-testid="policy-bollo-due-date" value={form.bollo_due_date || ""}
                          onChange={(e) => setForm({ ...form, bollo_due_date: e.target.value })}
                          className="font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Importo bollo (€)</Label>
                        <Input
                          data-testid="policy-bollo-amount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.bollo_amount ?? ""}
                          onChange={(e) => setForm({ ...form, bollo_amount: e.target.value })}
                          placeholder="Es. 215.50"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 sm:col-span-1 self-end pb-2">
                        Stessi promemoria automatici della revisione (30/7 gg).
                      </p>
                      </div>
                    </fieldset>
                    </>
                    )}
                  </div>
                )}

                {(typeHasVehicleFields || form.show_vehicle_data) && !vehicleCollapsed && (
                  <PolicyVehicleDetails form={form} onChange={(patch) => {
                    setForm((prev) => {
                      const next = { ...prev, ...patch };
                      // Se cambia la data di prima immatricolazione e non c'è ancora next_review_date
                      // né last_review_date, calcola scadenza revisione a +4 anni (veicolo nuovo).
                      // Forza il giorno = 30 dello stesso mese (o ultimo giorno se febbraio): la revisione
                      // scade legalmente a fine mese.
                      if (
                        patch.vehicle_first_registration &&
                        !prev.last_review_date &&
                        !prev.next_review_date
                      ) {
                        try {
                          const d = new Date(patch.vehicle_first_registration);
                          if (!isNaN(d.getTime())) {
                            d.setFullYear(d.getFullYear() + 4);
                            const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                            d.setDate(Math.min(30, lastDay));
                            next.next_review_date = d.toISOString().slice(0, 10);
                          }
                        } catch { /* noop */ }
                      }
                      return next;
                    });
                  }} />
                )}

                {(typeHasVehicleFields || form.show_vehicle_data) && !vehicleCollapsed && (
                  <div className="col-span-2 mt-2 p-3 bg-white border border-sky-100 rounded-md space-y-3">
                    {/* 🆕 Feb 2026 — Classificazione polizza multi-veicolo (visibile solo con 2+ veicoli) */}
                    {(form.vehicles_extra?.length || 0) >= 1 && (
                      <div className="p-2 rounded-md border border-slate-200 bg-slate-50/40 flex items-center gap-3 flex-wrap" data-testid="policy-multi-kind-selector">
                        <span className="text-xs font-semibold text-slate-700">Tipo polizza multi-veicolo:</span>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="multi_vehicle_kind"
                            value="storica_cumulativa"
                            checked={form.multi_vehicle_kind === "storica_cumulativa"}
                            onChange={() => setForm((prev) => ({ ...prev, multi_vehicle_kind: "storica_cumulativa" }))}
                            data-testid="policy-multi-kind-storica"
                          />
                          <span className="text-xs">🕰️ <strong>Storica cumulativa</strong> (veicoli aggiunti nel tempo)</span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="multi_vehicle_kind"
                            value="flotta"
                            checked={form.multi_vehicle_kind === "flotta"}
                            onChange={() => setForm((prev) => ({ ...prev, multi_vehicle_kind: "flotta" }))}
                            data-testid="policy-multi-kind-flotta"
                          />
                          <span className="text-xs">🚛 <strong>Flotta</strong> (veicoli assicurati contemporaneamente)</span>
                        </label>
                        {form.multi_vehicle_kind && (
                          <button
                            type="button"
                            onClick={() => setForm((prev) => ({ ...prev, multi_vehicle_kind: null }))}
                            className="text-[11px] text-slate-500 hover:text-rose-600 hover:underline ml-auto"
                            data-testid="policy-multi-kind-clear"
                          >
                            ✕ Rimuovi classificazione
                          </button>
                        )}
                      </div>
                    )}
                    <VehiclesEditor
                      extraVehicles={form.vehicles_extra || []}
                      primaryPlate={form.vehicle_plate || ""}
                      onChange={(next) => setForm((prev) => ({ ...prev, vehicles_extra: next }))}
                    />
                  </div>
                )}

                {/* 🆕 30/07/2026 — Sezione Natante (visibile solo per polizze type="natante") */}
                {form.type === "natante" && (
                  <div className="col-span-2 border-t border-slate-200 pt-3 mt-1" data-testid="policy-natante-section">
                    <div className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-cyan-50 border border-cyan-200 mb-3">
                      <span className="text-sm font-semibold text-cyan-900">⛵ Dati imbarcazione</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="col-span-1 sm:col-span-2">
                        <Label>Marca e modello</Label>
                        <Input data-testid="pol-boat-maker-model" value={form.boat_maker_model || ""} onChange={(e) => setForm({ ...form, boat_maker_model: e.target.value })} />
                      </div>
                      <div>
                        <Label>Materiale scafo</Label>
                        <Select value={form.boat_hull_material || "_none"} onValueChange={(v) => setForm({ ...form, boat_hull_material: v === "_none" ? "" : v })}>
                          <SelectTrigger data-testid="pol-boat-hull-material"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Non specificato —</SelectItem>
                            {BOAT_HULL_MATERIAL_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Lunghezza (m)</Label>
                        <Input type="number" step="0.01" data-testid="pol-boat-length-m" value={form.boat_length_m || ""} onChange={(e) => setForm({ ...form, boat_length_m: e.target.value })} />
                      </div>
                      <div>
                        <Label>Larghezza / Baglio (m)</Label>
                        <Input type="number" step="0.01" data-testid="pol-boat-beam-m" value={form.boat_beam_m || ""} onChange={(e) => setForm({ ...form, boat_beam_m: e.target.value })} />
                      </div>
                      <div>
                        <Label>Anno di costruzione</Label>
                        <Input type="number" min="1900" max="2100" data-testid="pol-boat-build-year" value={form.boat_build_year || ""} onChange={(e) => setForm({ ...form, boat_build_year: e.target.value })} />
                      </div>
                      <div>
                        <Label>Matricola / Immatricolazione</Label>
                        <Input data-testid="pol-boat-registration-number" value={form.boat_registration_number || ""} onChange={(e) => setForm({ ...form, boat_registration_number: e.target.value })} />
                      </div>
                    </div>

                    <div className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-cyan-50 border border-cyan-200 my-3">
                      <span className="text-sm font-semibold text-cyan-900">⚙️ Dati motore</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div>
                        <Label>Costruttore motore</Label>
                        <Input data-testid="pol-boat-engine-maker" value={form.boat_engine_maker || ""} onChange={(e) => setForm({ ...form, boat_engine_maker: e.target.value })} />
                      </div>
                      <div>
                        <Label>Modello motore</Label>
                        <Input data-testid="pol-boat-engine-model" value={form.boat_engine_model || ""} onChange={(e) => setForm({ ...form, boat_engine_model: e.target.value })} />
                      </div>
                      <div>
                        <Label>Numero di matricola motore</Label>
                        <Input data-testid="pol-boat-engine-serial" value={form.boat_engine_serial || ""} onChange={(e) => setForm({ ...form, boat_engine_serial: e.target.value })} />
                      </div>
                      <div>
                        <Label>Tipo di installazione</Label>
                        <Select value={form.boat_engine_install || "_none"} onValueChange={(v) => setForm({ ...form, boat_engine_install: v === "_none" ? "" : v })}>
                          <SelectTrigger data-testid="pol-boat-engine-install"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Non specificato —</SelectItem>
                            {BOAT_ENGINE_INSTALL_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Combustibile</Label>
                        <Select value={form.boat_engine_fuel || "_none"} onValueChange={(v) => setForm({ ...form, boat_engine_fuel: v === "_none" ? "" : v })}>
                          <SelectTrigger data-testid="pol-boat-engine-fuel"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Non specificato —</SelectItem>
                            {BOAT_ENGINE_FUEL_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Potenza dichiarata (Kw)</Label>
                        <Input type="number" step="0.01" data-testid="pol-boat-engine-kw" value={form.boat_engine_kw || ""} onChange={(e) => setForm({ ...form, boat_engine_kw: e.target.value })} />
                      </div>
                      <div>
                        <Label>Massa motore (kg)</Label>
                        <Input type="number" step="0.1" data-testid="pol-boat-engine-mass-kg" value={form.boat_engine_mass_kg || ""} onChange={(e) => setForm({ ...form, boat_engine_mass_kg: e.target.value })} />
                      </div>
                      <div>
                        <Label>N° dichiarazione</Label>
                        <Input data-testid="pol-boat-declaration-number" value={form.boat_declaration_number || ""} onChange={(e) => setForm({ ...form, boat_declaration_number: e.target.value })} />
                      </div>
                      <div>
                        <Label>Data dichiarazione</Label>
                        <Input type="date" data-testid="pol-boat-declaration-date" value={form.boat_declaration_date || ""} onChange={(e) => setForm({ ...form, boat_declaration_date: e.target.value })} />
                      </div>
                    </div>

                    {/* 🆕 30/07/2026 — Motori aggiuntivi (multi-motore) */}
                    <div className="mt-4 pt-3 border-t border-cyan-100">
                      <p className="text-xs uppercase tracking-wide text-cyan-700 font-bold mb-2 flex items-center gap-1.5">
                        🔧 Motori aggiuntivi
                      </p>
                      <BoatEnginesEditor
                        extraEngines={form.boat_engines_extra || []}
                        onChange={(next) => setForm((prev) => ({ ...prev, boat_engines_extra: next }))}
                      />
                    </div>
                  </div>
                )}

                {/* 🆕 Card unica "Dati Polizza" — raggruppa tutti i campi della polizza sotto la card cliente */}
                <div className="col-span-2 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4 shadow-sm space-y-3" data-testid="policy-data-card">
                  <div className="flex items-center gap-2 text-blue-900 font-semibold border-b border-blue-200 pb-2">
                    <FileText className="h-4 w-4" /> Dati Polizza
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* 🆕 15/07/2026 — Layout aggiornato: Stato in alto (auto-fit + colorato), poi Numero + Tipo + Categoria + Frazionamento sulla stessa riga */}
                    <div className="sm:col-span-2 flex items-start">
                      <div className="w-fit min-w-[160px]">
                        <Label>Stato</Label>
                        {(() => {
                          const statusStyles = {
                            attiva: "bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold",
                            in_scadenza: "bg-amber-50 border-amber-400 text-amber-800 font-semibold",
                            scaduta: "bg-rose-50 border-rose-400 text-rose-800 font-semibold",
                            sospesa: "bg-orange-50 border-orange-400 text-orange-800 font-semibold",
                            disdetta: "bg-rose-100 border-rose-500 text-rose-900 font-semibold",
                            in_emissione: "bg-blue-50 border-blue-400 text-blue-800 font-semibold",
                          };
                          const cls = statusStyles[form.status] || "bg-white text-slate-700";
                          return (
                            <div className="flex flex-wrap items-center gap-2">
                              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger data-testid="policy-status" className={`${cls} w-auto min-w-[160px] transition-colors`}><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                              </Select>
                              {form.was_reactivated && form.status === "attiva" && (
                                <span
                                  data-testid="policy-reactivated-badge"
                                  className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide bg-sky-100 text-sky-800 border border-sky-400 rounded-full px-2.5 py-1"
                                  title={form.reactivated_at ? `Polizza riattivata il ${formatDate(form.reactivated_at)}${(form.reactivations_count || 0) > 1 ? ` · ${form.reactivations_count} riattivazioni totali` : ""}` : "Polizza riattivata dopo sospensione"}
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                  Riattivata{(form.reactivations_count || 0) > 1 ? ` ×${form.reactivations_count}` : ""}{form.reactivated_at ? ` il ${formatDate(form.reactivated_at)}` : ""}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    {/* 🆕 28/07/2026 — Se lo stato è "sospesa" mostra Data sospensione + Fine sospensione
                        con storico eventi e promemoria (logica ereditata da PolicySuspensionPanel, ora dismesso). */}
                    {/* 🆕 Feb 2026 — Blocco "Riattivata": visibile quando status=attiva e was_reactivated=true.
                        Mostra la data di riattivazione + espande lo storico sospensioni. */}
                    {form.status === "attiva" && form.was_reactivated && (
                      <div
                        className="sm:col-span-2 rounded-md bg-sky-50/70 border border-sky-200 p-3 space-y-2"
                        data-testid="policy-reactivated-block"
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 text-sky-800">
                            <PlayCircle className="h-4 w-4" />
                            <span className="text-sm font-semibold">
                              Polizza riattivata{form.reactivated_at ? ` in data ${formatDate(form.reactivated_at)}` : ""}
                              {(form.reactivations_count || 0) > 1 && (
                                <span className="ml-2 text-[11px] font-bold bg-sky-200 text-sky-900 rounded-full px-2 py-0.5" data-testid="reactivations-count">
                                  ×{form.reactivations_count}
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {Array.isArray(form.suspension_history) && form.suspension_history.length > 0 && (
                              <button
                                type="button"
                                data-testid="toggle-reactivated-history"
                                onClick={() => setShowSuspensionHistory((v) => !v)}
                                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-sky-800 hover:text-sky-900 bg-white/60 hover:bg-white border border-sky-200 rounded-full px-2.5 py-1"
                                title="Mostra lo storico completo di sospensioni e riattivazioni"
                              >
                                Storico ({form.suspension_history.length})
                              </button>
                            )}
                            {form.reactivated_from_policy_id && (
                              <button
                                type="button"
                                data-testid="undo-reactivation-btn"
                                onClick={async () => {
                                  if (!window.confirm(
                                    "Annullare la riattivazione?\n\n" +
                                    "• Questa polizza (la duplicata creata dalla riattivazione) verrà ELIMINATA.\n" +
                                    "• La polizza originale tornerà in stato 'sospesa' e sarà nuovamente visibile.\n\n" +
                                    "Vuoi procedere?"
                                  )) return;
                                  try {
                                    const r = await api.post(`/policies/${form.id}/undo-reactivation`);
                                    toast.success(r.data?.message || "Riattivazione annullata");
                                    const restoredId = r.data?.restored_policy_id;
                                    setOpen(false);
                                    setEditing(null);
                                    await load();
                                    if (restoredId) {
                                      try {
                                        const rd = await api.get(`/policies/${restoredId}`);
                                        if (rd?.data) openEdit(rd.data);
                                      } catch (_) { /* ignore load fail */ }
                                    }
                                  } catch (e) {
                                    toast.error(formatApiError(e));
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-700 hover:text-white hover:bg-rose-600 bg-white border border-rose-300 rounded-full px-2.5 py-1 transition-colors"
                                title="Elimina questa polizza duplicata e riporta l'originale in stato sospesa"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Annulla riattivazione
                              </button>
                            )}
                          </div>
                        </div>
                        {/* 🆕 Feb 2026 — Riquadri "Decorrenza precedente" / "Scadenza precedente"
                            (valori della polizza prima della sospensione, preservati sul clone). */}
                        {(form.original_start_date || form.original_end_date) && (
                          <div className="grid grid-cols-2 gap-2 mt-1" data-testid="reactivated-previous-dates">
                            <div className="rounded-sm bg-white/70 border border-sky-200 px-2 py-1.5">
                              <div className="text-[10px] uppercase tracking-wide text-sky-800/80 font-semibold">Decorrenza precedente</div>
                              <div className="text-[13px] font-semibold text-slate-800" data-testid="reactivated-prev-start">
                                {form.original_start_date ? formatDate(form.original_start_date) : "—"}
                              </div>
                            </div>
                            <div className="rounded-sm bg-white/70 border border-sky-200 px-2 py-1.5">
                              <div className="text-[10px] uppercase tracking-wide text-sky-800/80 font-semibold">Scadenza precedente</div>
                              <div className="text-[13px] font-semibold text-slate-800" data-testid="reactivated-prev-end">
                                {form.original_end_date ? formatDate(form.original_end_date) : "—"}
                              </div>
                            </div>
                          </div>
                        )}
                        {showSuspensionHistory && Array.isArray(form.suspension_history) && form.suspension_history.length > 0 && (
                          <div className="mt-1 pt-2 border-t border-sky-200">
                            <div className="flex justify-end mb-2">
                              <button
                                type="button"
                                data-testid="clear-suspension-history"
                                onClick={async () => {
                                  if (!window.confirm(`Svuotare completamente lo storico sospensioni (${form.suspension_history.length} record)?`)) return;
                                  try {
                                    await api.delete(`/policies/${form.id}/suspension-history`);
                                    setForm((f) => ({ ...f, suspension_history: [] }));
                                    toast.success("Storico svuotato");
                                  } catch (e) { toast.error(formatApiError(e)); }
                                }}
                                className="text-[10px] font-medium text-red-600 hover:text-red-700 border border-red-200 hover:bg-red-50 rounded px-2 py-0.5"
                              >
                                🗑 Svuota storico
                              </button>
                            </div>
                            <ul className="space-y-1.5 text-[12px] text-slate-700" data-testid="reactivated-history-list">
                              {form.suspension_history.map((h, i) => (
                                <li key={`react-hist-${i}`} className="pl-2 border-l-2 border-sky-300 flex items-start justify-between gap-2">
                                  <span>
                                    ↻ <b>Polizza riattivata</b> in data <b>{formatDate(h.reactivation_date)}</b>
                                    {" · "}
                                    sospesa dal {formatDate(h.suspension_start_date)} ({h.days_suspended}gg)
                                    {" · "}
                                    scadenza {formatDate(h.old_end_date)} → <b>{formatDate(h.new_end_date)}</b>
                                  </span>
                                  <button
                                    type="button"
                                    data-testid={`delete-history-entry-${i}`}
                                    onClick={async () => {
                                      if (!window.confirm("Eliminare questo record dallo storico?")) return;
                                      try {
                                        await api.delete(`/policies/${form.id}/suspension-history/${i}`);
                                        setForm((f) => {
                                          const nh = [...(f.suspension_history || [])];
                                          nh.splice(i, 1);
                                          return { ...f, suspension_history: nh };
                                        });
                                        toast.success("Record eliminato");
                                      } catch (e) { toast.error(formatApiError(e)); }
                                    }}
                                    className="text-red-500 hover:text-red-700 shrink-0"
                                    title="Elimina questo record"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {form.status === "sospesa" && (
                      <div className="sm:col-span-2 rounded-md bg-amber-50/70 border border-amber-200 p-3 space-y-3" data-testid="policy-suspension-block">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-amber-800">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-sm font-semibold">Polizza sospesa — Data sospensione e riattivazione</span>
                          </div>
                          {Array.isArray(form.suspension_history) && form.suspension_history.length > 0 && (
                            <button
                              type="button"
                              data-testid="toggle-suspension-history"
                              onClick={() => setShowSuspensionHistory((v) => !v)}
                              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-800 hover:text-amber-900 bg-white/60 hover:bg-white border border-amber-200 rounded-full px-2.5 py-1"
                              title="Mostra lo storico delle sospensioni, riattivazioni e proroghe"
                            >
                              <History className="h-3.5 w-3.5" />
                              Storico ({form.suspension_history.length})
                              {showSuspensionHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Data sospensione</Label>
                            <DatePicker
                              data-testid="policy-suspension-start"
                              value={form.suspension_start_date || ""}
                              onChange={(e) => setForm({ ...form, suspension_start_date: e.target.value || null })}
                              className="bg-white"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">Se vuota, oggi.</p>
                          </div>
                          <div>
                            <Label className="text-xs">Fine sospensione (data riattivazione)</Label>
                            <DatePicker
                              data-testid="policy-suspension-end"
                              value={form.suspension_end_date || ""}
                              onChange={(e) => setForm({ ...form, suspension_end_date: e.target.value || null })}
                              className="bg-white"
                              min={form.suspension_start_date || undefined}
                            />
                            <p className="text-[11px] text-slate-500 mt-1">
                              {(() => {
                                if (!form.suspension_start_date || !form.suspension_end_date) return "Alla riattivazione, scadenza polizza e rate saranno spostate avanti dei giorni di fermo.";
                                try {
                                  const a = new Date(form.suspension_start_date);
                                  const b = new Date(form.suspension_end_date);
                                  if (isNaN(a) || isNaN(b) || b < a) return "Alla riattivazione, scadenza polizza e rate saranno spostate avanti dei giorni di fermo.";
                                  const days = Math.round((b - a) / 86400000);
                                  return `Scadenza e rate future saranno spostate avanti di ${days} giorni.`;
                                } catch { return ""; }
                              })()}
                            </p>
                          </div>
                        </div>
                        <p className="text-[11px] text-amber-700">
                          Il cliente riceverà un promemoria 15 giorni prima della scadenza del termine massimo di riattivazione, pena la perdita del contratto.
                        </p>
                        {/* 🆕 Feb 2026 — Riquadri "Decorrenza precedente" / "Scadenza precedente"
                            = start_date/end_date della polizza al momento della sospensione.
                            Utili quando lo staff cambia le date correnti durante il periodo sospeso. */}
                        {(form.original_start_date || form.original_end_date) && (
                          <div className="grid grid-cols-2 gap-2" data-testid="suspension-previous-dates">
                            <div className="rounded-sm bg-white/70 border border-amber-200 px-2 py-1.5">
                              <div className="text-[10px] uppercase tracking-wide text-amber-800/80 font-semibold">Decorrenza precedente</div>
                              <div className="text-[13px] font-semibold text-slate-800" data-testid="suspension-prev-start">
                                {form.original_start_date ? formatDate(form.original_start_date) : "—"}
                              </div>
                            </div>
                            <div className="rounded-sm bg-white/70 border border-amber-200 px-2 py-1.5">
                              <div className="text-[10px] uppercase tracking-wide text-amber-800/80 font-semibold">Scadenza precedente</div>
                              <div className="text-[13px] font-semibold text-slate-800" data-testid="suspension-prev-end">
                                {form.original_end_date ? formatDate(form.original_end_date) : "—"}
                              </div>
                            </div>
                          </div>
                        )}
                        {showSuspensionHistory && Array.isArray(form.suspension_history) && form.suspension_history.length > 0 && (
                          <div className="border-t border-amber-200 pt-3 space-y-1.5" data-testid="suspension-history-list">
                            <div className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold">📜 Storico eventi</div>
                            <ul className="space-y-1.5 text-xs">
                              {form.suspension_history.map((h, i) => (
                                <li key={i} className="p-2 rounded border border-amber-200 bg-white">
                                  {h.kind === "extension" ? (
                                    <>
                                      🕐 <b>Proroga</b>: {formatDate(h.previous_deadline)} → <b>{formatDate(h.new_deadline)}</b>
                                      {h.reason && <> · <span className="text-slate-600">{h.reason}</span></>}
                                      {h.logged_at && <span className="text-slate-400 text-[10px] ml-2">({formatDate(h.logged_at)})</span>}
                                    </>
                                  ) : (
                                    <>
                                      ▶️ <b>Sospesa</b> {formatDate(h.suspension_start_date)} → <b>Riattivata</b> {formatDate(h.reactivation_date)}
                                      {" · "}
                                      <b>{h.days_suspended}gg</b> di fermo
                                      {" · "}
                                      scadenza {formatDate(h.old_end_date)} → <b>{formatDate(h.new_end_date)}</b>
                                      {h.reason && <> · <span className="text-slate-600">{h.reason}</span></>}
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    <div className={`sm:col-span-2 grid grid-cols-1 gap-3 ${(typeHasVehicleFields || form.show_vehicle_data) ? (categories.length > 0 ? "sm:grid-cols-3 lg:grid-cols-5" : "sm:grid-cols-2 lg:grid-cols-4") : (categories.length > 0 ? "sm:grid-cols-4" : "sm:grid-cols-3")}`}>
                      <div>
                        <Label>N° polizza</Label>
                        <Input
                          data-testid="policy-number"
                          value={form.policy_number}
                          onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                          className="bg-white"
                          autoFocus={focusPolicyNumber}
                          ref={(el) => {
                            if (el && focusPolicyNumber) {
                              setTimeout(() => { try { el.focus(); el.select(); } catch (_e) { /* noop */ } setFocusPolicyNumber(false); }, 150);
                            }
                          }}
                        />
                      </div>
                      <div>
                        <Label>Tipo</Label>
                        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                          <SelectTrigger data-testid="policy-type" className="bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent>{(policyTypes.length ? policyTypes.map((t) => ({ v: t.code, l: t.label })) : TYPES).map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                        </Select>
                        {!typeHasVehicleFields && !form.show_vehicle_data && (
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, show_vehicle_data: true })}
                            className="mt-1.5 text-[11px] text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
                            title="Aggiungi la sezione Veicolo assicurato anche se il tipo polizza non prevede campi veicolo"
                            data-testid="policy-show-vehicle"
                          >
                            <Car className="h-3 w-3" /> ➕ Aggiungi dati veicolo
                          </button>
                        )}
                      </div>
                      {categories.length > 0 && (
                        <div>
                          <Label>Categoria <span className="text-slate-400 font-normal">(facoltativa)</span></Label>
                          <Select
                            value={form.category_id || "__none__"}
                            onValueChange={(v) => {
                              const cat = categories.find((c) => c.id === v);
                              setForm({
                                ...form,
                                category_id: v === "__none__" ? "" : v,
                                type: cat?.suggested_type || form.type,
                              });
                            }}
                          >
                            <SelectTrigger data-testid="policy-category" className="bg-white"><SelectValue placeholder="— Nessuna —" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Nessuna —</SelectItem>
                              {categories.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.parent_id ? `↳ ${c.name}` : c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div>
                        <Label>Frazionamento</Label>
                        <Select value={form.frazionamento} onValueChange={(v) => setForm({ ...form, frazionamento: v })}>
                          <SelectTrigger data-testid="policy-frazionamento" className="bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent>{FRAZIONAMENTI.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {(typeHasVehicleFields || form.show_vehicle_data) && (
                        <div>
                          <Label>🪪 Tipo di guida</Label>
                          <Select
                            value={form.driving_type || "_none"}
                            onValueChange={(v) => setForm({ ...form, driving_type: v === "_none" ? "" : v })}
                          >
                            <SelectTrigger data-testid="policy-driving-type" className="bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">— Nessuno</SelectItem>
                              {drivingTypeOpts.map((o) => (
                                <SelectItem key={o.code} value={o.code}>{o.label || o.code}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    {/* 🗑️ 15/07/2026 — "Tipo di guida" spostato nella riga di N° polizza/Tipo/Categoria/Frazionamento. */}
                    {/* 🆕 15/07/2026 — Broker | Compagnia | Collaboratore assegnato tutti sulla stessa riga (nested grid 3 col) */}
                    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <Label className="text-amber-800 font-semibold">🤝 Broker / Agenzia (opz.)</Label>
                        {form.broker_id && (() => {
                          const b = brokers.find((x) => x.id === form.broker_id);
                          if (!b) return null;
                          const returnTo = editing?.id ? `&return_to_policy=${editing.id}` : "";
                          return (
                            <button
                              type="button"
                              onClick={() => navigate(`/brokers?expand=${form.broker_id}${returnTo}`)}
                              className="text-[10.5px] text-amber-700 hover:text-amber-900 hover:underline flex items-center gap-1"
                              title={`Apri la scheda di ${b.name} (potrai tornare a questa polizza)`}
                              data-testid="policy-broker-open-detail"
                            >
                              📂 Apri scheda <b>{b.name}</b> →
                            </button>
                          );
                        })()}
                      </div>
                      <Select value={form.broker_id || "none"} onValueChange={(v) => {
                        const brokerId = v === "none" ? "" : v;
                        // Reset compagnia quando cambia broker: se la compagnia corrente non è
                        // più collegata al nuovo broker, l'utente la riseleziona dall'elenco filtrato.
                        setForm((f) => {
                          const newCid = brokerId && f.company_id
                            ? (getCompaniesForBroker(brokerId).some((c) => c.id === f.company_id) ? f.company_id : "")
                            : f.company_id;
                          const co = newCid ? companies.find((c) => c.id === newCid) : null;
                          // 🆕 Feb 2026 — Se cambia broker E il campo `broker_management_fee` non è stato
                          // ancora personalizzato dall'utente, precompila dal default del broker selezionato.
                          const newBroker = brokerId ? (brokers.find((bx) => bx.id === brokerId) || null) : null;
                          const mgmtFeeDefault = newBroker?.broker_management_fee_default != null
                            ? String(newBroker.broker_management_fee_default) : "";
                          const currentMgmt = f.broker_management_fee ?? "";
                          const shouldOverride = !currentMgmt || currentMgmt === "" || currentMgmt === "0" || currentMgmt === "0.00";
                          return {
                            ...f,
                            broker_id: brokerId,
                            company_id: newCid,
                            company: co?.name || (newCid ? f.company : ""),
                            broker_management_fee: shouldOverride ? mgmtFeeDefault : currentMgmt,
                          };
                        });
                      }}>
                        <SelectTrigger data-testid="policy-broker" className="bg-amber-50 border-amber-300 focus:ring-amber-400"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuno</SelectItem>
                          {brokers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} ({b.type})</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {/* 🆕 Badge conteggio compagnie collegate al broker selezionato (cliccabile) */}
                      {form.broker_id && (() => {
                        const cnt = getLinkedCompaniesCount(form.broker_id);
                        const returnTo = editing?.id ? `&return_to_policy=${editing.id}` : "";
                        const onOpen = () => navigate(`/brokers?expand=${form.broker_id}${returnTo}`);
                        return cnt !== null ? (
                          <button
                            type="button"
                            onClick={onOpen}
                            className="mt-1 text-[10.5px] text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1"
                            title="Gestisci compagnie collegate a questo partner"
                            data-testid="policy-broker-links-count"
                          >
                            🔗 <span className="font-medium">{cnt}</span>&nbsp;{cnt === 1 ? "compagnia collegata" : "compagnie collegate"} · gestisci →
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={onOpen}
                            className="mt-1 text-[10.5px] text-amber-700 hover:text-amber-900 hover:underline flex items-center gap-1"
                            title="Configura le compagnie collegate a questo partner"
                            data-testid="policy-broker-links-count"
                          >
                            ⚠️ nessuna compagnia collegata · configura →
                          </button>
                        );
                      })()}
                    </div>
                    <div>
                      <Label className="text-amber-800 font-semibold">🏢 Compagnia</Label>
                      <Select value={form.company_id} onValueChange={onSelectCompany}>
                        <SelectTrigger data-testid="policy-company" className="bg-amber-50 border-amber-300 focus:ring-amber-400"><SelectValue placeholder="Seleziona compagnia" /></SelectTrigger>
                        <SelectContent>
                          {getCompaniesForBroker(form.broker_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-rose-800 font-semibold flex items-center gap-1.5">
                        🧑‍💼 Collaboratore assegnato
                        {form.agent_id && (() => {
                          const u = users.find((x) => x.id === form.agent_id);
                          const pct = parseFloat(u?.collab_commission_share_percent || 0);
                          return pct > 0 ? (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-rose-100 border border-rose-200 text-rose-700" title="Percentuale di retrocessione al collaboratore sulla quota agenzia">
                              retro {pct}%
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-500" title="Il collaboratore non ha una % di retrocessione configurata">
                              no retro
                            </span>
                          );
                        })()}
                      </Label>
                      <Select
                        value={form.agent_id || "none"}
                        onValueChange={(v) => setForm({ ...form, agent_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger data-testid="policy-agent" className="bg-rose-50/40 border-rose-200 focus:ring-rose-400">
                          <SelectValue placeholder="Nessun collaboratore assegnato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessun collaboratore</SelectItem>
                          {users
                            .filter((u) => u.role !== "super_admin")
                            .map((u) => {
                              const pct = parseFloat(u?.collab_commission_share_percent || 0);
                              const suffix = pct > 0 ? ` · ${pct}%` : "";
                              return (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name || u.email}{u.role ? ` (${u.role})` : ""}{suffix}
                                </SelectItem>
                              );
                            })}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-slate-500 mt-1">Se il collaboratore ha una <b>% di retrocessione</b> configurata, la sua quota compare automaticamente nel Riepilogo Provvigioni.</p>
                    </div>
                    </div>
                    {/* 🗑️ 15/07/2026 — "Compagnia" spostata accanto a Broker/Agenzia su richiesta utente. Collaboratore ora nella stessa riga. */}
                    {/* 📦 Prodotto (catalogo) — RIMOSSO 09/07/2026 su richiesta utente:
                        i prodotti ora vengono selezionati direttamente sulle singole Garanzie
                        (vedi PolicyCoveragesMatrix). Costo Acquisto/Vendita vengono trasferiti
                        sulla garanzia quando l'utente collega un prodotto. */}
                    {/* 🗑️ 15/07/2026 — "Frazionamento" spostato nella riga di N° polizza/Tipo/Categoria. */}
                    {/* Durata + Decorrenza + Scadenza sulla stessa riga per compattezza */}
                    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-[120px_1fr_1fr] gap-3">
                      <div>
                        <Label>⏱️ Durata <span className="text-xs text-slate-400 font-normal">(anni)</span></Label>
                        <Input
                          data-testid="policy-duration-years"
                          type="number"
                          step="0.5"
                          min="0.1"
                          value={form.contract_duration_years ?? ""}
                          onChange={(e) => setForm({ ...form, contract_duration_years: e.target.value === "" ? "" : Number(e.target.value) })}
                          placeholder="1"
                          className="bg-white text-center"
                        />
                      </div>
                      <div>
                        <Label>📅 Decorrenza (inizio)</Label>
                        <div className="grid grid-cols-[1fr,72px] gap-2">
                          <DatePicker data-testid="policy-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-white" />
                          <Input
                            data-testid="policy-start-time"
                            type="text"
                            inputMode="numeric"
                            maxLength={5}
                            pattern="^([01]?\d|2[0-4]):[0-5]\d$"
                            value={form.start_time || ""}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^\d:]/g, "");
                              if (v.length === 2 && !v.includes(":") && form.start_time?.length !== 3) v = v + ":";
                              if (v.length > 5) v = v.slice(0, 5);
                              setForm({ ...form, start_time: v });
                            }}
                            className="bg-white text-center font-mono tabular-nums px-1"
                            title="Orario di copertura decorrenza"
                            placeholder="24:00"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">Orario copertura — es. 24:00 o 13:30.</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <Label>📅 Scadenza <span className="text-xs text-slate-400 font-normal">(auto)</span></Label>
                          <button type="button" onClick={recalcEndDate} data-testid="policy-end-recalc"
                            className="text-[11px] font-medium text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1">
                            <RefreshCw className="h-3 w-3" /> Ricalcola
                          </button>
                        </div>
                        <div className="grid grid-cols-[1fr,72px] gap-2">
                          <DatePicker data-testid="policy-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-white" />
                          <Input
                            data-testid="policy-end-time"
                            type="text"
                            inputMode="numeric"
                            maxLength={5}
                            pattern="^([01]?\d|2[0-4]):[0-5]\d$"
                            value={form.end_time || ""}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^\d:]/g, "");
                              if (v.length === 2 && !v.includes(":") && form.end_time?.length !== 3) v = v + ":";
                              if (v.length > 5) v = v.slice(0, 5);
                              setForm({ ...form, end_time: v });
                            }}
                            className="bg-white text-center font-mono tabular-nums px-1"
                            title="Orario di copertura scadenza"
                            placeholder="24:00"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">Orario scadenza — di norma 24:00.</p>
                        {endRecalc && (
                          <div className="mt-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs flex flex-wrap items-center gap-2" data-testid="policy-end-recalc-confirm">
                            <span>Sostituire <b>{formatDate(form.end_date)}</b> con <b>{formatDate(endRecalc)}</b>?</span>
                            <span className="ml-auto flex gap-1.5">
                              <Button type="button" size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => setEndRecalc(null)} data-testid="policy-end-recalc-cancel">Annulla</Button>
                              <Button type="button" size="sm" className="h-6 text-[11px] px-2" onClick={() => { setForm((f) => ({ ...f, end_date: endRecalc })); setEndRecalc(null); }} data-testid="policy-end-recalc-apply">Sostituisci</Button>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 🆕 Classe di merito (CU) — visibile su polizze RC Auto/Moto (o quando il tipo abilita i campi veicolo) */}
                    {(typeHasVehicleFields || form.show_vehicle_data) && (
                      <div className="sm:col-span-2 rounded-lg border border-purple-200 bg-purple-50/50 p-3">
                        <Label className="text-purple-900 font-semibold text-sm">📊 Classe di merito (CU)</Label>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <Label className="text-xs">CU di provenienza</Label>
                            <Input
                              data-testid="policy-merit-class-from"
                              type="number"
                              min="0"
                              max="18"
                              step="1"
                              value={form.merit_class_from ?? ""}
                              onChange={(e) => setForm({ ...form, merit_class_from: e.target.value === "" ? null : Number(e.target.value) })}
                              placeholder="Es. 14"
                              className="bg-white"
                            />
                            <p className="text-[10px] text-slate-500 mt-0.5">Classe da attestato precedente (0-18).</p>
                          </div>
                          <div>
                            <Label className="text-xs">CU attuale</Label>
                            <Input
                              data-testid="policy-merit-class-current"
                              type="number"
                              min="0"
                              max="18"
                              step="1"
                              value={form.merit_class_current ?? ""}
                              onChange={(e) => setForm({ ...form, merit_class_current: e.target.value === "" ? null : Number(e.target.value) })}
                              placeholder="Es. 13"
                              className="bg-white"
                            />
                            <p className="text-[10px] text-slate-500 mt-0.5">Classe assegnata su questa polizza.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 🆕 Polizza sospesa — logica spostata in-line sotto lo Stato (vedi blocco "policy-suspension-block") */}

                    {/* 🆕 15/07/2026 — Gestione Garanzie (sostituisce "Importi polizza")
                        Le garanzie della polizza vengono aggiunte dal listino della compagnia.
                        Il "Totale Generale Premio Lordo" è la somma dei premi lordi delle garanzie ed è
                        sincronizzato su form.premium (per retrocompatibilità con provvigioni/ricevute). */}
                    {(() => {
                      // 🆕 15/07/2026 — Calcolo % split Provvigione / Fee Agenzia / Fee Collaboratore
                      // basato sugli accordi broker↔compagnia + retrocessione collaboratore.
                      const commissionPct = parseFloat(form.commission_percent) || 0;
                      const broker = form.broker_id ? brokers.find((b) => b.id === form.broker_id) : null;
                      const _linkSplit = broker && form.company_id
                        ? partnerCompanyLinks.find((l) => l.partner_id === broker.id && l.linked_company_id === form.company_id && l.active !== false)
                        : null;
                      const { prov: agencyPctOfCommission, fee: agencyPctOfFee } = brokerSplitPcts(broker, _linkSplit);
                      const collabUser = form.agent_id ? users.find((x) => x.id === form.agent_id) : null;
                      const collabPctOfAgency = parseFloat(collabUser?.collab_commission_share_percent || 0) || 0;
                      // 🆕 15/07/2026 — link_id per fetch override provvigione per garanzia
                      const linkForPolicy = broker && form.company_id
                        ? partnerCompanyLinks.find((l) => l.partner_id === broker.id && l.linked_company_id === form.company_id && l.active !== false)
                        : null;
                      return (
                        <PolicyGuaranteesEditor
                          companyId={form.company_id}
                          companyName={form.company || ""}
                          coverages={form.coverages || []}
                          canSeeMargins={canSeeMargins}
                          commissionPercent={commissionPct}
                          agencyPctOfCommission={agencyPctOfCommission}
                          agencyPctOfFee={agencyPctOfFee}
                          collabPctOfAgency={collabPctOfAgency}
                          collabName={collabUser?.name || collabUser?.email || ""}
                          products={products}
                          linkId={linkForPolicy?.id || null}
                          multiGuaranteeBonusPercent={parseFloat(broker?.multi_guarantee_bonus_percent) || 0}
                          onTotalsChange={setGuaranteeTotals}
                          excludeExtraFeesFromStatement={!!form.exclude_extra_fees_from_statement}
                          onExcludeExtraFeesFromStatementChange={(v) => setForm({ ...form, exclude_extra_fees_from_statement: v })}
                          excludeBrokerFixedFromStatement={!!form.exclude_broker_fixed_from_statement}
                          onExcludeBrokerFixedFromStatementChange={(v) => setForm({ ...form, exclude_broker_fixed_from_statement: v })}
                          onChange={(newCoverages) => {
                            const totLordo = (newCoverages || []).reduce((s, c) => s + (parseFloat(c.premium) || 0), 0);
                            const totNetto = (newCoverages || []).reduce((s, c) => s + (parseFloat(c.net_premium) || 0), 0);
                            // 🆕 16/07/2026 — FIX: le garanzie forniscono già taxable_base=Σ net_premium.
                            // NON impostare `_edit_source` per evitare che il useEffect tax-rate
                            // ricomputi taxable_base dal lordo (sovrascriverebbe con imposte/SSN
                            // globali di ramo, causando divergenza tra Provv. UI vs commission_amount salvato).
                            setForm({
                              ...form,
                              coverages: newCoverages,
                              premium: totLordo,
                              taxable_base: totNetto > 0 ? String(totNetto) : "",
                              _edit_source: null,
                            });
                          }}
                          testIdPrefix="policy"
                        />
                      );
                    })()}

                  </div>
                  {/* Sezione scadenze rate (calcolate da start_date + frazionamento + durata) */}
                  {form.start_date && form.frazionamento && (() => {
                    const FRAZ_MONTHS = { annuale: 12, semestrale: 6, quadrimestrale: 4, trimestrale: 3, mensile: 1 };
                    const months = FRAZ_MONTHS[form.frazionamento] || 12;
                    const computeInstallments = () => {
                      if (!form.start_date) return [];
                      const start = new Date(form.start_date);
                      let end;
                      if (form.end_date) end = new Date(form.end_date);
                      else { end = new Date(start); end.setUTCFullYear(end.getUTCFullYear() + 1); }
                      if (isNaN(start) || isNaN(end) || end <= start) return [];
                      const out = []; const cur = new Date(start);
                      for (let i = 0; i < 120; i++) {
                        if (cur >= end) break;
                        out.push(cur.toISOString().slice(0, 10));
                        cur.setUTCMonth(cur.getUTCMonth() + months);
                      }
                      return out;
                    };
                    const installments = computeInstallments();
                    const today = new Date().toISOString().slice(0, 10);
                    const paidDates = form.installment_paid_dates || {};
                    // La rata "attuale" è la PRIMA non ancora pagata (ordine cronologico),
                    // a prescindere dal fatto che la sua data di scadenza sia passata.
                    const currentInstallment = installments.find((d) => !paidDates[d]);
                    const statusOf = (d) => {
                      if (paidDates[d]) return { lbl: "pagata", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
                      if (d === currentInstallment) return { lbl: "attuale", cls: "bg-blue-100 text-blue-800 border-blue-300 font-semibold" };
                      if (d < today) return { lbl: "scaduta", cls: "bg-red-100 text-red-800 border-red-300" };
                      if (d === today) return { lbl: "oggi", cls: "bg-amber-100 text-amber-800 border-amber-300" };
                      return { lbl: "futura", cls: "bg-slate-100 text-slate-600 border-slate-300" };
                    };
                    return (
                      <>
                        {installments.length > 0 && (
                          <div className="pt-2 border-t border-blue-200">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setInstallmentsCollapsed((v) => !v)}
                                  data-testid="policy-installments-toggle"
                                  className="text-blue-900 hover:text-blue-700"
                                  title={installmentsCollapsed ? "Espandi scadenze rate" : "Comprimi scadenze rate"}
                                >
                                  <span className={`transition-transform inline-block text-xs ${installmentsCollapsed ? "" : "rotate-90"}`}>▶</span>
                                </button>
                                <Label className="text-[11px] text-blue-800 m-0">📌 Scadenze rate + importi (modificabili)</Label>
                              </div>
                              <span className="text-[10px] text-blue-700">{installments.length} rate · ogni {months} mesi</span>
                            </div>
                            {installmentsCollapsed ? null : (() => {
                              const totalPremium = Number(form.premium) || 0;
                              // 🆕 15/07/2026 (aggiornato) — Il "Totale Generale Premio Lordo" della Gestione Garanzie
                              // include: Σ premi + Σ Vendita + Σ Fee Broker (ripart.) + Σ Spese Gest. Broker + Σ Extra Fees.
                              // Il fallback delle rate deve corrispondere. Rispetta il toggle "Escludi Vendita dal totale".
                              const excludeSell = (() => {
                                try { return localStorage.getItem("policy_guarantees_exclude_sell_from_total") === "1"; } catch { return false; }
                              })();
                              const covs = form.coverages || [];
                              const commissionPct = parseFloat(form.commission_percent) || 0;
                              // 🆕 16/07/2026 — Ricava la quota agenzia dalla configurazione broker↔compagnia (stesso calcolo di PolicyGuaranteesEditor)
                              const _brokerForInst = form.broker_id ? brokers.find((b) => b.id === form.broker_id) : null;
                              const _linkInst = _brokerForInst && form.company_id
                                ? partnerCompanyLinks.find((l) => l.partner_id === _brokerForInst.id && l.linked_company_id === form.company_id && l.active !== false)
                                : null;
                              const agencyPctInst = brokerSplitPcts(_brokerForInst, _linkInst).fee;
                              const sumSellCost = excludeSell ? 0 : covs.reduce((s, c) => s + (parseFloat(c.sell_cost) || 0), 0);
                              const sumFeeBrokerSplit = covs.reduce((s, c) => s + (parseFloat(c.broker_fee_to_split) || 0), 0);
                              const sumBrokerMgmt = covs.reduce((s, c) => s + (parseFloat(c.broker_management_cost) || 0), 0);
                              const sumExtraFees = covs.reduce((s, c) => s + (parseFloat(c.extra_fees) || 0), 0);
                              // 🆕 16/07/2026 — Comp. Fisso Broker € (solo provvig.): 100% agenzia, va incluso nelle fee per rata
                              const sumBrokerFixed = covs.reduce((s, c) => s + (parseFloat(c.broker_fixed_fee) || 0), 0);
                              // 🐛 FIX 16/07/2026 — Fee agenzia = ESATTAMENTE la somma della colonna "Fee Agenzia €" per garanzia
                              // (Fee Broker rip. × agencyPct%) + Extra Fees (100% ag.) + Comp. Fisso Broker (100% ag.)
                              // ❌ ESCLUSA "Spese Gest. Broker" (100% broker) e "Vendita" (concettualmente separata)
                              // Preferisce i totali emessi dal PolicyGuaranteesEditor (che tengono conto degli override broker↔compagnia)
                              const sumAllFees = (guaranteeTotals?.totAgencyFromBrokerFee != null || guaranteeTotals?.totExtraFees != null || guaranteeTotals?.totBrokerFixed != null)
                                ? (Number(guaranteeTotals?.totAgencyFromBrokerFee) || 0) + (Number(guaranteeTotals?.totExtraFees) || 0) + (Number(guaranteeTotals?.totBrokerFixed) || 0)
                                : ((sumFeeBrokerSplit * (agencyPctInst || 100) / 100) + sumExtraFees + sumBrokerFixed);
                              const feePerInstallment = installments.length > 0 ? sumAllFees / installments.length : 0;
                              // 🆕 16/07/2026 — Vendita prodotti per rata = Σ margine (auto) ÷ N rate
                              const sumVenditaProdotti = Number(guaranteeTotals?.totMargin) || 0;
                              const venditaProdottiPerInstallment = installments.length > 0 ? sumVenditaProdotti / installments.length : 0;
                              const totalGross = totalPremium + sumSellCost + sumFeeBrokerSplit + sumBrokerMgmt + sumExtraFees;
                              const totalTaxable = Number(form.taxable_base) || 0;
                              const taxableRatio = totalGross > 0 && totalTaxable > 0 ? (totalTaxable / totalGross) : 0;
                              // 🆕 16/07/2026 — Σ Provv. Agenzia = usa i totali emessi dal PolicyGuaranteesEditor (include override broker↔compagnia).
                              // Fallback su calcolo locale se il figlio non ha ancora emesso i totali (primo render).
                              const sumProvvAgency = guaranteeTotals?.totFeeAg != null
                                ? Number(guaranteeTotals.totFeeAg) || 0
                                : covs.reduce((s, c) => {
                                    const netVal = parseFloat(c.net_premium) || 0;
                                    const provv = netVal * commissionPct / 100;
                                    return s + (provv * agencyPctInst / 100);
                                  }, 0);
                              // Diviso per N rate → provvigione agenzia costante per ogni rata (in base al frazionamento)
                              const provvAgencyPerInstallment = installments.length > 0 ? sumProvvAgency / installments.length : 0;
                              const defaultPerInstallment = installments.length > 0 ? Math.round((totalGross / installments.length) * 100) / 100 : 0;
                              const rawAmounts = form.installment_amounts || {};
                              // 🆕 15/07/2026 — Rilevamento "auto-default legacy": se tutti gli importi sono
                              // uguali e il loro totale coincide con form.premium (SENZA vendita), assumiamo
                              // che siano stati auto-generati da un vecchio "Reimposta ripartizione equa"
                              // (prima del feature Vendita) → li trattiamo come vuoti per abilitare il calcolo
                              // dinamico via fallback defaultPerInstallment (che ora include Σ Vendita).
                              const _amountValues = Object.values(rawAmounts).map((v) => Number(v)).filter((v) => Number.isFinite(v));
                              const _allEqual = _amountValues.length === installments.length && _amountValues.length > 0 && _amountValues.every((v) => Math.abs(v - _amountValues[0]) < 0.005);
                              const _extraSum = sumSellCost + sumFeeBrokerSplit + sumBrokerMgmt + sumExtraFees;
                              const _looksLikeAutoDefault = _allEqual && Math.abs(_amountValues[0] * installments.length - totalPremium) < 0.5 && _extraSum > 0;
                              const amounts = _looksLikeAutoDefault ? {} : rawAmounts;
                              const handleAmountChange = (date, value) => {
                                // Se il caso "auto-default" è stato rilevato, partiamo da un base pulito
                                // per evitare di "congelare" i valori legacy quando l'utente ne modifica uno solo.
                                const base = _looksLikeAutoDefault ? {} : rawAmounts;
                                const next = { ...base };
                                if (value === "" || value == null) delete next[date];
                                else next[date] = Number(value);
                                setForm({ ...form, installment_amounts: next });
                              };
                              const resetAll = () => {
                                // 🆕 15/07/2026 — Clear invece di stored default → il fallback dinamico kicks in
                                // e il Totale rate si aggiorna automaticamente ad ogni cambio di premio/vendita.
                                setForm({ ...form, installment_amounts: {} });
                              };
                              const sum = installments.reduce((s, d) => s + (Number(amounts[d] ?? defaultPerInstallment) || 0), 0);
                              const sumTaxable = taxableRatio > 0 ? sum * taxableRatio : 0;
                              const sumCommission = sumProvvAgency; // 🆕 16/07/2026 — Provvigione totale = Σ per garanzia (non più proporzionale)
                              const diff = Math.round((totalGross - sum) * 100) / 100;
                              return (
                                <>
                                  <div className="mb-2 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-[11px] text-indigo-900 flex items-start gap-1.5" data-testid="installments-info-note">
                                    <span className="text-sm leading-none mt-px">ℹ️</span>
                                    <span>
                                      Le <strong>date di pagamento effettivo</strong> di ogni rata fanno parte del <strong>periodo di calcolo per le provvigioni</strong> nell&apos;Estratto Conto. Solo le rate con una data di pagamento inclusa nel range selezionato vengono conteggiate.
                                    </span>
                                  </div>
                                  <div className="space-y-1.5" data-testid="policy-installments">
                                    {installments.map((d, idx) => {
                                      const s = statusOf(d);
                                      const amt = amounts[d] ?? defaultPerInstallment;
                                      const paidOn = paidDates[d] || "";
                                      const ordinals = ["Prima", "Seconda", "Terza", "Quarta", "Quinta", "Sesta", "Settima", "Ottava", "Nona", "Decima", "Undicesima", "Dodicesima"];
                                      const label = idx < ordinals.length ? `${ordinals[idx]} rata` : `Rata ${idx + 1}`;
                                      const handlePaidChange = (val) => {
                                        const next = { ...paidDates };
                                        if (val) next[d] = val; else delete next[d];
                                        // 🆕 28/07/2026 — Ricalcola paid_amount coerente con lo stato rate (stessa logica di "Salva incasso")
                                        const curAmounts = form.installment_amounts || {};
                                        const totalPaid = Object.entries(curAmounts)
                                          .filter(([k]) => next[k])
                                          .reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
                                        const lastPaidDate = Object.values(next).filter(Boolean).sort().slice(-1)[0] || null;
                                        const anyPaid = Object.values(next).some(Boolean);
                                        setForm({
                                          ...form,
                                          installment_paid_dates: next,
                                          paid_amount: Math.round(totalPaid * 100) / 100,
                                          paid_date: lastPaidDate,
                                          is_paid: anyPaid,
                                        });
                                      };
                                      const isReallyPaid = !!paidOn;
                                      const amtNum = typeof amt === "number" ? amt : parseFloat(amt);
                                      // 🆕 10/07/2026 — Rata con importo > 0 ma nessuna data di pagamento effettivo
                                      const missingPaidDate = Number.isFinite(amtNum) && amtNum > 0 && !isReallyPaid;
                                      return (
                                        <div key={d} className={`grid grid-cols-12 sm:grid-cols-[repeat(21,minmax(0,1fr))] gap-1 items-center bg-white border rounded-md px-1.5 py-1 ${isReallyPaid ? "border-emerald-300 bg-emerald-50/30" : missingPaidDate ? "border-rose-400 bg-rose-50/40" : "border-blue-200"}`} data-testid={`installment-${d}`}>
                                          <div className="col-span-12 sm:col-span-2 flex items-center gap-1.5">
                                            <span className="text-[11px] font-semibold text-blue-900">{label}</span>
                                            {missingPaidDate && (
                                              <span
                                                data-testid={`installment-missing-paid-date-${d}`}
                                                className="inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-rose-200 animate-pulse"
                                                title="Rata con importo compilato ma senza 'Data incasso rata': compila la data per tracciare l'incasso"
                                              />
                                            )}
                                            <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border ${s.cls}`}>
                                              {s.lbl}
                                            </span>
                                          </div>
                                          <div className="col-span-6 sm:col-span-3">
                                            <Label className="text-[11px] text-slate-500">Scadenza rata</Label>
                                            <div className="text-[12px] font-semibold tabular-nums text-slate-800">{new Date(d).toLocaleDateString("it-IT")}</div>
                                          </div>
                                          <div className="col-span-6 sm:col-span-3">
                                            <Label className="text-[11px] text-slate-500 flex items-center justify-end gap-1">
                                              Importo rata (€)
                                              {/* 🆕 16/07/2026 — Indicatore importo personalizzato (diverso dal default equamente distribuito) */}
                                              {(() => {
                                                const _hasCustom = rawAmounts[d] != null && rawAmounts[d] !== "";
                                                const _diff = _hasCustom && Math.abs(Number(rawAmounts[d]) - defaultPerInstallment) > 0.005;
                                                if (!_diff) return null;
                                                return (
                                                  <span
                                                    data-testid={`installment-custom-badge-${d}`}
                                                    className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-300"
                                                    title="Importo personalizzato · non influisce sul netto agenzia (che resta uniforme sul frazionamento)"
                                                  >
                                                    ✎ pers.
                                                  </span>
                                                );
                                              })()}
                                            </Label>
                                            <MoneyInput
                                              data-testid={`installment-amount-${d}`}
                                              value={amt}
                                              onChange={(v) => handleAmountChange(d, v)}
                                              className="w-full"
                                              inputClassName="h-6 text-[11px] text-right tabular-nums bg-blue-50 font-semibold text-blue-900 border-blue-200 focus:border-blue-500 focus:ring-blue-200"
                                              placeholder={formatAmount(defaultPerInstallment)}
                                            />
                                          </div>
                                          {/* 🗑️ 16/07/2026 — "Imponibile (calc.)" per rata rimosso su richiesta utente */}
                                          {/* 🆕 Provvigione per rata: Σ Provv. Agenzia per garanzia ÷ N rate (in base al frazionamento) */}
                                          <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10px] text-indigo-700 block text-right">Provvigione agenzia (€)</Label>
                                            <div
                                              data-testid={`installment-commission-${d}`}
                                              className="h-6 text-[11px] text-right tabular-nums font-medium text-indigo-800 bg-indigo-50/70 border border-indigo-200 rounded px-1.5 flex items-center justify-end"
                                              title={provvAgencyPerInstallment > 0
                                                ? `Σ Provv. Agenzia per garanzia (${formatAmount(sumProvvAgency)} €) ÷ ${installments.length} rate = ${formatAmount(provvAgencyPerInstallment)} € per rata`
                                                : "Nessuna provvigione agenzia calcolata (verifica commissione % e configurazione broker/compagnia)"}
                                            >
                                              {provvAgencyPerInstallment > 0 ? formatAmount(provvAgencyPerInstallment) : "—"}
                                            </div>
                                          </div>
                                          {/* 🆕 16/07/2026 — Fee agenzia per rata (Σ fee / N rate = frazionamento) */}
                                          <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10px] text-amber-700 block text-right">Fee agenzia (€)</Label>
                                            <div
                                              data-testid={`installment-fee-${d}`}
                                              className="h-6 text-[11px] text-right tabular-nums font-medium text-amber-800 bg-amber-50/70 border border-amber-200 rounded px-1.5 flex items-center justify-end"
                                              title={feePerInstallment > 0
                                                ? `Σ Fee Agenzia (Fee Broker rip. × agencyPct + Extra Fees + Comp. Fisso Broker) = ${formatAmount(sumAllFees)} € ÷ ${installments.length} rate = ${formatAmount(feePerInstallment)} € per rata`
                                                : "Nessuna fee configurata nella Gestione Garanzie"}
                                            >
                                              {feePerInstallment > 0 ? formatAmount(feePerInstallment) : "—"}
                                            </div>
                                          </div>
                                          {/* 🆕 16/07/2026 — Vendita prodotti per rata (Σ margine (auto) ÷ N rate) */}
                                          <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10px] text-purple-700 block text-right">Vendita prodotti (€)</Label>
                                            <div
                                              data-testid={`installment-vendita-prodotti-${d}`}
                                              className="h-6 text-[11px] text-right tabular-nums font-medium text-purple-800 bg-purple-50/70 border border-purple-200 rounded px-1.5 flex items-center justify-end"
                                              title={venditaProdottiPerInstallment > 0
                                                ? `Σ Margine (Vendita − Acquisto) sui prodotti dal catalogo = ${formatAmount(sumVenditaProdotti)} € ÷ ${installments.length} rate = ${formatAmount(venditaProdottiPerInstallment)} € per rata`
                                                : "Nessun margine (auto) calcolato — richiede prodotti dal Catalogo con Vendita e Acquisto valorizzati"}
                                            >
                                              {venditaProdottiPerInstallment > 0 ? formatAmount(venditaProdottiPerInstallment) : "—"}
                                            </div>
                                          </div>
                                          {/* 🆕 16/07/2026 — Totale spettanze agenzia per rata (Provv. Agenzia + Fee + Vendita prodotti) */}
                                          {(() => {
                                            const _netAgency = provvAgencyPerInstallment + feePerInstallment + venditaProdottiPerInstallment;
                                            return (
                                              <div className="col-span-6 sm:col-span-2">
                                                <Label className="text-[10px] text-emerald-700 block text-right">Totale spettanze agenzia (€)</Label>
                                                <div
                                                  data-testid={`installment-net-agency-${d}`}
                                                  className="h-6 text-[11px] text-right tabular-nums font-semibold text-emerald-800 bg-emerald-50/70 border border-emerald-200 rounded px-1.5 flex items-center justify-end"
                                                  title={_netAgency > 0
                                                    ? `Provv. Agenzia (${formatAmount(provvAgencyPerInstallment)} €) + Fee agenzia (${formatAmount(feePerInstallment)} €) + Vendita prodotti (${formatAmount(venditaProdottiPerInstallment)} €) = ${formatAmount(_netAgency)} € per rata`
                                                    : "Imposta provvigione, fee o margine prodotti per calcolare le spettanze"}
                                                >
                                                  {_netAgency > 0 ? formatAmount(_netAgency) : "—"}
                                                </div>
                                              </div>
                                            );
                                          })()}
                                          <div className="col-span-12 sm:col-span-5">
                                            <Label className={`text-[11px] ${missingPaidDate ? "text-rose-700 font-semibold" : "text-slate-500"}`}>
                                              Data incasso rata
                                              {missingPaidDate && <span className="ml-1">⚠️</span>}
                                            </Label>
                                            <div className="flex items-center gap-1">
                                              <DatePicker data-testid={`installment-paid-${d}`} value={paidOn}
                                                onChange={(e) => handlePaidChange(e.target.value)}
                                                className={`h-6 text-[11px] flex-1 min-w-0 ${isReallyPaid ? "bg-emerald-100 border-emerald-300 font-semibold text-emerald-900" : missingPaidDate ? "border-rose-400 bg-rose-50" : ""}`} />
                                              <button
                                                type="button"
                                                data-testid={`installment-pay-btn-${d}`}
                                                onClick={() => setInstallmentPayDialog({
                                                  date: d,
                                                  label,
                                                  amount: (form.installment_amounts && form.installment_amounts[d] != null) ? String(form.installment_amounts[d]) : String(defaultPerInstallment || ""),
                                                  paid_date: paidOn || new Date().toISOString().slice(0, 10),
                                                  method: (form.installment_paid_methods && form.installment_paid_methods[d]) || (form.paid_method || "contanti"),
                                                })}
                                                title="Registra incasso rata (importo, data, modalità di pagamento)"
                                                className={`shrink-0 h-6 w-6 inline-flex items-center justify-center rounded border transition-colors ${isReallyPaid ? "bg-emerald-600 border-emerald-700 text-white hover:bg-emerald-700" : "bg-emerald-100 border-emerald-300 text-emerald-800 hover:bg-emerald-200"}`}
                                              >
                                                <CreditCard className="h-3.5 w-3.5" />
                                              </button>
                                            </div>
                                            {(form.installment_paid_methods && form.installment_paid_methods[d]) && (
                                              <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-300 text-emerald-800 font-medium capitalize" data-testid={`installment-paid-method-${d}`}>
                                                💶 {form.installment_paid_methods[d]}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-blue-200 text-[11px]">
                                    <button
                                      type="button"
                                      data-testid="installments-reset"
                                      onClick={resetAll}
                                      className="text-blue-700 hover:text-blue-900 underline underline-offset-2"
                                      title="Ripartisci il premio equamente fra le rate"
                                    >
                                      🔄 Reimposta ripartizione equa
                                    </button>
                                    <div className="text-slate-700">
                                      Totale rate: <strong className="tabular-nums" data-testid="installments-total">{formatCurrency(sum)}</strong>
                                      {sumCommission > 0 && (
                                        <> · Tot. provvig.: <strong className="tabular-nums text-indigo-800" data-testid="installments-commission-total">{formatCurrency(sumCommission)}</strong></>
                                      )}
                                      {sumAllFees > 0 && (
                                        <> · Tot. fee agenzia: <strong className="tabular-nums text-amber-800" data-testid="installments-fee-total">{formatCurrency(sumAllFees)}</strong></>
                                      )}
                                      {sumVenditaProdotti > 0 && (
                                        <> · Tot. vendita prod.: <strong className="tabular-nums text-purple-800" data-testid="installments-vendita-total">{formatCurrency(sumVenditaProdotti)}</strong></>
                                      )}
                                      {(sumCommission + sumAllFees + sumVenditaProdotti) > 0 && (
                                        <> · Tot. spettanze agenzia: <strong className="tabular-nums text-emerald-800" data-testid="installments-net-agency-total">{formatCurrency(sumCommission + sumAllFees + sumVenditaProdotti)}</strong></>
                                      )}
                                      {Math.abs(diff) > 0.01 && (
                                        <span className={diff > 0 ? "ml-2 text-amber-700" : "ml-2 text-red-700"} title={diff > 0 ? "Manca rispetto al totale lordo (premio + vendita)" : "Eccede il totale lordo (premio + vendita)"}>
                                          ({diff > 0 ? "+" : ""}{formatAmount(diff)} € vs totale)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                {/* 🗑️ 15/07/2026 (aggiornato #2) — Sezione "💶 Polizza pagata / incassata" rimossa su richiesta utente. La registrazione dell'incasso avviene ora esclusivamente per singola rata (pulsante 💶 accanto ad ogni rata in "Rate di pagamento"). I campi legacy (is_paid, paid_amount, paid_date, paid_method) vengono sincronizzati automaticamente per backward-compat con Estratto Conto e ricevute PDF. */}


                {/* 🗑️ 15/07/2026 — Sezione "Coperture / Garanzie operanti" eliminata su richiesta utente.
                    Le garanzie si gestiscono ora dal listino per compagnia (Compagnie → 🛡️ Garanzie). */}

                {/* 🗑️ 15/07/2026 (aggiornato) — Sezione "💼 Provvigioni" rimossa su richiesta utente. Ora tutti i calcoli provvigioni/fee/collaboratore sono gestiti dalla nuova "Gestione Garanzie" (PolicyGuaranteesEditor). */}


                {/* Policy meta: concessione, subagency, rate — RIMOSSO su richiesta utente */}

                <div className="col-span-2">
                  <Label className="text-amber-800 font-semibold flex items-center gap-2 flex-wrap">
                    📝 Note
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 uppercase tracking-wide" title="Visibili solo agli operatori dell'agenzia — mai inviate al cliente né stampate nei preventivi/polizze/PDF">
                      🔒 Uso interno
                    </span>
                  </Label>
                  <Textarea
                    data-testid="policy-notes"
                    value={form.notes || ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className="bg-amber-50/30 border-amber-200 focus:ring-amber-400 mt-1"
                    placeholder="Annotazioni interne (accordi commerciali, comunicazioni cliente, sinistri pendenti, ecc.). NON verranno mai inviate al cliente né stampate nei preventivi/polizze."
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    🔒 Le note qui inserite sono <b>strettamente interne</b>: non compaiono nei PDF di preventivi/polizze, non vengono inviate via email al cliente o al broker.
                  </p>
                </div>

                <div className="col-span-2 mt-2 p-3 rounded-md bg-blue-50/50 border border-blue-100">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      data-testid="policy-notify-expiry"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      checked={form.notify_expiry_client !== false}
                      onChange={(e) => setForm({ ...form, notify_expiry_client: e.target.checked })}
                    />
                    <span className="text-sm">
                      <span className="font-medium text-slate-800">Avvisa il cliente della scadenza via email</span>
                      <span className="block text-xs text-slate-500 mt-0.5">Se disattivato, il cliente non riceverà i promemoria automatici a 30/15/7/3/1 giorni dalla scadenza. L&apos;agenzia continua a vederle nel CRM.</span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Drag & drop allegati polizza */}
              <div className="mt-4">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  📎 Allegati polizza (drag &amp; drop)
                </Label>
                <DropZone
                  testid="policy-attachments-dropzone"
                  onFiles={(files) => handleFilesAdd(files)}
                  accept={["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/heic", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"]}
                  className="border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-lg p-4 bg-slate-50/50 cursor-pointer transition-colors"
                >
                  <div className="text-center text-sm text-slate-600">
                    Trascina qui PDF/immagini/documenti, oppure
                    <label className="inline-block ml-1 text-blue-600 hover:underline cursor-pointer font-medium">
                      sfoglia
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx"
                        className="hidden"
                        data-testid="policy-attachments-browse"
                        onChange={(e) => {
                          handleFilesAdd(Array.from(e.target.files || []));
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                  <p className="text-[11px] text-slate-400 text-center mt-1">
                    {editing?.id
                      ? "I file vengono caricati immediatamente, prima ancora di salvare la polizza."
                      : "I file verranno caricati automaticamente dopo il salvataggio della polizza."}
                  </p>
                  <div className="text-center mt-2">
                    <ScanButton
                      testid="scan-policy-pending"
                      label="🖨️ Importa da scanner"
                      variant="outline"
                      onFile={(f) => handleFilesAdd([f])}
                    />
<CameraButton testid="cam-scan-policy-pending" label="📷 Foto da fotocamera" variant="outline"  onFile={(f) => handleFilesAdd([f])} />
                  </div>
                </DropZone>
                {pendingFiles.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid="policy-pending-files">
                    {pendingFiles.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1">
                        <span className="truncate flex-1">📄 {f.name} <span className="text-slate-500">({formatFileSize(f.size)})</span></span>
                        <button
                          type="button"
                          onClick={() => setPendingFiles((arr) => arr.filter((_, j) => j !== i))}
                          className="text-rose-600 hover:text-rose-800 ml-2"
                          aria-label="Rimuovi"
                        >×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* 🆕 Allegati polizza inline — visibili solo in modalità modifica */}
              {editing?.id && (
                <div className="mt-4 border-t border-slate-200 pt-4" data-testid="edit-attachments-inline">
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-slate-800">Allegati polizza</span>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800">{editAttachments.length}</span>
                    <button
                      type="button"
                      onClick={() => { setAttachOpen(editing); loadAttachments(editing.id); loadSendLogs(editing.id); setSelectedForEmail({}); }}
                      className="ml-auto text-[11px] text-blue-700 hover:text-blue-900 hover:underline"
                      data-testid="edit-attachments-open-full"
                    >
                      Apri pannello completo →
                    </button>
                  </div>
                  {editAttachments.length === 0 ? (
                    <p className="text-xs text-slate-400">Nessun allegato. Usa il pannello completo per caricare file.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {editAttachments.map((a) => (
                        <AttachmentItem
                          key={a.id}
                          att={a}
                          onDownload={downloadAttachment}
                          onPreview={previewPolicyAttachment}
                          onDelete={async (att) => {
                            await removeAttachment(att);
                            loadEditAttachments(editing.id);
                          }}
                          showClientVisibilityToggle={true}
                          onRequestSignature={requestSignatureForAttachment}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={closePolicyDialog}>Annulla</Button>
                {editing?.id && canWrite("policies") && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => emettiRicevutaPolizza(editing)}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="policy-dialog-crea-ricevuta"
                    title="Crea una ricevuta con il Premio assicurativo precompilato — le altre voci restano manuali"
                  >
                    🧾 Crea Ricevuta
                  </Button>
                )}
                <Button data-testid="save-policy-btn" onClick={submit} disabled={!form.client_id} className="bg-[#0F172A] hover:bg-[#1E293B]">
                  Salva{pendingFiles.length > 0 ? ` + ${pendingFiles.length} allegat${pendingFiles.length === 1 ? "o" : "i"}` : ""}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap print:hidden" data-testid="policies-filters">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Input
            data-testid="policies-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per n° polizza, cliente, cod. fiscale, compagnia, targa…"
            className="pl-9 pr-9 text-sm"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" /></svg>
          {search && (
            <button
              type="button"
              data-testid="policies-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-800 transition-colors"
              title="Cancella ricerca"
              aria-label="Cancella ricerca"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          )}
        </div>
        {(currentUser?.role === "agente" || currentUser?.role === "admin") && (
          <button
            data-testid="filter-mine"
            onClick={() => setMine((v) => !v)}
            className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all ${
              mine ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <UserCircle className="h-3 w-3" /> {mine ? "Le mie" : "Tutte"}
          </button>
        )}
        <button
          data-testid="toggle-archive"
          onClick={() => setShowArchive((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all ${
            showArchive ? "border-amber-600 bg-amber-50 text-amber-700" : "border-slate-200 hover:border-slate-300"
          }`}
        >
          📦 {showArchive ? "Archivio (disdette)" : "Polizze attive"}
        </button>
        {categories.length > 0 && (
          <select
            data-testid="filter-category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 cursor-pointer bg-white"
          >
            <option value="all">Tutte le categorie</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.parent_id ? `↳ ${c.name}` : c.name}</option>
            ))}
          </select>
        )}
        {companies.length > 0 && (
          <select
            data-testid="filter-company"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 cursor-pointer bg-white max-w-[180px]"
          >
            <option value="all">Tutte le compagnie</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        {brokers.length > 0 && (
          <select
            data-testid="filter-broker"
            value={brokerFilter}
            onChange={(e) => setBrokerFilter(e.target.value)}
            className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 cursor-pointer bg-white max-w-[200px]"
          >
            <option value="all">Tutti broker/agenzie</option>
            <option value="__none__">— Senza broker —</option>
            {brokers.map((b) => (
              <option key={b.id} value={b.id}>{b.name} ({b.type})</option>
            ))}
          </select>
        )}
        {["all", ...STATUS].map((s) => {
          const isArchiveStatus = s === "disdetta" || s === "sostituita";
          return (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => {
                // 🆕 28/07/2026 — I pulsanti DISDETTA e SOSTITUITA richiedono l'archivio abilitato:
                // il backend `GET /api/policies` esclude questi status quando archived=false, quindi
                // senza attivare showArchive la lista resterebbe vuota. Attivo automaticamente
                // showArchive (che ri-triggera il fetch con archived=true).
                if (isArchiveStatus && !showArchive) {
                  setShowArchive(true);
                }
                setFilter(s);
              }}
              className={`text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all inline-flex items-center gap-1.5 ${
                filter === s ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <span>{s === "all" ? "Tutte" : s.replace("_", " ")}</span>
              {isArchiveStatus && (
                <span
                  className={`text-[9px] font-semibold uppercase tracking-tight rounded px-1 py-px border ${
                    filter === s
                      ? "bg-white/15 text-white border-white/30"
                      : "bg-amber-50 text-amber-800 border-amber-200"
                  }`}
                  title="Cliccando qui viene attivata la modalità archivio (mostra polizze disdette/sostituite)"
                >
                  📦 archivio
                </span>
              )}
            </button>
          );
        })}
        {/* Filtro rapido revisioni da gestire */}
        <button
          type="button"
          data-testid="filter-revisione"
          onClick={() => setRevisioneFilter((v) => !v)}
          disabled={revisioneCount === 0 && !revisioneFilter}
          className={`text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 ${
            revisioneFilter
              ? "border-amber-600 bg-amber-500 text-white shadow"
              : revisioneCount > 0
                ? "border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-400 hover:bg-amber-100"
                : "border-slate-200 text-slate-400 cursor-not-allowed"
          }`}
          title={revisioneCount > 0 ? `${revisioneCount} polizze auto con revisione da gestire` : "Tutte le revisioni sono ok"}
        >
          <span>🔧</span>
          <span>Revisioni da gestire</span>
          {revisioneCount > 0 && (
            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none ${revisioneFilter ? "bg-white text-amber-700" : "bg-amber-600 text-white"}`}>
              {revisioneCount}
            </span>
          )}
        </button>
        {/* Filtro rapido polizze senza garanzie */}
        <button
          type="button"
          data-testid="filter-no-coverages"
          onClick={() => setNoCoveragesFilter((v) => !v)}
          disabled={noCoveragesCount === 0 && !noCoveragesFilter}
          className={`text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 ${
            noCoveragesFilter
              ? "border-rose-600 bg-rose-500 text-white shadow"
              : noCoveragesCount > 0
                ? "border-rose-300 bg-rose-50 text-rose-800 hover:border-rose-400 hover:bg-rose-100"
                : "border-slate-200 text-slate-400 cursor-not-allowed"
          }`}
          title={noCoveragesCount > 0 ? `${noCoveragesCount} polizze senza garanzie da completare` : "Tutte le polizze hanno almeno una garanzia"}
        >
          <span>🚨</span>
          <span>Senza garanzie</span>
          {noCoveragesCount > 0 && (
            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none ${noCoveragesFilter ? "bg-white text-rose-700" : "bg-rose-600 text-white"}`}>
              {noCoveragesCount}
            </span>
          )}
        </button>
        {/* 🆕 Filtro rapido polizze da completare (offerta pagata, in_emissione) */}
        <button
          type="button"
          data-testid="filter-to-complete"
          onClick={() => setToCompleteFilter((v) => !v)}
          disabled={toCompleteCount === 0 && !toCompleteFilter}
          className={`text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 ${
            toCompleteFilter
              ? "border-blue-600 bg-blue-500 text-white shadow"
              : toCompleteCount > 0
                ? "border-blue-300 bg-blue-50 text-blue-800 hover:border-blue-400 hover:bg-blue-100"
                : "border-slate-200 text-slate-400 cursor-not-allowed"
          }`}
          title={toCompleteCount > 0 ? `${toCompleteCount} polizze auto-create da offerte pagate, da completare con i dati definitivi` : "Nessuna polizza in attesa di completamento"}
        >
          <span>🚀</span>
          <span>Da completare</span>
          {toCompleteCount > 0 && (
            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none ${toCompleteFilter ? "bg-white text-blue-700" : "bg-blue-600 text-white"}`}>
              {toCompleteCount}
            </span>
          )}
        </button>
        {/* 🆕 Feb 2026 — Filtri polizze multi-veicolo */}
        <div className="inline-flex items-stretch rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="filter-multi-vehicle-group">
          {[
            { v: "all", l: "Tutte", cls: "text-slate-700 hover:bg-slate-50" },
            { v: "multi", l: "🚗 Multi-veicolo", cls: "text-sky-800 hover:bg-sky-50" },
            { v: "flotta", l: "🚛 Flotte", cls: "text-purple-800 hover:bg-purple-50" },
            { v: "storica", l: "🕰️ Storiche", cls: "text-amber-800 hover:bg-amber-50" },
          ].map((opt, i) => {
            const active = multiVehFilter === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                data-testid={`filter-multi-${opt.v}`}
                onClick={() => setMultiVehFilter(opt.v)}
                className={`text-xs uppercase tracking-wide px-3 py-1.5 border-r border-slate-200 last:border-r-0 transition-colors ${
                  active
                    ? (opt.v === "flotta" ? "bg-purple-600 text-white" : opt.v === "storica" ? "bg-amber-600 text-white" : opt.v === "multi" ? "bg-sky-600 text-white" : "bg-slate-700 text-white")
                    : opt.cls
                }`}
              >
                {opt.l}
              </button>
            );
          })}
        </div>
        {/* 🆕 Filtri "anomalie" attivi (visibili solo quando applicati dalla Dashboard) */}
        {noCompanyFilter && (
          <button
            type="button"
            data-testid="filter-no-company-clear"
            onClick={() => setNoCompanyFilter(false)}
            className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 border-rose-600 bg-rose-500 text-white shadow hover:bg-rose-600"
            title="Rimuovi filtro anomalia — Polizze senza compagnia"
          >
            <span>🚫</span>
            <span>Senza compagnia</span>
            <span className="text-[11px] leading-none opacity-80">✕</span>
          </button>
        )}
        {noAttachmentsFilter && (
          <button
            type="button"
            data-testid="filter-no-attachments-clear"
            onClick={() => setNoAttachmentsFilter(false)}
            className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 border-amber-600 bg-amber-500 text-white shadow hover:bg-amber-600"
            title="Rimuovi filtro anomalia — Polizze senza allegati"
          >
            <span>📎</span>
            <span>Senza allegati</span>
            <span className="text-[11px] leading-none opacity-80">✕</span>
          </button>
        )}
        {expiringWithinDays != null && (
          <button
            type="button"
            data-testid="filter-expiring-within-clear"
            onClick={() => setExpiringWithinDays(null)}
            className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 border-orange-600 bg-orange-500 text-white shadow hover:bg-orange-600"
            title={`Rimuovi filtro — Scadenza entro ${expiringWithinDays} giorni`}
          >
            <span>⏰</span>
            <span>Entro {expiringWithinDays}gg</span>
            <span className="text-[11px] leading-none opacity-80">✕</span>
          </button>
        )}
      </div>

      <div className="hidden print:block mb-6">
        <h1 className="font-display text-2xl font-semibold">Portafoglio Polizze — Assicura</h1>
        <p className="text-xs text-slate-500">{new Date().toLocaleDateString("it-IT")}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
        {isAdmin && selectedIds.size > 0 && (
          <div
            data-testid="bulk-selection-bar"
            className="flex items-center justify-between gap-3 px-4 py-2.5 bg-rose-50 border-b border-rose-200 text-sm"
          >
            <div className="flex items-center gap-3">
              <span className="font-semibold text-rose-900" data-testid="bulk-selected-count">
                {selectedIds.size} {selectedIds.size === 1 ? "polizza selezionata" : "polizze selezionate"}
              </span>
              <button
                onClick={clearSelection}
                className="text-xs text-rose-700 hover:underline"
                data-testid="bulk-clear-selection"
              >
                Annulla selezione
              </button>
            </div>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setBulkStage("confirm")}
              data-testid="bulk-delete-trigger"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Elimina selezionate ({selectedIds.size})
            </Button>
          </div>
        )}
        <div className="hidden md:block">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-amber-500 text-white text-xs uppercase tracking-wide">
            <tr>
              {isAdmin && (
                <th className="px-2 py-2 w-10 print:hidden">
                  <input
                    type="checkbox"
                    data-testid="bulk-select-all"
                    aria-label="Seleziona tutte le polizze visibili"
                    checked={filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id))}
                    onChange={(e) => selectAllVisible(filtered, e.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-rose-600"
                  />
                </th>
              )}
              {[
                { k: "client_name", l: "Cliente" },
                { k: "vehicle_plate", l: "Targa" },
                { k: "type_vehicle", l: "Tipo / Veicolo" },
                { k: "company", l: "Compagnia" },
                { k: "broker", l: "Agenzia / Broker" },
                { k: "end_date", l: "Scadenza" },
                { k: "created_at", l: "Inserita il" },
                { k: "status", l: "Stato" },
              ].map((col) => (
                <th
                  key={col.k}
                  onClick={() => onSort(col.k)}
                  data-testid={`sort-${col.k}`}
                  className="text-left px-2 py-2 cursor-pointer select-none hover:text-slate-900 transition-colors"
                  title={`Ordina per ${col.l}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.l}
                    {sortKey === col.k ? (
                      <span className="text-blue-600 text-[10px]">{sortDir === "asc" ? "▲" : "▼"}</span>
                    ) : (
                      <span className="text-slate-300 text-[10px]">↕</span>
                    )}
                  </span>
                </th>
              ))}
              <th className="text-right px-2 py-2 print:hidden sticky right-0 bg-amber-500 z-10">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="policies-table">
            {sorted.length === 0 && <tr><td colSpan={isAdmin ? 11 : 10} className="text-center py-10 text-slate-400">Nessuna polizza.</td></tr>}
            {sorted.map((p) => {
              const brokerObj = p.broker_id ? brokers.find((b) => b.id === p.broker_id) : null;
              const brokerName = brokerObj?.name || "—";
              const brokerColor = brokerObj?.color || null;
              const assignedUser = p.agent_id ? users.find((u) => u.id === p.agent_id) : null;
              const isSelected = selectedIds.has(p.id);
              return (
              <tr
                key={p.id}
                id={`policy-row-${p.id}`}
                className={`border-t border-slate-100 hover:bg-slate-50 transition-shadow duration-500 ${isSelected ? "bg-rose-50/40" : ""} ${flashId === p.id ? "ring-4 ring-emerald-400 ring-inset bg-emerald-50/40" : ""}`}
              >
                {isAdmin && (
                  <td className="px-2 py-2 print:hidden">
                    <input
                      type="checkbox"
                      data-testid={`bulk-select-${p.id}`}
                      aria-label={`Seleziona polizza ${p.policy_number}`}
                      checked={isSelected}
                      onChange={() => toggleSelect(p.id)}
                      className="h-4 w-4 cursor-pointer accent-rose-600"
                    />
                  </td>
                )}
                <td className="px-2 py-2">
                  {(() => {
                    const c = clients.find((x) => x.id === p.client_id);
                    const hasPortal = !!(c && c.portal_enabled);
                    return (
                      <>
                        <div
                          className="font-semibold text-slate-900 text-base flex items-center gap-1.5 cursor-pointer hover:text-blue-700 select-none uppercase"
                          onDoubleClick={() => openEdit(p)}
                          data-testid={`policy-client-name-${p.id}`}
                          title="Doppio click per modificare la polizza"
                        >
                          {p.client_name || "—"}
                          {/* 🆕 09/07/2026 — Indicatore Area Privata cliente */}
                          <span
                            data-testid={`policy-client-portal-${p.id}`}
                            title={hasPortal ? "Area privata cliente ATTIVA" : "Area privata cliente NON attiva"}
                            className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] leading-none font-bold border ${
                              hasPortal
                                ? "bg-emerald-100 border-emerald-300 text-emerald-700"
                                : "bg-slate-100 border-slate-300 text-slate-400"
                            }`}
                          >
                            {hasPortal ? "🔓" : "🔒"}
                          </span>
                          {/* 🆕 Feb 2026 — Indicatore polizza visibile in area privata condivisa (family) */}
                          {sharedClientIds.has(p.client_id) && (
                            <span
                              data-testid={`policy-shared-portal-${p.id}`}
                              title="Polizza visibile anche nell'area privata di un altro cliente (famiglia)"
                              className="inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] leading-none font-bold border bg-violet-100 border-violet-300 text-violet-700"
                            >
                              🔗
                            </span>
                          )}
                          <NotesHoverIcon notes={p.notes} testid={`policy-notes-icon-${p.id}`} />
                        </div>
                        {/* 🆕 16/07/2026 — Codice fiscale sotto il nominativo (era in colonna separata) */}
                        {/* 🆕 CF cliccabile per copia negli appunti */}
                        {p.client_fiscal_code && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(String(p.client_fiscal_code || "").toUpperCase(), "Codice fiscale");
                            }}
                            className="inline-flex items-center gap-1 text-xs font-mono text-slate-500 hover:text-blue-600 hover:bg-blue-50 uppercase tracking-wide mt-0.5 px-1 -mx-1 rounded transition-colors cursor-pointer"
                            data-testid={`policy-client-cf-${p.id}`}
                            title="Clicca per copiare il codice fiscale negli appunti"
                          >
                            🆔 {p.client_fiscal_code}
                          </button>
                        )}
                        {c && (
                          <div className="text-xs text-slate-500 mt-0.5 truncate max-w-[280px]" data-testid={`policy-client-contacts-${p.id}`}>
                            {c.email ? (
                              <span className="inline-flex items-center gap-0.5">
                                <a
                                  href={`mailto:${c.email}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-blue-600 hover:underline"
                                  title={`Scrivi a ${c.email}`}
                                  data-testid={`policy-client-email-${p.id}`}
                                >
                                  ✉ {c.email}
                                </a>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); copyToClipboard(c.email, "Email"); }}
                                  className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded px-0.5 cursor-pointer"
                                  title="Copia email negli appunti"
                                  data-testid={`policy-client-email-copy-${p.id}`}
                                >
                                  📋
                                </button>
                              </span>
                            ) : null}
                            {c.email && (c.mobile || c.phone) ? <span className="mx-1 text-slate-300">·</span> : null}
                            {(c.mobile || c.phone) ? (
                              <span className="inline-flex items-center gap-0.5">
                                <a
                                  href={`tel:${c.mobile || c.phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-blue-600 hover:underline"
                                  title="Chiama"
                                  data-testid={`policy-client-phone-${p.id}`}
                                >
                                  📱 {c.mobile || c.phone}
                                </a>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); copyToClipboard(c.mobile || c.phone, "Telefono"); }}
                                  className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded px-0.5 cursor-pointer"
                                  title="Copia telefono negli appunti"
                                  data-testid={`policy-client-phone-copy-${p.id}`}
                                >
                                  📋
                                </button>
                              </span>
                            ) : null}
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div className="text-xs font-mono font-semibold text-slate-700 mt-1 flex items-center gap-1 flex-wrap" data-testid={`policy-number-${p.id}`}>
                    {p.policy_number && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(p.policy_number, "N° polizza"); }}
                        className="hover:text-blue-600 hover:bg-blue-50 rounded px-1 -mx-1 cursor-pointer inline-flex items-center gap-1"
                        title="Clicca per copiare il numero di polizza negli appunti"
                        data-testid={`policy-number-copy-${p.id}`}
                      >
                        {p.policy_number}
                      </button>
                    )}
                    {/* 🆕 16/07/2026 — Badge 🎠 catena versioni (Rinnovo/Sostituzione/da Offerta) */}
                    {typeof p.chain_size === "number" && p.chain_size > 1 && (
                      <span
                        data-testid={`policy-chain-badge-${p.id}`}
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-tight bg-amber-100 text-amber-800 border border-amber-300 rounded px-1 py-px leading-tight"
                        title={`Questa polizza è parte di una catena di ${p.chain_size} versioni (Rinnovi/Sostituzioni). Aprila per vedere lo storico.`}
                      >
                        🎠 {p.chain_size}
                      </span>
                    )}
                    {p.source_quote_number && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href="/quotes"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`policy-source-quote-${p.id}`}
                              className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-tight bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-0.5 py-px hover:bg-emerald-200 cursor-help leading-tight"
                            >
                              📋 {p.source_quote_number}
                              {typeof p.source_quote_days_to_emit === "number" && (
                                <span className="ml-0.5 bg-emerald-700 text-white rounded-full px-1 leading-tight text-[7px]">{p.source_quote_days_to_emit}gg</span>
                              )}
                            </a>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            align="start"
                            className="bg-white text-slate-900 border border-emerald-200 shadow-xl p-2.5 max-w-[16rem] z-[100]"
                          >
                            <div className="text-[10px] uppercase font-semibold text-emerald-700 mb-1">Polizza da preventivo</div>
                            <div className="text-xs space-y-0.5">
                              <div><span className="text-slate-500">Numero:</span> <span className="font-mono font-semibold">{p.source_quote_number}</span></div>
                              {p.source_quote_type && <div><span className="text-slate-500">Tipo:</span> <span className="font-medium capitalize">{p.source_quote_type}</span></div>}
                              {p.source_quote_created_at && (
                                <div><span className="text-slate-500">Richiesta:</span> {formatDate(p.source_quote_created_at)}</div>
                              )}
                              {typeof p.source_quote_days_to_emit === "number" && (
                                <div className="text-emerald-700 font-semibold pt-1 mt-1 border-t border-emerald-100">
                                  ⏱ {p.source_quote_days_to_emit === 0 ? "Stesso giorno" : `${p.source_quote_days_to_emit} ${p.source_quote_days_to_emit === 1 ? "giorno" : "giorni"} dalla richiesta`}
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {p.renewed_from_policy_id && (() => {
                      // 🆕 Feb 2026 — Badge dinamico secondo il tipo di operazione di catena
                      const lt = p.link_type;
                      const cfg = lt === "replacement"
                        ? { label: "Sostituzione", icon: "🔄", cls: "bg-blue-100 text-blue-800 border-blue-300", tooltip: "Polizza sostituita da una precedente" }
                        : lt === "from_quote_offer"
                          ? { label: "Da Offerta", icon: "📄", cls: "bg-violet-100 text-violet-800 border-violet-300", tooltip: "Polizza generata da un'offerta finale" }
                          : { label: "Rinnovo", icon: null, cls: "bg-amber-100 text-amber-800 border-amber-300", tooltip: "Polizza rinnovata da una precedente" };
                      return (
                        <span
                          data-testid={`renewed-from-badge-${p.id}`}
                          title={cfg.tooltip}
                          className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide border rounded px-1 py-0.5 ${cfg.cls}`}
                        >
                          {cfg.icon ? <span className="text-[10px] leading-none">{cfg.icon}</span> : <RefreshCw className="h-2.5 w-2.5" />}
                          {cfg.label}
                        </span>
                      );
                    })()}
                    {p.renewed_to_policy_id && (
                      <span
                        data-testid={`renewed-to-badge-${p.id}`}
                        title="Polizza già rinnovata"
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide bg-slate-200 text-slate-700 border border-slate-300 rounded px-1 py-0.5"
                      >
                        🗃️ Archiviata
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2" data-testid={`policy-plate-cell-${p.id}`}>
                  {p.vehicle_plate ? (
                    <div className="flex flex-col items-start gap-1">
                      <button
                        type="button"
                        data-testid={`policy-plate-${p.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(p.vehicle_plate.toUpperCase(), "Targa");
                        }}
                        className="inline-flex items-center gap-1.5 font-mono text-base font-extrabold tracking-wider bg-slate-300 hover:bg-slate-200 text-black px-3 py-1.5 rounded-md border-2 border-slate-500 hover:border-blue-500 uppercase shadow-sm transition-colors cursor-pointer"
                        title="Clicca per copiare la targa negli appunti"
                      >
                        {p.type === "moto" ? <Bike className="h-5 w-5 text-black" /> : <Car className="h-5 w-5 text-black" />}
                        {p.vehicle_plate.toUpperCase()}
                      </button>
                      {/* 🆕 Feb 2026 — Badge multi-veicolo + classificazione flotta/storica */}
                      {(() => {
                        const nVeh = Array.isArray(p.vehicles) ? p.vehicles.length : 0;
                        if (nVeh < 2) return null;
                        const kind = p.multi_vehicle_kind;
                        const kindLabel = kind === "flotta"
                          ? { emoji: "🚛", text: "Flotta", cls: "bg-purple-100 text-purple-800 border-purple-300" }
                          : kind === "storica_cumulativa"
                          ? { emoji: "🕰️", text: "Storica", cls: "bg-amber-100 text-amber-800 border-amber-300" }
                          : { emoji: "🚗", text: `${nVeh} veicoli`, cls: "bg-sky-100 text-sky-800 border-sky-300" };
                        return (
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${kindLabel.cls}`}
                            title={`Polizza multi-veicolo (${nVeh} veicoli)${kind ? ` — tipo: ${kindLabel.text}` : ""}`}
                            data-testid={`policy-multi-badge-${p.id}`}
                          >
                            {kindLabel.emoji} {kindLabel.text}{kind && ` (${nVeh})`}
                          </span>
                        );
                      })()}
                      {/* Badge revisione: mancante / scaduta / in scadenza — compatto e leggibile */}
                      {p.type === "auto" && (() => {
                        const today = new Date(); today.setHours(0, 0, 0, 0);
                        const rev = p.next_review_date ? new Date(p.next_review_date) : null;
                        if (rev) rev.setHours(0, 0, 0, 0);
                        const days = rev ? Math.round((rev - today) / (1000 * 60 * 60 * 24)) : null;
                        let style = null;
                        if (!rev) {
                          style = { cls: "bg-amber-100 text-amber-800 border-amber-400 hover:bg-amber-200", icon: "⚠", label: "Rev. mancante", tip: "Revisione mancante — clicca per verificare sul Portale dell'Automobilista" };
                        } else if (days < 0) {
                          style = { cls: "bg-rose-100 text-rose-800 border-rose-400 hover:bg-rose-200", icon: "🚨", label: `Rev. −${Math.abs(days)}gg`, tip: `Revisione scaduta da ${Math.abs(days)} giorni` };
                        } else if (days <= 30) {
                          style = { cls: "bg-orange-100 text-orange-800 border-orange-400 hover:bg-orange-200", icon: "🔧", label: `Rev. ${days}gg`, tip: `Revisione in scadenza tra ${days} giorni` };
                        }
                        if (!style) {
                          return (
                            <span className="text-[10px] text-slate-500" data-testid={`policy-rev-ok-${p.id}`}>
                              ✓ Rev. {new Date(p.next_review_date).toLocaleDateString("it-IT")}
                            </span>
                          );
                        }
                        return (
                          <button
                            type="button"
                            data-testid={`policy-rev-badge-${p.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              try { Promise.resolve(navigator.clipboard?.writeText(p.vehicle_plate.toUpperCase())).catch(() => {}); } catch { /* noop */ }
                              toast.info(`Targa ${p.vehicle_plate.toUpperCase()} copiata. Verifica la revisione sul portale, poi aggiornala nella polizza.`, { duration: 6000 });
                              window.open("https://www.ilportaledellautomobilista.it/interrogazionistoricorevisioni/spa/", "_blank", "noopener,noreferrer");
                            }}
                            className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-md px-1.5 py-0.5 leading-none transition cursor-pointer ${style.cls}`}
                            title={`${style.tip} — clicca per verificare sul Portale dell'Automobilista (targa copiata)`}
                          >
                            <span className="text-[11px] leading-none">{style.icon}</span>
                            <span className="tabular-nums">{style.label}</span>
                          </button>
                        );
                      })()}
                      {/* Badge bollo: mancante / scaduto / in scadenza */}
                      {p.type === "auto" && (() => {
                        const today = new Date(); today.setHours(0, 0, 0, 0);
                        const b = p.bollo_due_date ? new Date(p.bollo_due_date) : null;
                        if (b) b.setHours(0, 0, 0, 0);
                        const days = b ? Math.round((b - today) / (1000 * 60 * 60 * 24)) : null;
                        let style = null;
                        if (!b) {
                          style = { cls: "bg-slate-100 text-slate-700 border-slate-300", icon: "🚗", label: "Bollo mancante" };
                        } else if (days < 0) {
                          style = { cls: "bg-rose-100 text-rose-800 border-rose-400", icon: "🚨", label: `Bollo scaduto da ${Math.abs(days)}gg` };
                        } else if (days <= 30) {
                          style = { cls: "bg-orange-100 text-orange-800 border-orange-400", icon: "🚗", label: `Bollo in scadenza ${days}gg` };
                        }
                        if (!style) return null;
                        return (
                          <span
                            data-testid={`policy-bollo-badge-${p.id}`}
                            className={`inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-tight border rounded px-1 py-px ${style.cls}`}
                            title={p.bollo_due_date ? `Scadenza bollo: ${new Date(p.bollo_due_date).toLocaleDateString("it-IT")}` : "Bollo non registrato"}
                          >
                            <span className="text-[10px] leading-none">{style.icon}</span> {style.label}
                          </span>
                        );
                      })()}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>
                <td className="px-2 py-2" data-testid={`policy-type-${p.id}`}>
                  <div className="flex flex-col gap-1">
                    {(() => {
                      // Icona dinamica in base al tipo di veicolo:
                      // - "moto" → Bike (override)
                      // - altri tipi → icona configurata in policy_types (con fallback intelligente)
                      const isMoto = p.type === "moto"
                        || /moto|scooter|ciclomotor/i.test(p.type || "")
                        || /ducati|aprilia|yamaha|kawasaki|harley|triumph|piaggio|vespa|bmw motorrad/i.test(p.vehicle_brand || "");
                      return (
                        <PolicyTypeBadge
                          code={p.type}
                          policyTypes={policyTypes}
                          size="sm"
                          overrideIcon={isMoto ? "bike" : undefined}
                          forceLabel={isMoto && p.type === "auto" ? "Moto" : undefined}
                          testid={`policy-type-badge-${p.id}`}
                        />
                      );
                    })()}
                    {(p.vehicle_brand || p.vehicle_model) && (
                      <span className="text-[10px] text-slate-500">
                        {[p.vehicle_brand, p.vehicle_model].filter(Boolean).join(" ")}
                      </span>
                    )}
                    {/* 🆕 15/07/2026 — Totale Generale Premio Lordo (leggi da p.premium, già calcolato lato backend) */}
                    {(() => {
                      const totale = parseFloat(p.premium) || 0;
                      if (totale <= 0) return null;
                      return (
                        <span
                          data-testid={`policy-total-gross-${p.id}`}
                          className="inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums bg-indigo-50 text-indigo-800 border border-indigo-200 rounded px-1.5 py-px w-fit"
                          title="Totale Generale Premio Lordo"
                        >
                          {formatCurrency(totale)}
                        </span>
                      );
                    })()}
                    {/* Badge n° garanzie attive — rosso se zero (polizza senza coperture) */}
                    {(() => {
                      const cnt = Array.isArray(p.coverages) ? p.coverages.length : 0;
                      const style = cnt === 0
                        ? "bg-rose-100 text-rose-800 border-rose-400"
                        : "bg-emerald-50 text-emerald-800 border-emerald-300";
                      const icon = cnt === 0 ? "🚨" : "🛡️";
                      const label = cnt === 0 ? "Nessuna garanzia" : `${cnt} garanzi${cnt === 1 ? "a" : "e"}`;
                      return (
                        <span
                          data-testid={`policy-coverages-badge-${p.id}`}
                          className={`inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-tight border rounded px-1 py-px ${style}`}
                          title={cnt === 0 ? "Polizza senza coperture: modifica e aggiungi almeno una garanzia" : `Garanzie attive: ${cnt}`}
                        >
                          <span className="text-[10px] leading-none">{icon}</span> {label}
                        </span>
                      );
                    })()}
                    {/* 🆕 Badge "Da completare" su polizze create automaticamente da offerta pagata */}
                    {p.status === "in_emissione" && p.source_offer_id && (
                      <>
                        <span
                          data-testid={`policy-to-complete-badge-${p.id}`}
                          className="inline-flex items-center gap-0.5 text-xs font-semibold uppercase tracking-tight border rounded px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-400 animate-pulse"
                          title="Polizza creata automaticamente da offerta pagata. Apri in modifica per allegare la copia firmata ed estrarre i dati definitivi."
                        >
                          <span className="text-xs leading-none">🚀</span> Da completare
                        </span>
                        {canWrite("policies") && (
                          <button
                            type="button"
                            data-testid={`emit-policy-${p.id}`}
                            onClick={() => { setFocusPolicyNumber(true); openEdit(p); }}
                            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-tight rounded px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-700"
                            title="Apri la polizza per emetterla (focus su N° polizza)"
                          >
                            ✓ Emetti
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2">{p.company ? (
                  <span
                    data-testid={`policy-company-${p.id}`}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-900 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5"
                    title={`Compagnia: ${p.company}`}
                  >
                    🏢 {p.company}
                  </span>
                ) : <span className="text-slate-300 text-sm">—</span>}</td>
                <td className="px-2 py-2 text-slate-600 text-xs">
                  <div className="inline-flex items-center gap-1.5 flex-wrap">
                    {brokerColor && (
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full border border-slate-200 shrink-0"
                        style={{ backgroundColor: brokerColor }}
                        title={`Colore identificativo broker: ${brokerColor}`}
                      />
                    )}
                    <span>{brokerName}</span>
                    {/* 🆕 Feb 2026 — Badge ripartizione Agenzia/Broker (solo se ≠ 100/0) */}
                    {brokerObj && (() => {
                      const link = p.company_id
                        ? partnerCompanyLinks.find((l) => l.partner_id === brokerObj.id && l.linked_company_id === p.company_id && l.active !== false)
                        : null;
                      const agencyPct = brokerSplitPcts(brokerObj, link).prov;
                      const brokerPct = 100 - agencyPct;
                      // Solo se lo split ≠ 100/0
                      if (agencyPct >= 99.995) return null;
                      const fmt = (n) => (Math.round(n * 10) / 10).toString().replace(/\.0$/, "");
                      return (
                        <span
                          data-testid={`policy-split-badge-${p.id}`}
                          className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 rounded px-1 py-0.5"
                          title={`Ripartizione provvigione: Agenzia ${agencyPct.toFixed(1)}% · Broker ${brokerPct.toFixed(1)}%`}
                        >
                          {fmt(agencyPct)}/{fmt(brokerPct)}
                        </span>
                      );
                    })()}
                  </div>
                  {assignedUser && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-700" title="Collaboratore assegnato alla polizza" data-testid={`policy-assigned-user-${p.id}`}>
                      <span className="font-semibold">{assignedUser.name || assignedUser.email}</span>
                      {assignedUser.role && <span className="opacity-70">· {assignedUser.role}</span>}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-slate-600 tabular-nums text-sm">
                  {(() => {
                    // 🆕 22/07/2026 — Se la polizza ha rate e la prossima non pagata è diversa dal fine polizza,
                    // mostra la scadenza effettiva (con badge "rata") e la scadenza finale sotto in piccolo.
                    // 🆕 28/07/2026 — Considera anche `installment_dates` (rate calcolate) oltre a `installment_amounts`
                    // per intercettare polizze rateizzate senza importi esplicitati.
                    const hasInstAmounts = p.installment_amounts && Object.keys(p.installment_amounts).length > 1;
                    const hasInstDates = Array.isArray(p.installment_dates) && p.installment_dates.length > 1;
                    const isRateizzata = (p.frazionamento && String(p.frazionamento).toLowerCase() !== "annuale");
                    const hasInst = hasInstAmounts || (isRateizzata && hasInstDates);
                    const eff = p.effective_end_date || p.end_date;
                    const showBoth = hasInst && eff && p.end_date && eff !== p.end_date;
                    return (
                      <>
                        <div className="flex items-center gap-1">
                          <span className={showBoth ? "text-blue-800 font-semibold" : ""}>{formatDate(eff)}</span>
                          {showBoth && (
                            <span className="text-[9px] font-bold px-1 py-px rounded bg-blue-100 text-blue-800 border border-blue-200 uppercase tracking-tight" title="Prossima rata da pagare">rata</span>
                          )}
                        </div>
                        {showBoth && (
                          <div className="text-[11px] text-slate-400 mt-0.5" title="Fine polizza">fine: {formatDate(p.end_date)}</div>
                        )}
                        <div className="mt-1.5"><ExpiryTimeline endDate={eff} status={p.status} /></div>
                      </>
                    );
                  })()}
                  {p.status === "sospesa" && p.reactivation_deadline && (
                    <div data-testid={`reactivation-${p.id}`} className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block mt-1">
                      Riattivare entro {formatDate(p.reactivation_deadline)}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-slate-600 tabular-nums text-sm" data-testid={`policy-created-at-${p.id}`}>
                  {p.created_at ? (
                    <>
                      <div>{formatDate(p.created_at)}</div>
                      <div className="text-[11px] text-slate-400">{new Date(p.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                    </>
                  ) : "—"}
                </td>
                <td className="px-2 py-2">
                  <StatusBadge status={p.status} testid={`status-${p.id}`} />
                  {p.was_reactivated && p.status === "attiva" && (
                    <span
                      data-testid={`reactivated-badge-${p.id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 border border-sky-300 rounded px-2 py-0.5 w-fit"
                      title={p.reactivated_at ? `Riattivata il ${new Date(p.reactivated_at).toLocaleDateString('it-IT')}${(p.reactivations_count || 0) > 1 ? ` · ${p.reactivations_count} riattivazioni totali` : ""}` : "Polizza riattivata dopo sospensione"}
                    >
                      <RefreshCw className="h-3 w-3" />
                      Riattivata{(p.reactivations_count || 0) > 1 ? ` ×${p.reactivations_count}` : ""}
                    </span>
                  )}
                  {p.is_paid && (
                    <span
                      data-testid={`paid-badge-${p.id}`}
                      className="block mt-1 text-xs font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-2 py-0.5 w-fit"
                      title={`Incassati € ${formatAmount(p.paid_amount || 0)}${p.paid_commission ? ` (di cui commissione € ${formatAmount(p.paid_commission)})` : ""}`}
                    >
                      💶 Pagata · € {formatAmount(p.paid_amount || 0)}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1 text-right print:hidden sticky right-0 bg-white/95 backdrop-blur-sm">
                  <div className="inline-flex items-center gap-1.5">
                    <div className="grid grid-cols-2 gap-y-1.5 gap-x-5">
                      <button data-testid={`view-policy-${p.id}`} onClick={() => setDetailOpen(p)} className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded p-1 transition-colors" title="Vedi dettagli">
                        <Eye className="h-4 w-4" />
                      </button>
                      <AttachmentsHoverPreview
                        endpoint={`/policies/${p.id}/attachments`}
                        count={p.attachments_count || 0}
                        testid={`attach-hover-${p.id}`}
                      >
                        <button
                          data-testid={`attach-policy-${p.id}`}
                          onClick={() => { setAttachOpen(p); loadAttachments(p.id); loadSendLogs(p.id); setSelectedForEmail({}); }}
                          className={`${(p.attachments_count || 0) === 0 ? "text-red-500 hover:text-red-700" : "text-slate-600 hover:text-blue-700"} relative rounded p-1 hover:bg-slate-100 transition-colors`}
                          title={(p.attachments_count || 0) === 0 ? "Nessun allegato caricato" : `${p.attachments_count} allegat${p.attachments_count === 1 ? "o" : "i"}`}
                        >
                          <Paperclip className="h-4 w-4" />
                          {(p.attachments_count || 0) > 0 && (
                            <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                              {p.attachments_count}
                            </span>
                          )}
                        </button>
                      </AttachmentsHoverPreview>
                      <button
                        data-testid={`msg-policy-${p.id}`}
                        onClick={() => setMsgTarget(p)}
                        className="text-green-600 hover:text-green-800 hover:bg-green-50 rounded p-1 transition-colors"
                        title="Invia messaggio cliente su questa polizza (Email + WhatsApp)"
                      >
                        <MessageSquareText className="h-4 w-4" />
                      </button>
                      {canWrite("policies") ? (
                        <button data-testid={`edit-policy-${p.id}`} onClick={() => openEdit(p)} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1 transition-colors" title="Modifica"><Pencil className="h-4 w-4" /></button>
                      ) : <span />}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`policy-actions-menu-${p.id}`}
                          className="p-1 rounded hover:bg-slate-100 text-slate-600 transition-colors ml-0.5 relative"
                          title="Altre azioni"
                          aria-label="Altre azioni"
                        >
                          <MoreVertical className="h-4 w-4" />
                          {((p.claims_count || 0) > 0 || (receiptsByPolicy[p.id] || 0) > 0) && (
                            <span className="absolute -top-0.5 -right-0.5 bg-amber-500 rounded-full h-2 w-2" title="Azioni con contatore attivo" />
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-60" data-testid={`policy-actions-menu-content-${p.id}`}>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                          Azioni polizza
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => setClaimsOpen(p)}
                          data-testid={`menu-claim-policy-${p.id}`}
                        >
                          <AlertTriangle className={`mr-2 h-4 w-4 ${(p.claims_count || 0) > 0 ? "text-amber-600" : "text-slate-600"}`} />
                          {(p.claims_count || 0) > 0 ? `Sinistri (${p.claims_count})` : "Segnala sinistro"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setPdfTplPolicyId(p.id)}
                          data-testid={`menu-gen-doc-${p.id}`}
                        >
                          <span className="mr-2 text-base leading-none">📄</span>
                          Genera documento
                        </DropdownMenuItem>
                        {(() => {
                          const client = clients.find((c) => c.id === p.client_id);
                          const rawPhone = client?.mobile || client?.phone;
                          if (!rawPhone) return null;
                          const polNum = p?.policy_number ? ` n° ${p.policy_number}` : "";
                          const compagnia = p?.company ? ` (${p.company})` : "";
                          const defaultMessage =
                            `Ciao ${client?.name || ""}, ti scrivo a proposito della tua polizza${polNum}${compagnia}${p.vehicle_plate ? ` · targa ${p.vehicle_plate}` : ""}. ` +
                            `Fammi sapere quando hai un minuto.`;
                          return (
                            <WhatsAppButton
                              rawPhone={rawPhone}
                              defaultMessage={defaultMessage}
                              testid={`whatsapp-policy-${p.id}`}
                            >
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid={`menu-whatsapp-policy-${p.id}`}>
                                <span className="mr-2 inline-flex items-center justify-center bg-green-500 text-white rounded-full h-4 w-4 text-[10px]">💬</span>
                                WhatsApp cliente
                              </DropdownMenuItem>
                            </WhatsAppButton>
                          );
                        })()}
                        {canWrite("policies") && (
                          <>
                            <DropdownMenuItem
                              onClick={() => emettiRicevutaPolizza(p)}
                              data-testid={`menu-receipt-policy-${p.id}`}
                            >
                              <span className="mr-2 text-base leading-none">🧾</span>
                              Emetti ricevuta
                              {receiptsByPolicy[p.id] > 0 && (
                                <span className="ml-auto text-[10px] bg-emerald-100 text-emerald-800 rounded-full px-1.5 py-0 font-bold min-w-[18px] text-center" data-testid={`receipt-count-${p.id}`}>{receiptsByPolicy[p.id]}</span>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => remove(p)}
                              className="text-red-600 focus:text-red-700 focus:bg-red-50"
                              data-testid={`menu-delete-policy-${p.id}`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Elimina polizza
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
        </div>

        {/* 🆕 16/07/2026 — Vista mobile a card (<768 px) — sezione polizze responsive */}
        <div className="md:hidden divide-y divide-slate-200" data-testid="policies-mobile-list">
          {sorted.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-400 text-sm">Nessuna polizza.</div>
          ) : (
            sorted.map((p) => {
              const brokerObj = p.broker_id ? brokers.find((b) => b.id === p.broker_id) : null;
              const isSelected = selectedIds.has(p.id);
              const c = clients.find((x) => x.id === p.client_id);
              const hasPortal = !!(c && c.portal_enabled);
              return (
                <div
                  key={`m-${p.id}`}
                  className={`px-3 py-3 space-y-2 ${isSelected ? "bg-rose-50/50" : "bg-white"} ${flashId === p.id ? "ring-4 ring-emerald-400 ring-inset bg-emerald-50/40" : ""}`}
                  data-testid={`policy-card-${p.id}`}
                >
                  {/* Riga 1: N° polizza + tipo + selezione */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono font-semibold text-sm">{p.policy_number || "—"}</span>
                        <PolicyTypeBadge code={p.type} size="xs" testid={`policy-card-type-${p.id}`} />
                        {hasPortal && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300" title="Area privata cliente attiva">🔓</span>
                        )}
                        {sharedClientIds.has(p.client_id) && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-300" title="Visibile in area condivisa">🔗</span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={p.status} />
                        {p.was_reactivated && p.status === "attiva" && (
                          <span
                            data-testid={`reactivated-badge-mobile-${p.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 border border-sky-300 rounded px-1.5 py-0.5"
                            title={p.reactivated_at ? `Riattivata il ${new Date(p.reactivated_at).toLocaleDateString('it-IT')}${(p.reactivations_count || 0) > 1 ? ` · ${p.reactivations_count} riattivazioni totali` : ""}` : "Polizza riattivata dopo sospensione"}
                          >
                            <RefreshCw className="h-3 w-3" />
                            Riattivata{(p.reactivations_count || 0) > 1 ? ` ×${p.reactivations_count}` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    {isAdmin && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(p.id)}
                        className="h-4 w-4 mt-1 cursor-pointer accent-rose-600"
                        aria-label={`Seleziona polizza ${p.policy_number}`}
                      />
                    )}
                  </div>

                  {/* Riga 2: Cliente */}
                  <div>
                    <div
                      className="font-medium text-sm uppercase cursor-pointer hover:text-blue-700"
                      onDoubleClick={() => openEdit(p)}
                      data-testid={`policy-card-client-name-${p.id}`}
                    >
                      {p.client_name || "—"}
                    </div>
                    {p.client_fiscal_code && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); try { navigator.clipboard.writeText(String(p.client_fiscal_code || "").toUpperCase()); toast.success("Codice fiscale copiato negli appunti ✓"); } catch { /* silent */ } }}
                        className="text-[10px] font-mono text-slate-500 hover:text-blue-600 uppercase"
                        title="Clicca per copiare"
                      >
                        🆔 {p.client_fiscal_code}
                      </button>
                    )}
                  </div>

                  {/* Riga 3: Veicolo (marca + modello + targa) */}
                  {(p.vehicle_brand || p.vehicle_model || p.vehicle_plate) && (
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      {(p.vehicle_brand || p.vehicle_model) && (
                        <span className="text-slate-600 font-medium">{[p.vehicle_brand, p.vehicle_model].filter(Boolean).join(" ")}</span>
                      )}
                      {p.vehicle_plate && (
                        <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-700">
                          {p.type === "moto" ? "🏍️" : (p.type === "auto" ? "🚗" : "🚙")}
                          {String(p.vehicle_plate).toUpperCase()}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Riga 4: Compagnia + Broker */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-600">
                    {p.company && (
                      <span className="inline-flex items-center gap-1"><span className="text-slate-400">Compagnia:</span> <strong className="text-slate-800">{p.company}</strong></span>
                    )}
                    {brokerObj && (
                      <span className="inline-flex items-center gap-1"><span className="text-slate-400">Broker:</span> <strong className="text-slate-800">{brokerObj.name}</strong></span>
                    )}
                  </div>

                  {/* Riga 5: Premio + Scadenza */}
                  <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
                    <div className="text-base font-semibold tabular-nums">
                      {p.gross_premium ? formatCurrency(p.gross_premium) : (p.premium ? formatCurrency(p.premium) : <span className="text-slate-400 text-sm">—</span>)}
                    </div>
                    <div className="text-[11px] text-slate-500 tabular-nums">
                      scad. <span className="font-medium text-slate-700">{formatDate(p.end_date)}</span>
                    </div>
                  </div>

                  {/* Riga 6: azioni */}
                  <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                    <button
                      onClick={() => setDetailOpen(p)}
                      className="flex-1 min-w-[90px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                      data-testid={`policy-card-view-${p.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" /> Dettagli
                    </button>
                    {canWrite("policies") && (
                      <button
                        onClick={() => openEdit(p)}
                        className="flex-1 min-w-[90px] px-2 py-1 rounded border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                        data-testid={`policy-card-edit-${p.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Modifica
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Detail dialog "Vedi" */}
      <PolicyDetailDialog
        policy={detailOpen}
        brokers={brokers}
        canWrite={canWrite("policies")}
        onClose={() => setDetailOpen(null)}
        onEdit={(p) => { setDetailOpen(null); openEdit(p); }}
        onSuspend={(p) => { setDetailOpen(null); openSuspend(p); }}
        onReactivate={(p) => { setDetailOpen(null); openReactivate(p); }}
        onExtendSuspension={(p) => { setDetailOpen(null); openExtend(p); }}
        onUndoReactivation={async (p) => {
          if (!window.confirm(
            "Annullare la riattivazione?\n\n" +
            "• Questa polizza (la duplicata creata dalla riattivazione) verrà ELIMINATA.\n" +
            "• La polizza originale tornerà in stato 'sospesa' e sarà nuovamente visibile.\n\n" +
            "Vuoi procedere?"
          )) return;
          try {
            const r = await api.post(`/policies/${p.id}/undo-reactivation`);
            toast.success(r.data?.message || "Riattivazione annullata");
            setDetailOpen(null);
            await load();
            const restoredId = r.data?.restored_policy_id;
            if (restoredId) {
              try {
                const rd = await api.get(`/policies/${restoredId}`);
                if (rd?.data) setDetailOpen(rd.data);
              } catch (_) { /* ignore load fail */ }
            }
          } catch (e) {
            toast.error(formatApiError(e));
          }
        }}
        onRenew={(p) => { setDetailOpen(null); startRenew(p); }}
      />

      {/* 🆕 Claims dialog */}
      <ClaimsDialog
        policy={claimsOpen}
        open={!!claimsOpen}
        onClose={() => setClaimsOpen(null)}
        onChanged={() => { load(); }}
      />

      {/* 🆕 Send message dialog (per polizza) */}
      <SendMessageDialog
        open={!!msgTarget}
        onClose={() => setMsgTarget(null)}
        clientId={msgTarget?.client_id}
        clientLabel={msgTarget?.client_name || ""}
        policyId={msgTarget?.id}
        policyLabel={msgTarget?.policy_number || ""}
        templateListKey="policy_message_templates"
        title="Invia messaggio cliente su questa polizza"
        subtitle="Comunicazione al cliente collegata a questa polizza (email + WhatsApp)."
      />

      {/* Dialog Rinnova polizza */}
      <Dialog open={!!renewTarget} onOpenChange={(o) => { if (!o && !renewing) closeRenew(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="renew-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RefreshCw className="h-5 w-5" /> Rinnova polizza
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 border border-slate-200 rounded p-2.5 text-xs">
              <p><strong>Polizza attuale:</strong> n° {renewTarget?.policy_number} · {renewTarget?.client_name}</p>
              <p>Compagnia: <strong>{renewTarget?.company || "—"}</strong> · Scadenza: <strong>{renewTarget?.end_date ? formatDate(renewTarget.end_date) : "—"}</strong></p>
              {renewTarget?.vehicle_plate && <p>Targa: <span className="font-mono font-bold">{renewTarget.vehicle_plate}</span></p>}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
              <Label className="text-amber-900 font-semibold">🏢 La compagnia è la stessa o è cambiata?</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="renew-same-company"
                  onClick={() => setRenewForm({ ...renewForm, same_company: true })}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm font-semibold transition ${renewForm.same_company ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                >
                  ✓ Stessa compagnia: {renewTarget?.company}
                </button>
                <button
                  type="button"
                  data-testid="renew-new-company"
                  onClick={() => setRenewForm({ ...renewForm, same_company: false })}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm font-semibold transition ${!renewForm.same_company ? "border-violet-600 bg-violet-50 text-violet-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                >
                  ↻ Compagnia diversa
                </button>
              </div>
            </div>

            {!renewForm.same_company && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-violet-50/40 border border-violet-200 rounded p-3">
                <div className="col-span-2">
                  <Label className="text-violet-900 font-semibold">Nuova compagnia *</Label>
                  <Select value={renewForm.new_company_id} onValueChange={(v) => setRenewForm({ ...renewForm, new_company_id: v })}>
                    <SelectTrigger data-testid="renew-company-select" className="bg-white"><SelectValue placeholder="Seleziona la nuova compagnia…" /></SelectTrigger>
                    <SelectContent>
                      {companies.filter((c) => c.id !== renewTarget?.company_id).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-violet-900 font-semibold">Nuovo numero polizza *</Label>
                  <Input
                    data-testid="renew-policy-number"
                    value={renewForm.new_policy_number}
                    onChange={(e) => setRenewForm({ ...renewForm, new_policy_number: e.target.value })}
                    placeholder="es. POL2026-12345"
                    className="bg-white font-mono"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Nuova data inizio *</Label>
                <DatePicker data-testid="renew-start-date"
                  value={renewForm.new_start_date}
                  onChange={(e) => setRenewForm({ ...renewForm, new_start_date: e.target.value })} />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  Nuova data scadenza
                  <span className="text-[10px] text-slate-500 normal-case font-normal">(auto da frazionamento)</span>
                </Label>
                <DatePicker data-testid="renew-end-date"
                  value={renewForm.new_end_date}
                  readOnly
                  className="bg-slate-50 cursor-not-allowed"
                  title="La scadenza viene calcolata automaticamente in base al frazionamento" />
                <p className="text-[10px] text-emerald-700 mt-1">
                  📅 = Data inizio + {(FRAZIONAMENTI.find((f) => f.v === renewForm.new_frazionamento)?.months || 12)} mesi
                </p>
              </div>
              <div>
                <Label>Premio (€)</Label>
                <MoneyInput
                  data-testid="renew-premium"
                  value={renewForm.new_premium}
                  onChange={(v) => setRenewForm({ ...renewForm, new_premium: v === "" ? 0 : parseFloat(v) })}
                />
              </div>
              <div>
                <Label>Frazionamento</Label>
                <Select value={renewForm.new_frazionamento} onValueChange={(v) => setRenewForm({ ...renewForm, new_frazionamento: v })}>
                  <SelectTrigger data-testid="renew-fraz"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FRAZIONAMENTI.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Note (opzionale)</Label>
                <Input
                  data-testid="renew-notes"
                  value={renewForm.notes}
                  onChange={(e) => setRenewForm({ ...renewForm, notes: e.target.value })}
                  placeholder="es. variazione premio, sconto fedeltà…"
                />
              </div>
            </div>

            <p className="text-[11px] text-slate-500 italic">
              ⚠️ La polizza esistente verrà archiviata (status &quot;rinnovata&quot;) e ne verrà creata una nuova
              {!renewForm.same_company && " con la compagnia indicata"}.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-renew" onClick={closeRenew} disabled={renewing}>Annulla</Button>
            <Button
              data-testid="confirm-renew"
              onClick={confirmRenew}
              disabled={renewing || !renewForm.new_start_date || (!renewForm.same_company && (!renewForm.new_company_id || !renewForm.new_policy_number))}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {renewing ? "Rinnovo in corso…" : <><RefreshCw className="h-4 w-4 mr-2" /> Rinnova polizza</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog Sospendi polizza */}
      <Dialog open={!!suspendTarget} onOpenChange={(o) => { if (!o && !suspending) { setSuspendTarget(null); setSuspendResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="suspend-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <PauseCircle className="h-5 w-5" /> Sospendi polizza
            </DialogTitle>
          </DialogHeader>
          {suspendResult ? (
            <div className="space-y-3 text-sm">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                ✅ Polizza sospesa dal <b>{formatDate(suspendResult.suspension_start_date)}</b>.
                {suspendResult.original_end_date && <> Scadenza originale <b>{formatDate(suspendResult.original_end_date)}</b> preservata.</>}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Messaggio WhatsApp per il cliente</div>
                <div className="text-xs text-slate-700 whitespace-pre-wrap">{suspendResult.whatsapp_text}</div>
              </div>
              <div className="flex gap-2">
                {suspendResult.whatsapp_url ? (
                  <a
                    href={suspendResult.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-whatsapp"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#25D366] hover:bg-[#1EBE5A] text-white text-sm font-medium"
                  >
                    💬 Apri WhatsApp e invia
                  </a>
                ) : (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    ⚠️ Il cliente non ha un numero di telefono valido — impossibile aprire WhatsApp automaticamente. Puoi copiare il testo qui sopra.
                  </div>
                )}
                <Button variant="outline" onClick={() => { setSuspendTarget(null); setSuspendResult(null); }} data-testid="close-suspend-result">
                  Chiudi
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3 text-sm">
                <div className="bg-orange-50 border border-orange-200 rounded p-2.5 text-xs">
                  Stai sospendendo la polizza <b>{suspendTarget?.policy_number}</b>. Alla riattivazione, la scadenza verrà spostata in avanti di tanti giorni quanti sono quelli di fermo.
                </div>
                <div>
                  <Label>Data inizio sospensione *</Label>
                  <Input
                    type="date"
                    value={suspendForm.suspension_start_date}
                    onChange={(e) => setSuspendForm({ ...suspendForm, suspension_start_date: e.target.value })}
                    data-testid="suspend-start-date"
                  />
                </div>
                <div>
                  <Label>Motivo (opzionale)</Label>
                  <Input
                    placeholder="Es. Fermo temporaneo veicolo"
                    value={suspendForm.reason}
                    onChange={(e) => setSuspendForm({ ...suspendForm, reason: e.target.value })}
                    data-testid="suspend-reason"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSuspendTarget(null)} disabled={suspending} data-testid="cancel-suspend">
                  Annulla
                </Button>
                <Button
                  onClick={confirmSuspend}
                  disabled={suspending || !suspendForm.suspension_start_date}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="confirm-suspend"
                >
                  {suspending ? "Sospensione in corso…" : <><PauseCircle className="h-4 w-4 mr-2" /> Conferma sospensione</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog Riattiva polizza */}
      <Dialog open={!!reactivateTarget} onOpenChange={(o) => { if (!o && !reactivating) { setReactivateTarget(null); setReactivateResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="reactivate-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <PlayCircle className="h-5 w-5" /> Riattiva polizza
            </DialogTitle>
          </DialogHeader>
          {reactivateResult ? (
            <div className="space-y-3 text-sm">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                ✅ Polizza riattivata. Nuova scadenza: <b>{formatDate(reactivateResult.new_end_date)}</b> ({reactivateResult.days_suspended} gg di fermo aggiunti).
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Messaggio WhatsApp per il cliente</div>
                <div className="text-xs text-slate-700 whitespace-pre-wrap">{reactivateResult.whatsapp_text}</div>
              </div>
              <div className="flex gap-2">
                {reactivateResult.whatsapp_url ? (
                  <a
                    href={reactivateResult.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-whatsapp-reactivate"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#25D366] hover:bg-[#1EBE5A] text-white text-sm font-medium"
                  >
                    💬 Apri WhatsApp e invia
                  </a>
                ) : (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    ⚠️ Nessun telefono cliente — puoi copiare il testo.
                  </div>
                )}
                <Button variant="outline" onClick={() => { setReactivateTarget(null); setReactivateResult(null); }}>
                  Chiudi
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3 text-sm">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-xs">
                  Polizza <b>{reactivateTarget?.policy_number}</b> sospesa dal <b>{formatDate(reactivateTarget?.suspension_start_date) || "—"}</b>.
                  {reactivateTarget?.reactivation_deadline && <> Termine max riattivazione: <b>{formatDate(reactivateTarget.reactivation_deadline)}</b>.</>}
                  <br />La nuova scadenza sarà calcolata automaticamente: <b>scadenza originale + giorni di fermo</b>.
                </div>
                <div>
                  <Label>Data riattivazione *</Label>
                  <Input
                    type="date"
                    value={reactivateForm.reactivation_date}
                    min={reactivateTarget?.suspension_start_date || undefined}
                    onChange={(e) => setReactivateForm({ reactivation_date: e.target.value })}
                    data-testid="reactivate-date"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReactivateTarget(null)} disabled={reactivating} data-testid="cancel-reactivate">
                  Annulla
                </Button>
                <Button
                  onClick={confirmReactivate}
                  disabled={reactivating || !reactivateForm.reactivation_date}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="confirm-reactivate"
                >
                  {reactivating ? "Riattivazione in corso…" : <><PlayCircle className="h-4 w-4 mr-2" /> Conferma riattivazione</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog Proroga sospensione */}
      <Dialog open={!!extendTarget} onOpenChange={(o) => { if (!o && !extending) { setExtendTarget(null); setExtendResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="extend-suspension-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              🕐 Proroga sospensione
            </DialogTitle>
          </DialogHeader>
          {extendResult ? (
            <div className="space-y-3 text-sm">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                ✅ Proroga registrata. Termine precedente: <b>{formatDate(extendResult.previous_deadline)}</b> → nuovo: <b>{formatDate(extendResult.reactivation_deadline)}</b>.
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Messaggio WhatsApp per il cliente</div>
                <div className="text-xs text-slate-700 whitespace-pre-wrap">{extendResult.whatsapp_text}</div>
              </div>
              <div className="flex gap-2">
                {extendResult.whatsapp_url && (
                  <a href={extendResult.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="open-whatsapp-extend" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#25D366] hover:bg-[#1EBE5A] text-white text-sm font-medium">
                    💬 Apri WhatsApp
                  </a>
                )}
                <Button variant="outline" onClick={() => { setExtendTarget(null); setExtendResult(null); }}>Chiudi</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3 text-sm">
                <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-xs">
                  <b>Regola dei 10 giorni</b>: la richiesta deve arrivare almeno 10 gg prima della scadenza corrente ({extendTarget?.reactivation_deadline ? formatDate(extendTarget.reactivation_deadline) : "—"}).
                </div>
                <div>
                  <Label>Mesi da aggiungere *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="24"
                    value={extendForm.additional_months}
                    onChange={(e) => setExtendForm({ ...extendForm, additional_months: e.target.value })}
                    data-testid="extend-additional-months"
                  />
                </div>
                <div>
                  <Label>Motivo (opzionale)</Label>
                  <Input
                    placeholder="Es. Riparazioni non ancora concluse"
                    value={extendForm.reason}
                    onChange={(e) => setExtendForm({ ...extendForm, reason: e.target.value })}
                    data-testid="extend-reason"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setExtendTarget(null)} disabled={extending}>Annulla</Button>
                <Button
                  onClick={confirmExtend}
                  disabled={extending || !extendForm.additional_months}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="confirm-extend"
                >
                  {extending ? "Proroga in corso…" : "🕐 Conferma proroga"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog "Genera documento da modello" attivabile dal DropdownMenu delle azioni polizza */}
      <ApplyPdfTemplateButton
        policyId={pdfTplPolicyId || undefined}
        hideTrigger
        open={!!pdfTplPolicyId}
        onOpenChange={(o) => { if (!o) setPdfTplPolicyId(null); }}
        testid="policy-gen-doc-controlled"
        onApplied={() => { try { load(); } catch { /* noop */ } }}
      />

      {/* Attachments dialog */}
      <Dialog open={!!attachOpen} onOpenChange={(o) => { if (!o) { setAttachOpen(null); setAttachments([]); setAttachDesc(""); setSelectedForEmail({}); setSendLogs([]); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="attachments-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="relative inline-flex">
                <Paperclip className="h-5 w-5 text-blue-600" />
                {attachments.length > 0 && (
                  <span data-testid="attach-dialog-badge" className="absolute -top-1 -right-2 bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                    {attachments.length > 99 ? "99+" : attachments.length}
                  </span>
                )}
              </span>
              <span>Allegati ({attachments.length}) · {attachOpen?.policy_number}</span>
              <ApplyPdfTemplateButton
                policyId={attachOpen?.id}
                variant="outline"
                testid="policy-apply-pdf-tpl"
                onApplied={async () => { try { const { data } = await api.get(`/policies/${attachOpen.id}/attachments`); setAttachments(data); } catch { /* noop */ } }}
              />
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <DropZone
              testid="attach-dialog-dropzone"
              onFiles={onUploadDrop}
              accept={["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/heic", "image/heif"]}
              multiple
              disabled={uploading}
              className="space-y-3"
            >
            <div className="bg-blue-50/50 border border-blue-100 rounded-md p-4 space-y-3">
              <Label className="text-xs uppercase tracking-wide text-blue-700">Carica nuovo allegato</Label>
              <p className="text-[11px] text-blue-700/70 -mt-1">💡 Trascina i file ovunque in questa finestra per caricarli rapidamente.</p>
              <Input
                data-testid="attach-description"
                value={attachDesc}
                onChange={(e) => setAttachDesc(e.target.value)}
                placeholder="Descrizione: es. Contratto firmato, foto sinistro 12/03…"
              />
              <label className="block">
                <input data-testid="attach-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={onUpload} className="hidden" disabled={uploading} />
                <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed ${uploading ? "border-slate-300 text-slate-400" : "border-blue-400 hover:bg-blue-100 cursor-pointer text-blue-700"} text-sm`}>
                  <Plus className="h-4 w-4" /> {uploading ? "Caricamento…" : "Scegli file (PDF / JPG / PNG / WEBP, max 10 MB)"}
                </span>
              </label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[11px] text-slate-500">oppure</span>
                <ScanButton
                  testid="scan-policy-attachment"
                  label="Importa da scanner"
                  variant="outline"
                  disabled={uploading}
                  onFile={(f) => onUpload({ target: { files: [f], value: "" } })}
                />
                <CameraButton
                  testid="cam-scan-policy-attachment"
                  label="📷 Foto da fotocamera"
                  variant="outline"
                  disabled={uploading}
                  onFile={(f) => onUpload({ target: { files: [f], value: "" } })}
                />
              </div>
            </div>
            <div className="space-y-2" data-testid="attachments-list">
              <div className="flex items-center justify-between gap-2 pb-1 border-b border-slate-100">
                <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  Seleziona uno o più file per inviarli via email
                </div>
                <Button
                  data-testid="send-attachments-btn"
                  onClick={openSendDialog}
                  disabled={!Object.values(selectedForEmail).some(Boolean)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs"
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" />
                  Invia via email ({Object.values(selectedForEmail).filter(Boolean).length})
                </Button>
              </div>
              {attachments.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nessun allegato.</p>}
              {attachments.length > 0 && (
                <div className="mb-2 flex items-center justify-between gap-2 flex-wrap p-2 rounded-md bg-blue-50 border border-blue-200 text-xs">
                  <span className="text-blue-800">
                    <strong>Gestione flag</strong>: seleziona/deseleziona liberamente, poi salva.
                    <span className="text-slate-500 ml-1">Nessuna email verrà inviata finché non premi &quot;Invia notifiche&quot;.</span>
                  </span>
                  <div className="flex items-center gap-2">
                    {flagsDirty && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="save-flags-btn"
                        onClick={saveAttFlags}
                        disabled={savingFlags}
                        className="h-7 text-xs border-slate-300"
                      >
                        {savingFlags ? "Salvo…" : "💾 Salva modifiche flag"}
                      </Button>
                    )}
                    {pendingNotifyIds.length > 0 && (
                      <Button
                        size="sm"
                        data-testid="open-notify-batch-btn"
                        onClick={() => setNotifyBatchOpen(true)}
                        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        📧 Invia notifiche ({pendingNotifyIds.length})
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {attachments.map((a) => {
                const flags = pendingAttFlags[a.id] || {};
                const isVisible = flags.client_visible ?? !!a.client_visible;
                const isNotify = flags.notify_email_pending ?? !!a.notify_email_pending;
                const setFlag = (key, val) => setPendingAttFlags((s) => ({ ...s, [a.id]: { ...(s[a.id] || { client_visible: !!a.client_visible, notify_email_pending: !!a.notify_email_pending }), [key]: val } }));
                return (
                <div key={a.id} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    data-testid={`send-check-${a.id}`}
                    checked={!!selectedForEmail[a.id]}
                    onChange={(e) => setSelectedForEmail({ ...selectedForEmail, [a.id]: e.target.checked })}
                    className="mt-2 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    title="Seleziona per invio email allegati"
                  />
                  <div className="flex-1">
                    <AttachmentItem att={a} onDownload={downloadAttachment} onPreview={previewPolicyAttachment} onDelete={removeAttachment} showClientVisibilityToggle={false} onRequestSignature={requestSignatureForAttachment} />
                    <div className="mt-1 flex items-center gap-4 text-xs pl-1">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`flag-visible-${a.id}`}
                          checked={isVisible}
                          onChange={(e) => setFlag("client_visible", e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 cursor-pointer"
                        />
                        <span className={isVisible ? "text-emerald-700 font-medium" : "text-slate-500"}>👁️ Visibile cliente</span>
                      </label>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`flag-notify-${a.id}`}
                          checked={isNotify}
                          onChange={(e) => setFlag("notify_email_pending", e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-amber-600 cursor-pointer"
                        />
                        <span className={isNotify ? "text-amber-700 font-medium" : "text-slate-500"}>📧 Notifica email</span>
                      </label>
                      {a.last_notified_at && (
                        <span className="text-[10px] text-slate-400 italic" title={`Ultima notifica ${a.last_notified_at}`}>Notificato: {formatDateTime(a.last_notified_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              );})}
            </div>
            {/* Storico invii */}
            {sendLogs.length > 0 && (
              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="send-logs-section">
                <Label className="text-xs uppercase tracking-wide text-slate-600 flex items-center gap-1.5 mb-2">
                  <Mail className="h-3.5 w-3.5" /> Storico invii ({sendLogs.length})
                </Label>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {sendLogs.map((l) => {
                    // 🆕 Badge tipo invio (Polizza pronta / Generico / Notifica)
                    const kindLabel = l.kind === "policy_ready_notice" ? "Polizza pronta"
                      : l.kind === "policy_notify" ? "Notifica"
                      : l.kind === "policy_att" ? "Allegati"
                      : (l.kind || "Invio");
                    const isReady = l.kind === "policy_ready_notice";
                    const borderColor = isReady ? "border-emerald-500" : l.status === "sent" ? "border-emerald-400" : "border-red-400";
                    return (
                      <div key={l.id} className={`text-xs flex items-start gap-2 border-l-2 ${borderColor} pl-2 py-0.5`} data-testid={`send-log-${l.id}`}>
                        <span className={`text-[10px] uppercase font-bold rounded px-1 ${l.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                          {l.status === "sent" ? "✓" : "✗"}
                        </span>
                        <span
                          className={`text-[10px] font-semibold rounded px-1.5 py-0.5 whitespace-nowrap ${isReady ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700"}`}
                          data-testid={`send-log-kind-${l.id}`}
                          title={l.kind || ""}
                        >
                          {kindLabel}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="truncate"><span className="font-semibold">{l.recipient}</span> · {formatDateTime(l.sent_at)} · <span className="text-slate-400">{l.sent_by || ""}</span></div>
                          <div className="text-slate-500 truncate">
                            {(l.attachment_names && l.attachment_names.length > 0)
                              ? l.attachment_names.join(", ")
                              : <span className="italic text-slate-400">senza allegati</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </DropZone>
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 16/07/2026 — Dialog conferma invio notifica batch allegati (Salva e Notifica) */}
      <Dialog open={notifyBatchOpen} onOpenChange={(o) => { setNotifyBatchOpen(o); if (!o) setNotifyCustomMsg(""); }}>
        <DialogContent className="max-w-lg" data-testid="notify-batch-dialog">
          <DialogHeader>
            <DialogTitle>📧 Invia notifica cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Sto per inviare UNA email di riepilogo al cliente della polizza <strong>{attachOpen?.policy_number || ""}</strong>.
            </p>
            {(() => {
              const files = (attachments || []).filter((a) => pendingNotifyIds.includes(a.id));
              return (
                <div className="p-2.5 rounded bg-blue-50 border border-blue-200 text-xs max-h-40 overflow-y-auto">
                  <p className="font-semibold text-blue-900 mb-1">File inclusi ({files.length}):</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {files.map((f) => (
                      <li key={f.id} className={f.client_visible || pendingAttFlags[f.id]?.client_visible ? "text-slate-700" : "text-red-600"}>
                        {f.original_filename}
                        {!(f.client_visible || pendingAttFlags[f.id]?.client_visible) && <span className="text-[10px] ml-1">(⚠ non visibile)</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            <div>
              <Label className="text-xs">Messaggio personalizzato (opzionale)</Label>
              <textarea
                data-testid="notify-custom-msg"
                value={notifyCustomMsg}
                onChange={(e) => setNotifyCustomMsg(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Es. Ti allego i documenti richiesti la scorsa settimana..."
                className="w-full mt-1 p-2 border border-slate-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-[10px] text-slate-500 mt-0.5">{notifyCustomMsg.length}/1000</p>
            </div>
            {flagsDirty && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠ Ci sono flag non salvati. Verranno salvati automaticamente prima dell&apos;invio.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyBatchOpen(false)} disabled={notifyBusy} data-testid="notify-batch-cancel">Annulla</Button>
            <Button onClick={sendBatchNotification} disabled={notifyBusy || pendingNotifyIds.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="notify-batch-confirm">
              {notifyBusy ? "Invio…" : `Salva e Notifica (${pendingNotifyIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog invio allegati via email */}
      <Dialog open={sendDialogOpen} onOpenChange={(o) => { if (!o && !sendBusy) setSendDialogOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="send-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Mail className="h-5 w-5" /> Invia allegati via email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded p-2.5 text-xs">
              <strong>{Object.values(selectedForEmail).filter(Boolean).length}</strong> file selezionati:
              <ul className="mt-1 list-disc list-inside text-slate-700 max-h-24 overflow-y-auto">
                {attachments.filter((a) => selectedForEmail[a.id]).map((a) => (
                  <li key={a.id} className="truncate">{a.original_filename}</li>
                ))}
              </ul>
            </div>
            <div>
              <Label className="text-xs">Destinatario *</Label>
              <Input
                data-testid="send-to-email"
                type="email"
                value={sendForm.to_email}
                onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })}
                placeholder="cliente@example.it"
              />
            </div>
            <div>
              <Label className="text-xs">Oggetto</Label>
              <Input
                data-testid="send-subject"
                value={sendForm.subject}
                onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Messaggio</Label>
              <Textarea
                data-testid="send-body"
                rows={5}
                value={sendForm.body}
                onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)} disabled={sendBusy}>Annulla</Button>
            <Button
              data-testid="confirm-send-attachments"
              onClick={confirmSendAttachments}
              disabled={sendBusy || !sendForm.to_email}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {sendBusy ? "Invio…" : <><Mail className="h-4 w-4 mr-1.5" /> Invia</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione allegato polizza */}
      <Dialog open={!!attDeleteTarget} onOpenChange={(o) => { if (!o && !attDeleting) setAttDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-attachment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare l&apos;allegato?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>Stai per eliminare il file:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2 break-all">
              {attDeleteTarget?.label}
            </p>
            <p className="text-xs text-red-600 font-semibold">⚠ L&apos;eliminazione è immediata: il file verrà rimosso subito, prima ancora di salvare la polizza. L&apos;operazione non può essere annullata.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-delete-attachment" onClick={() => setAttDeleteTarget(null)} disabled={attDeleting}>
              Annulla
            </Button>
            <Button
              data-testid="confirm-delete-attachment"
              onClick={confirmRemoveAttachment}
              disabled={attDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {attDeleting ? "Eliminazione…" : "Elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione polizza */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare la polizza?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-3">
            <p>Stai per eliminare definitivamente la polizza:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2">
              {deleteTarget?.label}
            </p>
            <p className="text-xs text-red-600">L&apos;operazione non può essere annullata. Gli allegati saranno archiviati.</p>
            {!deleteTarget?.otp_sent ? (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                <p className="font-medium mb-1">🔐 Conferma via OTP</p>
                <p>Per sicurezza, ti invieremo un codice OTP via email che dovrai inserire qui sotto per completare la cancellazione.</p>
                <Button
                  data-testid="request-delete-otp"
                  onClick={requestDeleteOtp}
                  disabled={!!deleteTarget?.requesting}
                  size="sm"
                  className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {deleteTarget?.requesting ? "Invio…" : "📧 Invia codice OTP"}
                </Button>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-xs text-blue-900 mb-2">
                  Codice inviato a <strong>{deleteTarget?.sent_to}</strong>. Scade tra 5 minuti.
                </p>
                <Input
                  data-testid="delete-otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••••"
                  value={deleteTarget?.otp || ""}
                  onChange={(e) => setDeleteTarget((prev) => ({ ...prev, otp: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                  className="text-center font-mono text-2xl tracking-[8px] h-12"
                />
                <button
                  type="button"
                  onClick={requestDeleteOtp}
                  className="text-xs text-blue-700 hover:text-blue-900 underline mt-1.5"
                  disabled={!!deleteTarget?.requesting}
                >
                  Reinvia codice
                </button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-delete-policy" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Annulla
            </Button>
            <Button
              data-testid="confirm-delete-policy"
              onClick={confirmDelete}
              disabled={deleting || !deleteTarget?.otp_sent || (deleteTarget?.otp?.length || 0) !== 6}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Bulk delete: Step 1 - conferma testuale ---------- */}
      <Dialog open={bulkStage === "confirm"} onOpenChange={(o) => { if (!o) setBulkStage(null); }}>
        <DialogContent data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" /> Cancellazione massiva — Conferma
            </DialogTitle>
            <DialogDescription>Richiedi un codice OTP via email per procedere con la cancellazione massiva.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border-2 border-rose-300 bg-rose-50 p-4">
              <p className="text-rose-900 font-semibold">
                Stai per eliminare <span className="text-2xl font-extrabold">{selectedIds.size}</span> {selectedIds.size === 1 ? "polizza" : "polizze"} dal database.
              </p>
              <p className="text-rose-800 text-xs mt-1">
                Tutti gli allegati associati verranno marcati come eliminati. L&apos;operazione non è recuperabile dall&apos;interfaccia.
              </p>
            </div>
            <p className="text-slate-600">
              Per procedere riceverai un <strong>codice OTP a 6 cifre</strong> via email all&apos;indirizzo
              <strong> {currentUser?.email}</strong>. Il codice scade dopo 5 minuti.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStage(null)} disabled={bulkBusy}>Annulla</Button>
            <Button
              variant="destructive"
              onClick={requestBulkOtp}
              disabled={bulkBusy}
              data-testid="bulk-request-otp-btn"
            >
              {bulkBusy ? "Invio OTP…" : "Invia codice OTP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Bulk delete: Step 2 - inserisci OTP ---------- */}
      <Dialog open={bulkStage === "otp"} onOpenChange={(o) => { if (!o && !bulkBusy) { setBulkStage(null); setBulkOtp(""); } }}>
        <DialogContent data-testid="bulk-otp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" /> Inserisci codice OTP
            </DialogTitle>
            <DialogDescription>Inserisci il codice a 6 cifre ricevuto via email per confermare la cancellazione.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700">
              Abbiamo inviato un codice a 6 cifre a <strong>{bulkOtpInfo?.email || currentUser?.email}</strong>.
              Inseriscilo qui sotto per confermare la cancellazione di <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? "polizza" : "polizze"}.
            </p>
            <div>
              <Label htmlFor="bulk-otp-input">Codice OTP</Label>
              <Input
                id="bulk-otp-input"
                data-testid="bulk-otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="——————"
                value={bulkOtp}
                onChange={(e) => setBulkOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-2xl font-mono tracking-[0.5em] py-6"
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-1">
                Non hai ricevuto l&apos;email? Controlla lo spam o{" "}
                <button
                  type="button"
                  className="text-blue-600 hover:underline"
                  onClick={requestBulkOtp}
                  disabled={bulkBusy}
                  data-testid="bulk-resend-otp"
                >
                  rigenera codice
                </button>.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkStage(null); setBulkOtp(""); }} disabled={bulkBusy}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBulkDelete}
              disabled={bulkBusy || bulkOtp.length < 4}
              data-testid="bulk-confirm-delete-btn"
            >
              {bulkBusy ? "Eliminazione…" : `Elimina definitivamente (${selectedIds.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Dialog anteprima email "Polizza pronta" — apertura automatica al passaggio in_emissione → attiva */}
      <PolicyReadyNoticeDialog
        open={!!readyNoticePolicy}
        onClose={() => setReadyNoticePolicy(null)}
        policy={readyNoticePolicy}
      />
      {/* 🆕 Dialog anteprima email generico (usato per l'avviso di scadenza polizza) */}
      <EmailPreviewDialog
        open={!!emailPreviewCtx}
        onOpenChange={(o) => { if (!o) setEmailPreviewCtx(null); }}
        emailTypeKey={emailPreviewCtx?.typeKey}
        title={emailPreviewCtx?.title || "Anteprima email prima dell'invio"}
        fetchPreview={emailPreviewCtx?.fetchPreview || (async () => ({ subject: "", html_body: "", to: [] }))}
        onConfirmSend={async () => {
          if (emailPreviewCtx?.onConfirm) await emailPreviewCtx.onConfirm();
          setEmailPreviewCtx(null);
        }}
      />
      {/* 🆕 28/07/2026 — Dialog "Avvisa" con scelta canale (Email / WhatsApp / Entrambi) */}
      <ExpiryNoticeDialog
        open={!!noticeDialog}
        policy={noticeDialog?.policy}
        client={noticeDialog?.client}
        onClose={() => setNoticeDialog(null)}
        onSent={() => { /* nessuna refetch necessaria: il campo notified è sulla polizza */ }}
      />
      {/* 🆕 10/07/2026 — Dialog "rate senza data pagam. effettivo" */}
      <Dialog open={!!missingPaidDatesPrompt} onOpenChange={(o) => { if (!o) setMissingPaidDatesPrompt(null); }}>
        <DialogContent className="max-w-md" data-testid="missing-paid-dates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Rate senza data di pagamento
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>
              Ci {missingPaidDatesPrompt?.count === 1 ? "è" : "sono"} <strong>{missingPaidDatesPrompt?.count} rata{missingPaidDatesPrompt?.count === 1 ? "" : "e"}</strong> con importo ma senza il campo <strong>&quot;Data incasso rata&quot;</strong> compilato.
            </p>
            <p className="text-xs text-slate-500">
              Se non lo compili, non saprai quando l&apos;incasso è avvenuto realmente (utile per rendicontazione e provvigioni).
            </p>
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              data-testid="missing-paid-dates-fill"
              onClick={() => {
                const target = missingPaidDatesPrompt?.firstDate;
                setMissingPaidDatesPrompt(null);
                setTimeout(() => {
                  try {
                    const el = document.querySelector(`[data-testid="installment-paid-${target}"]`);
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "center" });
                      try { el.focus(); } catch { /* noop */ }
                    }
                  } catch { /* noop */ }
                }, 200);
              }}
              className="border-blue-400 text-blue-700 hover:bg-blue-50"
            >
              📝 Compila subito
            </Button>
            <Button
              data-testid="missing-paid-dates-save-anyway"
              onClick={() => { setMissingPaidDatesPrompt(null); submit({ skipMissingPaidDatesCheck: true }); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              💾 Salva ed esci comunque
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Anteprima inline PDF/immagini allegati polizza */}
      {AttachmentPreviewDialog}
      {/* 🆕 Feb 2026 — Dialog "Collega area privata a un altro cliente" (retroattivo, dal dettaglio polizza) */}
      <Dialog open={!!portalLinkDialog} onOpenChange={(o) => { if (!o) { setPortalLinkDialog(null); setPortalLinkExisting([]); } }}>
        <DialogContent className="max-w-lg" data-testid="portal-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-800">🔗 Collega area privata</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="bg-violet-50 border border-violet-200 rounded-md p-3">
              <div className="text-[11px] uppercase font-semibold text-violet-700 tracking-wider mb-1">Cliente sorgente</div>
              <div className="font-medium">{portalLinkDialog?.sourceClientLabel}</div>
              <div className="text-[11px] text-slate-600 mt-1">
                Le polizze di questo cliente verranno mostrate <strong>anche</strong> nell&apos;area privata dei clienti selezionati (con badge intestatario).
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Aree private che già mostrano queste polizze</Label>
              <div className="mt-2 space-y-1.5" data-testid="portal-link-existing-list">
                {portalLinkExisting.length === 0 && (
                  <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-md px-3 py-2">Nessun collegamento attivo.</div>
                )}
                {portalLinkExisting.map((o) => (
                  <div key={o.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5 text-xs" data-testid={`portal-owner-${o.id}`}>
                    <span className="text-violet-700">🔗</span>
                    <span className="font-medium">{o.surname} {o.name}</span>
                    {o.email && <span className="text-slate-500 truncate">· {o.email}</span>}
                    <button
                      type="button"
                      onClick={() => removePortalLinkFromPolicy(o.id)}
                      className="ml-auto text-red-500 hover:text-red-700"
                      title="Rimuovi collegamento (reversibile)"
                      data-testid={`portal-owner-remove-${o.id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Aggiungi un nuovo cliente proprietario</Label>
              <Input
                data-testid="portal-link-search-input"
                className="mt-1"
                placeholder="Cerca per cognome, nome, email o cellulare…"
                value={portalLinkDialog?.search || ""}
                onChange={(e) => setPortalLinkDialog({ ...portalLinkDialog, search: e.target.value })}
              />
              {(portalLinkDialog?.search || "").trim().length >= 2 && (
                <div className="mt-1 bg-white border border-slate-200 rounded-md shadow-sm max-h-56 overflow-y-auto" data-testid="portal-link-search-results">
                  {(() => {
                    const q = (portalLinkDialog.search || "").trim().toLowerCase();
                    const excludedIds = new Set([portalLinkDialog.sourceClientId, ...portalLinkExisting.map((o) => o.id)]);
                    const matches = clients.filter((c) => {
                      if (excludedIds.has(c.id)) return false;
                      const hay = `${c.surname || ""} ${c.name || ""} ${c.email || ""} ${c.mobile || ""} ${c.phone || ""} ${c.fiscal_code || ""}`.toLowerCase();
                      return hay.includes(q);
                    }).slice(0, 8);
                    if (matches.length === 0) return <div className="px-3 py-2 text-xs text-slate-400 italic">Nessun risultato</div>;
                    return matches.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`portal-link-search-result-${c.id}`}
                        onClick={() => addPortalLinkFromPolicy(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium">{c.surname} {c.name}</div>
                        <div className="text-[11px] text-slate-500">
                          {c.email && <span>📧 {c.email}</span>}
                          {c.mobile && <span className="ml-2">📱 {c.mobile}</span>}
                          {c.fiscal_code && <span className="ml-2 font-mono">{c.fiscal_code}</span>}
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPortalLinkDialog(null); setPortalLinkExisting([]); }} data-testid="portal-link-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog Firma OTP da allegato: chiede titolo documento */}
      <Dialog open={!!signAttDialog} onOpenChange={(o) => { if (!o && !signAttBusy) setSignAttDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="policy-attach-sign-dialog">
          <DialogHeader>
            <DialogTitle>✍️ Invia per Firma OTP</DialogTitle>
            <DialogDescription>
              Il documento <strong>{signAttDialog?.att?.original_filename || signAttDialog?.att?.filename}</strong> verrà inviato via email al cliente per la firma con OTP.
              Scegli il titolo che apparirà nell&apos;oggetto dell&apos;email.
            </DialogDescription>
          </DialogHeader>
          {signAttDialog && (
            <div className="space-y-3 py-2">
              <DocumentTitleSelect
                value={signAttDialog.title}
                onChange={(v) => setSignAttDialog({ ...signAttDialog, title: v })}
                testid="policy-attach-sign-title"
              />
              {/* 🆕 Feb 2026 — Cambio destinatario firma: ricerca cliente in archivio.
                  Di default è il cliente della polizza, ma è possibile inviare a un contatto diverso. */}
              <div>
                <Label>Cliente destinatario *</Label>
                <ClientCombobox
                  clients={clients}
                  value={signAttDialog.client_id || ""}
                  onChange={(cid) => setSignAttDialog({ ...signAttDialog, client_id: cid })}
                  onClientCreated={(c) => { setClients((prev) => [c, ...prev.filter((x) => x.id !== c.id)]); setSignAttDialog((s) => ({ ...s, client_id: c.id })); }}
                  testid="policy-attach-sign-client"
                  placeholder="Cerca cliente (nome, CF, email…)"
                />
                {(() => {
                  const c = clients.find((x) => x.id === signAttDialog.client_id);
                  if (!c) return <p className="text-[11px] text-slate-500 mt-1">Seleziona un cliente per ricavare automaticamente email e cellulare per l&apos;OTP.</p>;
                  const email = c.legale_rappresentante_email || c.email || "—";
                  const mobile = c.legale_rappresentante_mobile || c.mobile || c.phone || "—";
                  return (
                    <p className="text-[11px] text-slate-500 mt-1">
                      📧 <span className="font-mono">{email}</span> · 📱 <span className="font-mono">{mobile}</span>
                    </p>
                  );
                })()}
              </div>
              <div>
                <Label htmlFor="attsig-notify">Email dove ricevere il PDF firmato (facoltativo)</Label>
                <StaffEmailCombobox
                  value={signAttDialog.notify_email || ""}
                  onChange={(v) => setSignAttDialog({ ...signAttDialog, notify_email: v })}
                  testid="policy-attach-sign-notify"
                  placeholder="Seleziona uno staff o digita un'email…"
                  defaultToCurrentUser
                />
                <p className="text-[11px] text-slate-500 mt-1">Se vuoto verrà usata la tua email dell&apos;account.</p>
              </div>
              {/* 🆕 Feb 2026 — Firma grafica manuale opt-in */}
              <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                <input
                  id="policy-attach-sign-require-drawn"
                  type="checkbox"
                  checked={!!signAttDialog.require_drawn}
                  onChange={(e) => setSignAttDialog({ ...signAttDialog, require_drawn: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-indigo-600"
                  data-testid="policy-attach-sign-require-drawn"
                />
                <label htmlFor="policy-attach-sign-require-drawn" className="text-sm text-slate-700 select-none cursor-pointer">
                  <span className="font-medium">Richiedi anche firma grafica manuale</span>
                  <span className="block text-xs text-slate-500">Il cliente dovrà disegnare la firma con dito o mouse nella pagina di firma, oltre a inserire l&apos;OTP.</span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignAttDialog(null)} disabled={signAttBusy} data-testid="policy-attach-sign-cancel">Annulla</Button>
            <Button onClick={submitAttSignature} disabled={signAttBusy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="policy-attach-sign-confirm">
              {signAttBusy ? "Invio…" : "Invia per firma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Preview pre-invio richiesta firma OTP allegato polizza */}
      <SignaturePreviewDialog
        open={!!attPreviewDialog}
        onOpenChange={(v) => { if (!v) setAttPreviewDialog(null); }}
        data={attPreviewDialog?.data}
        onConfirm={confirmAttPreview}
        loading={attPreviewBusy}
      />

      {/* 🆕 Feb 2026 — Dialog scelta causale sostituzione (mostrato PRIMA della conferma) */}
      <Dialog open={replacementReasonPickerOpen} onOpenChange={(o) => { if (!o) { setReplacementReasonPickerOpen(false); setSelectedReplacementReason(null); } }}>
        <DialogContent className="max-w-md" data-testid="policy-replacement-reason-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🔄 Motivo della sostituzione</DialogTitle>
            <DialogDescription>
              Seleziona la causale di sostituzione da associare a questa operazione.
              Le causali sono configurabili dal menù <strong>Gestione Agenzia → Causali sostituzione</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2 max-h-72 overflow-y-auto">
            {replacementReasonLoading && (
              <div className="text-center py-6 text-slate-400 text-sm">Caricamento causali…</div>
            )}
            {!replacementReasonLoading && replacementReasons.length === 0 && (
              <div className="text-center py-6 text-slate-500 text-sm space-y-2" data-testid="replacement-reason-empty">
                <div className="text-amber-800 font-semibold">⚠️ Nessuna causale configurata</div>
                <div>Per procedere con una sostituzione devi prima configurare almeno una causale.</div>
                <div className="text-xs text-slate-400">Vai in <strong>Gestione Agenzia → Causali sostituzione</strong> per crearne.</div>
              </div>
            )}
            {!replacementReasonLoading && replacementReasons.map((r) => {
              const isSelected = selectedReplacementReason?.code === r.code;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedReplacementReason(r)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition ${isSelected ? "bg-blue-50 border-blue-500 ring-2 ring-blue-200" : "bg-white border-slate-200 hover:border-blue-300 hover:bg-slate-50"}`}
                  data-testid={`replacement-reason-option-${r.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${isSelected ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
                      {isSelected && <div className="w-full h-full rounded-full bg-white scale-[0.4]" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-mono text-sm font-semibold text-slate-800">{r.code}</div>
                        {r.preserve_full_policy && (
                          <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 font-semibold uppercase tracking-wide" title="Copia integrale: solo targa e n° polizza modificabili">Copia integrale</span>
                        )}
                      </div>
                      {r.description && <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setReplacementReasonPickerOpen(false); setSelectedReplacementReason(null); }} data-testid="replacement-reason-picker-cancel">Annulla</Button>
            <Button
              disabled={!selectedReplacementReason}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                setReplacementReasonPickerOpen(false);
                setChainAction("replacement");
              }}
              data-testid="replacement-reason-picker-confirm"
            >
              Prosegui
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 16/07/2026 — Dialog conferma azione catena (Rinnovo / Sostituzione / Crea Offerta) */}
      <Dialog open={!!chainAction} onOpenChange={(o) => { if (!o) setChainAction(null); }}>
        <DialogContent className="max-w-md" data-testid="policy-chain-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {chainAction === "renewal" && <>🔁 Conferma Rinnovo polizza</>}
              {chainAction === "replacement" && <>🔄 Conferma Sostituzione polizza</>}
              {chainAction === "quote_offer" && <>📄 Conferma Crea Offerta da polizza</>}
              {chainAction === "undo" && <>🗑️ Conferma Annullamento {editing?.link_type === "renewal" ? "Rinnovo" : editing?.link_type === "replacement" ? "Sostituzione" : "Offerta"}</>}
            </DialogTitle>
            <DialogDescription>
              {chainAction === "renewal" && (
                <>Sarà creata una <strong>nuova polizza</strong> con gli stessi dati (cliente, veicolo, compagnia, garanzie) MA con <strong>date scadenza/decorrenza e importi rate azzerati</strong>. La polizza attuale passerà a stato <strong>&quot;scaduta&quot;</strong> e sarà accessibile solo dal carosello storico. Il numero polizza avrà suffisso <code className="bg-slate-100 px-1 rounded">-R{`{n}`}</code>.</>
              )}
              {chainAction === "replacement" && (
                <>{selectedReplacementReason?.preserve_full_policy ? (
                  <>Sarà creata una <strong>copia integrale</strong> della polizza attuale — verranno mantenuti tutti i dati (date, importi, garanzie, rate, incassi). Solo <strong>numero polizza</strong> e <strong>targa</strong> saranno azzerati per essere ricompilati. La polizza attuale passerà a stato <strong>&quot;sostituita&quot;</strong> e sarà accessibile solo dal carosello storico.</>
                ) : (
                  <>Sarà creata una <strong>nuova polizza</strong> con gli stessi dati (cliente, veicolo, compagnia, garanzie) MA con <strong>numero polizza, date scadenza/decorrenza e importi rate azzerati</strong>. La polizza attuale passerà a stato <strong>&quot;sostituita&quot;</strong> e sarà accessibile solo dal carosello storico.</>
                )}
                {selectedReplacementReason && (
                  <div className="mt-3 p-2 rounded bg-blue-50 border border-blue-200 text-blue-900 text-sm" data-testid="policy-chain-selected-reason">
                    <strong>Causale sostituzione:</strong> <code className="bg-white px-1.5 py-0.5 rounded border border-blue-200">{selectedReplacementReason.code}</code>
                    {selectedReplacementReason.description && <> — {selectedReplacementReason.description}</>}
                    {selectedReplacementReason.preserve_full_policy && (
                      <div className="mt-1 text-xs text-amber-800">
                        ⚠️ Modalità <strong>copia integrale</strong>: solo targa e numero polizza da compilare.
                      </div>
                    )}
                  </div>
                )}
                </>
              )}
              {chainAction === "quote_offer" && (
                <>Sarà creata una <strong>nuova Offerta Finale</strong> (bozza) duplicando i dati della polizza. Se il cliente accetta, l&apos;offerta verrà convertita in nuova polizza attiva, e questa polizza attuale passerà nel carosello storico.</>
              )}
              {chainAction === "undo" && (
                <>
                  <strong>⚠️ Attenzione:</strong> questa operazione <strong>cancellerà definitivamente</strong> la polizza corrente (n. <code className="bg-slate-100 px-1 rounded">{editing?.policy_number || "s.n."}</code>) e <strong>ripristinerà</strong> la polizza precedente allo stato che aveva prima del{" "}{editing?.link_type === "renewal" ? "Rinnovo" : editing?.link_type === "replacement" ? "Sostituzione" : "creazione Offerta"}. Verrà tornata visibile nella lista principale.
                  <br /><br />L&apos;operazione è <strong>irreversibile</strong>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setChainAction(null)} disabled={chainActionBusy} data-testid="policy-chain-cancel">Annulla</Button>
            <Button
              data-testid="policy-chain-confirm"
              disabled={chainActionBusy}
              className={chainAction === "undo" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}
              onClick={async () => {
                if (!editing?.id) return;
                setChainActionBusy(true);
                try {
                  if (chainAction === "renewal") {
                    const r = await api.post(`/policies/${editing.id}/quick-renew`);
                    toast.success(r.data?.message || "Rinnovo creato");
                    if (r.data?.new_policy) {
                      openEdit(r.data.new_policy);
                    }
                    load();
                  } else if (chainAction === "replacement") {
                    if (!selectedReplacementReason) {
                      toast.error("Seleziona prima la causale di sostituzione");
                      setChainActionBusy(false);
                      return;
                    }
                    const payload = {
                      reason_code: selectedReplacementReason.code,
                      reason_label: selectedReplacementReason.description || selectedReplacementReason.code,
                      preserve_full_policy: !!selectedReplacementReason.preserve_full_policy,
                    };
                    const r = await api.post(`/policies/${editing.id}/quick-replace`, payload);
                    toast.success(r.data?.message || "Sostituzione creata");
                    if (r.data?.new_policy) {
                      openEdit(r.data.new_policy);
                    }
                    load();
                    setSelectedReplacementReason(null);
                  } else if (chainAction === "quote_offer") {
                    const r = await api.post(`/policies/${editing.id}/create-quote-offer`);
                    toast.success(r.data?.message || "Offerta creata");
                    setOpen(false);
                    setEditing(null);
                    if (r.data?.quote_offer_id) {
                      navigate(`/quotes/offers?focus=${r.data.quote_offer_id}`);
                    }
                  } else if (chainAction === "undo") {
                    const r = await api.post(`/policies/${editing.id}/undo-chain-link`);
                    toast.success(r.data?.message || "Operazione annullata");
                    // Riapri la polizza parent ripristinata
                    if (r.data?.restored_policy) {
                      openEdit(r.data.restored_policy);
                    } else {
                      setOpen(false);
                      setEditing(null);
                    }
                    load();
                  }
                  setChainAction(null);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Errore durante l'operazione");
                } finally {
                  setChainActionBusy(false);
                }
              }}
            >
              {chainActionBusy ? "⏳ Attendi…" : chainAction === "undo" ? "Sì, annulla e ripristina" : "Conferma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog "Registra incasso" per singola rata */}
      <Dialog open={!!installmentPayDialog} onOpenChange={(o) => { if (!o) setInstallmentPayDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="installment-pay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CreditCard className="h-5 w-5" /> Registra incasso rata
            </DialogTitle>
            <DialogDescription>
              {installmentPayDialog?.label ? `${installmentPayDialog.label} · ` : ""}Scadenza {installmentPayDialog?.date ? new Date(installmentPayDialog.date).toLocaleDateString("it-IT") : "—"}
            </DialogDescription>
          </DialogHeader>
          {installmentPayDialog && (
            <div className="space-y-3 py-1">
              <div>
                <Label className="text-xs text-emerald-800 font-semibold">Importo incassato (€) *</Label>
                <Input
                  data-testid="installment-pay-amount"
                  type="number" step="0.01" min="0"
                  value={installmentPayDialog.amount}
                  onChange={(e) => setInstallmentPayDialog({ ...installmentPayDialog, amount: e.target.value })}
                  className="bg-white border-emerald-300 font-mono"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs text-emerald-800 font-semibold">Data pagamento effettivo *</Label>
                <Input
                  data-testid="installment-pay-date"
                  type="date"
                  value={installmentPayDialog.paid_date}
                  onChange={(e) => setInstallmentPayDialog({ ...installmentPayDialog, paid_date: e.target.value })}
                  className="bg-white border-emerald-300"
                />
              </div>
              <div>
                <Label className="text-xs text-emerald-800 font-semibold">Modalità di pagamento *</Label>
                <PaymentMethodSelect
                  value={installmentPayDialog.method}
                  onChange={(v) => setInstallmentPayDialog({ ...installmentPayDialog, method: v })}
                  testid="installment-pay-method"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {installmentPayDialog && form.installment_paid_methods && form.installment_paid_methods[installmentPayDialog.date] && (
              <Button
                variant="outline"
                data-testid="installment-pay-clear"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 mr-auto"
                onClick={() => {
                  const d = installmentPayDialog.date;
                  const nextMethods = { ...(form.installment_paid_methods || {}) };
                  const nextPaid = { ...(form.installment_paid_dates || {}) };
                  delete nextMethods[d];
                  delete nextPaid[d];
                  // 🆕 28/07/2026 — Ricalcola paid_amount coerente (stessa logica di "Salva incasso")
                  const curAmounts = form.installment_amounts || {};
                  const totalPaid = Object.entries(curAmounts)
                    .filter(([k]) => nextPaid[k])
                    .reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
                  const lastPaidDate = Object.values(nextPaid).filter(Boolean).sort().slice(-1)[0] || null;
                  const anyPaid = Object.values(nextPaid).some(Boolean);
                  setForm({
                    ...form,
                    installment_paid_methods: nextMethods,
                    installment_paid_dates: nextPaid,
                    paid_amount: Math.round(totalPaid * 100) / 100,
                    paid_date: lastPaidDate,
                    is_paid: anyPaid,
                  });
                  setInstallmentPayDialog(null);
                  toast.success("Incasso annullato per questa rata");
                }}
              >
                Annulla incasso
              </Button>
            )}
            <Button variant="outline" onClick={() => setInstallmentPayDialog(null)} data-testid="installment-pay-cancel">Chiudi</Button>
            <Button
              data-testid="installment-pay-save"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={!installmentPayDialog?.paid_date || !installmentPayDialog?.method || !installmentPayDialog?.amount}
              onClick={() => {
                const { date: d, amount, paid_date, method } = installmentPayDialog;
                const amtNum = parseFloat(amount);
                const nextAmounts = { ...(form.installment_amounts || {}) };
                const nextPaid = { ...(form.installment_paid_dates || {}) };
                const nextMethods = { ...(form.installment_paid_methods || {}) };
                if (Number.isFinite(amtNum) && amtNum >= 0) nextAmounts[d] = amtNum;
                nextPaid[d] = paid_date;
                nextMethods[d] = method;
                // 🆕 Sync automatico dei campi legacy a livello polizza (backward compat con Estratto Conto / Ricevute).
                // paid_amount = somma degli importi rate con paid_date; paid_method = ultima modalità usata.
                const totalPaid = Object.entries(nextAmounts)
                  .filter(([k]) => nextPaid[k])
                  .reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
                const lastPaidDate = Object.values(nextPaid).filter(Boolean).sort().slice(-1)[0] || paid_date;
                setForm({
                  ...form,
                  installment_amounts: nextAmounts,
                  installment_paid_dates: nextPaid,
                  installment_paid_methods: nextMethods,
                  is_paid: true,
                  paid_amount: Math.round(totalPaid * 100) / 100,
                  paid_date: lastPaidDate,
                  paid_method: method,
                });
                setInstallmentPayDialog(null);
                toast.success(`Incasso registrato: ${new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(amtNum || 0)} · ${method}`);
              }}
            >
              Salva incasso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog unificato Import Allegati: merge PDF + rinomina + categoria */}
      <Dialog open={!!importDialog} onOpenChange={(o) => { if (!o && !importDialog?.busy) setImportDialog(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700" data-testid="policy-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
              <Paperclip className="h-5 w-5" />
              Allega{importDialog?.files?.length > 1 ? ` ${importDialog.files.length} file` : " file"} alla polizza
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400 text-xs">
              Prima del caricamento puoi unire più file in un unico PDF, rinominarli e assegnare la categoria.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* Opzione MERGE (visibile solo se ≥2 file) */}
            {importDialog?.files?.length >= 2 && (
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!importDialog?.merge}
                    onChange={(e) => setImportDialog((s) => s ? { ...s, merge: e.target.checked } : s)}
                    className="mt-1 h-4 w-4 accent-emerald-600"
                    data-testid="policy-import-merge-toggle"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-emerald-900 dark:text-emerald-100">Unisci tutti in un unico PDF</div>
                    <div className="text-xs text-emerald-800/80 dark:text-emerald-200/70">
                      Immagini e PDF vengono fusi in un unico documento. Potrai scegliere un nome unico qui sotto.
                    </div>
                  </div>
                </label>
                {importDialog?.merge && (
                  <div className="mt-2 pl-6">
                    <Label className="text-xs text-emerald-900 dark:text-emerald-100 font-semibold">Nome del PDF unificato</Label>
                    <Input
                      value={importDialog?.mergedName || ""}
                      onChange={(e) => setImportDialog((s) => s ? { ...s, mergedName: e.target.value } : s)}
                      className="mt-1 h-9 text-sm bg-white dark:bg-slate-800 border-emerald-300 dark:border-emerald-600 text-slate-900 dark:text-slate-100"
                      placeholder="allegato_unificato"
                      data-testid="policy-import-merged-name"
                    />
                    <p className="text-[10px] text-emerald-800/70 dark:text-emerald-200/60 mt-1">L'estensione .pdf viene aggiunta automaticamente.</p>
                  </div>
                )}
              </div>
            )}

            {/* RINOMINA (visibile se non merge o se solo 1 file) */}
            {!importDialog?.merge && (
              <div>
                <Label className="text-xs text-slate-700 dark:text-slate-200 font-semibold mb-2 block">
                  Rinomina ogni file
                </Label>
                <div className="space-y-2">
                  {importDialog?.files?.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-2" data-testid={`policy-import-rename-row-${i}`}>
                      <FileText className="h-4 w-4 text-slate-500 dark:text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <Input
                          value={importDialog?.newNames?.[i] ?? ""}
                          onChange={(e) => setImportDialog((s) => {
                            if (!s) return s;
                            const nn = [...(s.newNames || [])];
                            nn[i] = e.target.value;
                            return { ...s, newNames: nn };
                          })}
                          className="h-8 text-sm bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                          data-testid={`policy-import-rename-input-${i}`}
                          placeholder={f.name}
                        />
                        <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate mt-0.5" title={f.name}>
                          Originale: {f.name} · {formatFileSize(f.size)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CATEGORIA */}
            <div>
              <Label className="text-xs text-slate-700 dark:text-slate-200 font-semibold mb-2 block">
                Categoria allegato
              </Label>
              <Select
                value={importDialog?.category || ""}
                onValueChange={(v) => setImportDialog((s) => s ? { ...s, category: v } : s)}
              >
                <SelectTrigger className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 h-9" data-testid="policy-import-category">
                  <SelectValue placeholder="Scegli una categoria (opzionale)" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700">
                  {POLICY_ATT_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`policy-import-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {importDialog?.category === "Altro" && (
                <Input
                  value={importDialog?.customCategory || ""}
                  onChange={(e) => setImportDialog((s) => s ? { ...s, customCategory: e.target.value } : s)}
                  className="mt-2 h-9 text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                  placeholder="Specifica la categoria personalizzata"
                  data-testid="policy-import-category-custom"
                />
              )}
            </div>
          </div>

          <DialogFooter className="pt-3 border-t border-slate-200 dark:border-slate-700">
            <Button
              variant="outline"
              onClick={() => setImportDialog(null)}
              disabled={!!importDialog?.busy}
              className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid="policy-import-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={confirmImport}
              disabled={!!importDialog?.busy}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="policy-import-confirm"
            >
              {importDialog?.busy ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Paperclip className="h-4 w-4 mr-1" />}
              {importDialog?.busy ? "Caricamento…" : `Carica ${importDialog?.merge ? "1 PDF unificato" : `${importDialog?.files?.length || 0} file`}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
